import type { DocumentContract } from '@protoss/doc';
import { DISTRIBUTION_MODEL_LABELS, isStageAvailable } from '@protoss/shared';
import type { DealCategoryMetadata } from '@protoss/shared';
import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  IconSize,
  InfoCircledIcon,
  NumberInput,
  Select,
  SelectItem,
  SkeletonBlock,
  Tabs,
  TabsContent,
  type TabsItem,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { ResizableDivider } from '../components/ResizableDivider';
import type { PreviewSample } from '../components/doc';
import { PageErrorState } from '../components/shared/PageErrorState';
import { SowTemplateWorkbench } from '../features/sales/components/sow-template/SowTemplateWorkbench';
import type { SowFidelityMetadata } from '../features/sales/components/sow-template/SowFidelityPanel';
import { AppShell, MainContentShell, type RightRailItem } from '../components/shell';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { DriveFolderField } from '../features/sales/components/DriveFolderField';
import { RateCardPane } from '../features/sales/components/RateCardPane';
import { ResourceModelPane } from '../features/sales/components/ResourceModelPane';
import {
  filterByScope,
  formatVersionLabel,
  pickActive,
} from '../features/sales/components/versioning';
import { useWorkspaceMenu } from '../features/workspace-menu/hooks/useWorkspaceMenu';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { apiFetch } from '../lib/apiClient';
import { StageBadge } from '../lib/featureStage';
import { AdminContextMenuPane } from './AdminContextMenuPane';
import { DealAttributesSection, DealAttributesSkeleton } from './DealAttributesPage';
import './product-template-page.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DealTemplateSummary {
  uuid: string;
  label: string;
  scope: string;
  version: string;
  status: string;
  isDefault: boolean;
  origin: string;
  parentTemplateId: number | null;
  calculatorProfileId: number | null;
  categoryKey: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CategoryOption {
  valueKey: string;
  label: string;
  isSystem?: boolean;
  metadata?: DealCategoryMetadata | null;
  /** True when the category's calculator profile is engine_mode='ai_inferred' (AI-mode estimate). */
  aiMode?: boolean;
}

// ─── Fork dialog ──────────────────────────────────────────────────────────────

interface ForkDialogProps {
  sourceTemplate: DealTemplateSummary;
  productUuid: string;
  onCreated: (t: DealTemplateSummary) => void;
  onCancel: () => void;
}

function ForkDialog({ sourceTemplate, productUuid, onCreated, onCancel }: ForkDialogProps) {
  const [label, setLabel] = React.useState(
    sourceTemplate.scope === 'system'
      ? `${sourceTemplate.label} (Workspace)`
      : `${sourceTemplate.label} v${parseInt(sourceTemplate.version, 10) + 1}`
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (label.trim() === '') return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await apiFetch<{ template: DealTemplateSummary }>(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates`,
        {
          method: 'POST',
          body: JSON.stringify({ sourceTemplateUuid: sourceTemplate.uuid, label: label.trim() }),
        }
      );
      onCreated(result.template);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ptp-fork-dialog">
      <h3 className="ptp-fork-dialog__title">Create workspace template</h3>
      <p className="ptp-fork-dialog__desc">
        Forking <strong>{sourceTemplate.label}</strong> (v{sourceTemplate.version})
      </p>
      <form onSubmit={(e) => void handleSubmit(e)} className="ptp-fork-dialog__form">
        <label className="ptp-fork-dialog__label" htmlFor="fork-label">
          Template name
        </label>
        <input
          id="fork-label"
          className="ptp-fork-dialog__input"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isSubmitting}
          autoFocus
          maxLength={120}
        />
        {error && <p className="ptp-fork-dialog__error">{error}</p>}
        <div className="ptp-fork-dialog__actions">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting || label.trim() === ''}
          >
            {isSubmitting ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Template tree types ──────────────────────────────────────────────────────

interface TemplateTreeField {
  id: number;
  fieldKey: string;
  label: string;
  description: string | null;
  fieldType: string;
  isRequired: boolean;
  uiControl: string;
}

interface TemplateTreeGroup {
  id: number;
  groupKey: string;
  label: string;
  subtitle: string | null;
  fields: TemplateTreeField[];
}

interface TemplateTreeSection {
  id: number;
  label: string;
  groups: TemplateTreeGroup[];
}

// ─── Template version bar ─────────────────────────────────────────────────────

/** Strip trailing " v{n}" suffix to derive the template family display name. */
function stripVersionSuffix(label: string): string {
  return label.replace(/ v\d+$/, '').trim();
}

/**
 * Wraps a (disabled) authoring control and shows a "Coming soon" tooltip on
 * hover. The template/profile create/edit/version flows are gated in the UI
 * until the exact authoring flow is designed; the wrapper span receives the
 * hover because the disabled control inside has pointer-events disabled.
 */
function ComingSoon({ children }: { children: React.ReactNode }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className="ptp-coming-soon">{children}</span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="top" sideOffset={6} className="ui-tooltip">
          Coming soon
          <Tooltip.Arrow className="ui-tooltip-arrow" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * Read-only pill shown when an artifact is being viewed in the context of a
 * selected deal category. `scoped` = a row dedicated to this category exists;
 * otherwise the artifact falls back to the shared, category-agnostic row.
 * Reuses the existing .ptp-badge styling - no new tokens.
 */
function ScopeBadge({ scoped }: { scoped: boolean }) {
  return scoped ? (
    <Tooltip.Simple label="A version dedicated to this category applies here.">
      <span className="ptp-badge ptp-badge--scope-workspace">Scoped to this category</span>
    </Tooltip.Simple>
  ) : (
    <Tooltip.Simple label="No category-specific version; the shared (all-categories) version applies here.">
      <span className="ptp-badge ptp-badge--scope-system">Shared (all categories)</span>
    </Tooltip.Simple>
  );
}

function TemplateVersionBar({
  templates,
  selectedUuid,
  isEditing,
  treeCollapsed,
  onSelect,
  onEditCancel,
  onForkClick,
  onStatusChange,
  onSetDefault,
  onCategoryChange,
  onToggleTree,
  isPatching,
  categoryOptions,
  scopedCategoryKey,
}: {
  templates: DealTemplateSummary[];
  selectedUuid: string;
  isEditing: boolean;
  treeCollapsed: boolean;
  onSelect: (uuid: string) => void;
  onEditCancel: () => void;
  onForkClick: () => void;
  onStatusChange: (status: 'draft' | 'saved' | 'published') => void;
  onSetDefault: () => void;
  onCategoryChange: (key: string | null) => void;
  onToggleTree: () => void;
  isPatching: boolean;
  categoryOptions: CategoryOption[];
  /**
   * When set, the bar is being viewed inside a selected deal category: the
   * category re-assignment dropdown is replaced by a read-only Scoped/Shared
   * badge (re-scoping from here is deferred to the category-authoring flow).
   */
  scopedCategoryKey?: string | null;
}) {
  const selected = templates.find((t) => t.uuid === selectedUuid);

  // Inline confirmation state for "Set as default"
  const [confirmingDefault, setConfirmingDefault] = React.useState(false);

  // Reset confirmation when selection changes
  React.useEffect(() => {
    setConfirmingDefault(false);
  }, [selectedUuid]);

  // Group templates by label (family)
  const templateGroups = React.useMemo(() => {
    const map = new Map<string, DealTemplateSummary[]>();
    for (const t of templates) {
      if (!map.has(t.label)) map.set(t.label, []);
      map.get(t.label)!.push(t);
    }
    return map;
  }, [templates]);

  // Family display label: use system template's label stripped of version suffix
  const groupBaseLabel = React.useCallback(
    (label: string): string => {
      const group = templateGroups.get(label) ?? [];
      const rep = group.find((t) => t.scope === 'system') ?? group[0];
      return rep ? stripVersionSuffix(rep.label) : label;
    },
    [templateGroups]
  );

  const handleFamilyChange = React.useCallback(
    (label: string) => {
      const group = templateGroups.get(label) ?? [];
      const preferred =
        group.find((t) => t.scope === 'workspace' && t.isDefault) ??
        group.find((t) => t.scope === 'workspace') ??
        [...group].sort((a, b) => parseInt(b.version, 10) - parseInt(a.version, 10))[0];
      if (preferred) onSelect(preferred.uuid);
    },
    [templateGroups, onSelect]
  );

  if (!selected) return null;

  const familyTemplates = templateGroups.get(selected.label) ?? [selected];

  const handleSave = (status: 'saved' | 'published') => {
    onStatusChange(status);
  };

  const handleConfirmDefault = () => {
    setConfirmingDefault(false);
    onSetDefault();
  };

  return (
    <div className="ptp-toolbar">
      <div className="ptp-toolbar__left">
        {/* Template family selector */}
        <Select value={selected.label} onValueChange={handleFamilyChange} size="sm">
          {[...templateGroups.keys()].map((label) => (
            <SelectItem key={label} value={label}>
              {groupBaseLabel(label)}
            </SelectItem>
          ))}
        </Select>
        {/* Version selector */}
        <Select value={selectedUuid} onValueChange={onSelect} size="sm">
          {familyTemplates.map((t) => (
            <SelectItem key={t.uuid} value={t.uuid}>
              {formatVersionLabel(t)}
            </SelectItem>
          ))}
        </Select>
        {scopedCategoryKey != null
          ? null
          : selected.categoryKey !== undefined &&
            categoryOptions.length > 0 && (
              <Select
                value={selected.categoryKey ?? ''}
                onValueChange={(v) => onCategoryChange(v || null)}
                disabled={selected.scope !== 'workspace' || isPatching}
                size="sm"
              >
                {categoryOptions.map((o) => (
                  <SelectItem key={o.valueKey} value={o.valueKey}>
                    {o.label}
                  </SelectItem>
                ))}
              </Select>
            )}
        {selected.isDefault && <span className="ptp-badge ptp-badge--default">Default</span>}
        {selected.scope === 'system' && (
          <span className="ptp-badge ptp-badge--scope-system">System</span>
        )}
      </div>
      <div className="ptp-toolbar__right">
        {/* Expand/collapse all tree sections (questionnaire tree only) */}
        {!isEditing && (
          <Button type="button" variant="outline" size="sm" onClick={onToggleTree}>
            {treeCollapsed ? 'Expand all' : 'Collapse all'}
          </Button>
        )}
        {/* Copy from (fork) - gated until the authoring flow is designed */}
        {!isEditing && (
          <ComingSoon>
            <Button type="button" variant="outline" size="sm" disabled>
              {selected.scope === 'system' ? 'Copy from system' : 'New version'}
            </Button>
          </ComingSoon>
        )}
        {/* Publish a workspace draft - gated until the authoring flow is designed */}
        {!isEditing && selected.scope === 'workspace' && selected.status !== 'published' && (
          <ComingSoon>
            <Button type="button" variant="outline" size="sm" disabled>
              Publish
            </Button>
          </ComingSoon>
        )}
        {/* Set as default - workspace only, published, when not already default */}
        {selected.scope === 'workspace' &&
          !selected.isDefault &&
          selected.status === 'published' &&
          !isEditing && (
            <>
              {confirmingDefault ? (
                <>
                  <span className="ptp-toolbar__confirm-label">Set as default?</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isPatching}
                    onClick={() => setConfirmingDefault(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={isPatching}
                    onClick={handleConfirmDefault}
                  >
                    Confirm
                  </Button>
                </>
              ) : (
                <ComingSoon>
                  <Button type="button" variant="primary" size="sm" disabled>
                    Set as default
                  </Button>
                </ComingSoon>
              )}
            </>
          )}
        {/* Workspace templates in edit mode: Cancel + split Save button */}
        {selected.scope === 'workspace' && isEditing && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isPatching}
              onClick={onEditCancel}
            >
              Cancel
            </Button>
            <div className="ptp-split-btn">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={isPatching}
                onClick={() => handleSave('saved')}
              >
                Save
              </Button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={isPatching}
                    aria-label="More save options"
                  >
                    <ChevronDownIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
                    <DropdownMenu.Item
                      className="dropdown-item"
                      onSelect={() => handleSave('saved')}
                    >
                      Save
                    </DropdownMenu.Item>
                    {selected.status !== 'published' && (
                      <DropdownMenu.Item
                        className="dropdown-item"
                        onSelect={() => handleSave('published')}
                      >
                        Publish
                      </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Separator className="dropdown-separator" />
                    <DropdownMenu.Item className="dropdown-item" onSelect={onForkClick}>
                      Save as new version
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Template tree view ───────────────────────────────────────────────────────

function TemplateTreeView({
  sections,
  isLoading,
  collapsed,
  onFieldClick,
}: {
  sections: TemplateTreeSection[];
  isLoading: boolean;
  collapsed: boolean;
  onFieldClick?: (uiControl: string) => void;
}) {
  const [expandedSections, setExpandedSections] = React.useState<Set<number>>(() =>
    collapsed ? new Set() : new Set(sections.map((s) => s.id))
  );
  const [expandedGroups, setExpandedGroups] = React.useState<Set<number>>(() => {
    if (collapsed) return new Set<number>();
    const set = new Set<number>();
    for (const s of sections) for (const g of s.groups) set.add(g.id);
    return set;
  });

  // Sync expand/collapse all state from parent
  React.useEffect(() => {
    if (collapsed) {
      setExpandedSections(new Set());
      setExpandedGroups(new Set());
    } else {
      setExpandedSections(new Set(sections.map((s) => s.id)));
      const groupSet = new Set<number>();
      for (const s of sections) for (const g of s.groups) groupSet.add(g.id);
      setExpandedGroups(groupSet);
    }
  }, [collapsed, sections]);

  if (isLoading) {
    return (
      <div className="ptp-tree-skeleton" aria-busy="true" aria-label="Loading template structure">
        <div className="ptp-skeleton ptp-skeleton--tree-row" />
        <div className="ptp-skeleton ptp-skeleton--tree-row" />
        <div className="ptp-skeleton ptp-skeleton--tree-row" />
        <div className="ptp-skeleton ptp-skeleton--tree-row" />
        <div className="ptp-skeleton ptp-skeleton--tree-row" />
      </div>
    );
  }

  if (sections.length === 0) {
    return <p className="ptp-empty-text">No sections in this template.</p>;
  }

  const toggleSection = (id: number) =>
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGroup = (id: number) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="ptp-tree">
      {sections.map((section) => {
        const isOpen = expandedSections.has(section.id);
        const totalFields = section.groups.reduce((n, g) => n + g.fields.length, 0);
        return (
          <div key={section.id} className="ptp-tree-section">
            <button
              type="button"
              className="ptp-tree-section__toggle"
              aria-expanded={isOpen}
              onClick={() => toggleSection(section.id)}
            >
              <span
                className={`ptp-tree-chevron${isOpen ? ' ptp-tree-chevron--open' : ''}`}
                aria-hidden="true"
              />
              <span className="ptp-tree-section__label">{section.label}</span>
              <span className="ptp-tree-section__meta">
                {section.groups.length} {section.groups.length === 1 ? 'group' : 'groups'} ·{' '}
                {totalFields} {totalFields === 1 ? 'field' : 'fields'}
              </span>
            </button>
            {isOpen && (
              <div className="ptp-tree-section__body">
                {section.groups.length === 0 ? (
                  <p className="ptp-tree-empty">No groups in this section.</p>
                ) : (
                  section.groups.map((group) => {
                    const grpOpen = expandedGroups.has(group.id);
                    return (
                      <div key={group.id} className="ptp-tree-group">
                        <button
                          type="button"
                          className="ptp-tree-group__toggle"
                          aria-expanded={grpOpen}
                          onClick={() => toggleGroup(group.id)}
                        >
                          <span
                            className={`ptp-tree-chevron${grpOpen ? ' ptp-tree-chevron--open' : ''}`}
                            aria-hidden="true"
                          />
                          <span className="ptp-tree-group__label">{group.label}</span>
                          {group.subtitle && (
                            <span className="ptp-tree-group__subtitle">{group.subtitle}</span>
                          )}
                          <span className="ptp-tree-group__count">
                            {group.fields.length} {group.fields.length === 1 ? 'field' : 'fields'}
                          </span>
                        </button>
                        {grpOpen && (
                          <ul className="ptp-tree-group__body">
                            {group.fields.length === 0 ? (
                              <li className="ptp-tree-empty">No fields in this group.</li>
                            ) : (
                              group.fields.map((field) => (
                                <li
                                  key={field.id}
                                  className={`ptp-tree-field${onFieldClick ? ' ptp-tree-field--sel' : ''}`}
                                  onClick={
                                    onFieldClick ? () => onFieldClick(field.uiControl) : undefined
                                  }
                                  role={onFieldClick ? 'button' : undefined}
                                  tabIndex={onFieldClick ? 0 : undefined}
                                  onKeyDown={
                                    onFieldClick
                                      ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ')
                                            onFieldClick(field.uiControl);
                                        }
                                      : undefined
                                  }
                                >
                                  <span className="ptp-tree-field__main">
                                    <span className="ptp-tree-field__label">{field.label}</span>
                                    {field.description && (
                                      <span className="ptp-tree-field__desc">
                                        {field.description}
                                      </span>
                                    )}
                                  </span>
                                  {field.isRequired && (
                                    <Tooltip.Simple label="Required">
                                      <span className="ptp-tree-field__req">required</span>
                                    </Tooltip.Simple>
                                  )}
                                  <span className="ptp-tree-field__type">{field.fieldType}</span>
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Estimate ──────────────────────────────────────────────────────────

// ---- Calculator profile helpers ----

const PROFILE_KEY_LABELS: Record<string, string> = {
  r2r_hours: 'R2R Hours',
  o2c_hours: 'O2C Hours',
  revenue_hours: 'Revenue Hours',
  advanced_projects_hours: 'Advanced Projects Hours',
  p2p_hours: 'P2P Hours',
  third_party_hours: 'Third Party Hours',
  objective_complexity: 'Objective Complexity',
  suitebilling_trigger_scores: 'SuiteBilling Trigger Scores',
  o2c_payment_processor_choices: 'Payment Processor Choices',
  choice_values: 'Choice Values',
  tool_aliases: 'Tool Aliases',
  // Rillet namespaces
  rillet_module_hours: 'Rillet Module Hours',
  rillet_coa_hours: 'Rillet CoA Hours',
  rillet_data_migration_hours: 'Rillet Data Migration Hours',
  rillet_techstack_hours: 'Rillet ERP Techstack Hours',
  rillet_complexity_hours: 'Rillet Complexity Adder Hours',
};

function formatProfileValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return (value as (string | number)[]).join(', ');
    }
    return `[${String(value.length)} items]`;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as object);
    return `{${String(keys.length)} keys}`;
  }
  return String(value);
}

interface EstimateProfileData {
  id: number;
  profileKey: string;
  displayName: string;
  version: string;
  config: Record<string, unknown>;
  scope: 'workspace' | 'system';
}

// ---- Signal detail types and helpers ----

interface SignalInfo {
  label: string;
  computationType: 'formula' | 'ai_task';
  aiTaskKey: string | null;
  formulaExpr: string | null;
  signalDeps: string[] | null;
  sourceFieldKeys: string[] | null;
}

interface AiTaskMeta {
  description: string;
  pseudoFormula: string;
  /** `input`: what the user said / AI inferred. `result`: the calculation + outcome. */
  example: { input: string; result: string };
}

/** Per-handler metadata: concise description, logic pseudo-formula, worked example. */
const AI_TASK_META: Record<string, AiTaskMeta> = {
  complexity_blend: {
    description:
      "Scores each section by blending the user's complexity slider with an objective score, then adds extra hours for each blended point above 5.",
    pseudoFormula:
      "cfg('discovery_base_hours', 10) + Σ_sections max(0, blend(slider_i, obj_score) − 5) × cfg('complexity_adder_per_score_point', 1)",
    example: {
      input: 'User: complexity sliders avg 7/10 across 5 sections - AI obj. score ≈ 7',
      result: '10 + 5 × max(0, 7 − 5) × 1 = 20 hrs',
    },
  },
  tiered_billing_hours: {
    description:
      'Checks for an external billing tool first (Stage 1), then scores internal billing complexity triggers to return high or low tier hours (Stage 2).',
    pseudoFormula:
      'external_detected ? flat_hours : trigger_count ≥ threshold ? high_hours : low_hours',
    example: {
      input: 'No external billing tool - AI found 3 complexity triggers in response',
      result: '3 ≥ threshold (2) → high tier hours',
    },
  },
  ai_band_hours: {
    description:
      'Uses AI inference and config patterns to classify the answer as high or low complexity, then returns the matching hours.',
    pseudoFormula: 'high_confidence ? high_hours : low_hours',
    example: {
      input: 'AI confidence score: 0.85 → classified high complexity',
      result: 'high_hours → 12 hrs',
    },
  },
  text_count_hours: {
    description:
      'Counts how many config-defined patterns match in the response, then returns high or low hours depending on a threshold.',
    pseudoFormula: 'count(matched_patterns) ≥ threshold ? high_hours : low_hours',
    example: {
      input: 'User response matched 3 patterns: "multi-entity", "interco", "consolidation"',
      result: '3 ≥ threshold (2) → high tier hours',
    },
  },
  numeric_band_hours: {
    description:
      'Maps a numeric questionnaire answer through config-defined value bands to a fixed hours amount.',
    pseudoFormula: 'bands.find(b → answer ∈ b.range) → b.hours',
    example: {
      input: 'User answered: 5 legal entities',
      result: '5 ∈ band [4–7] → 6 hrs',
    },
  },
  text_pattern_sum_hours: {
    description:
      'Accumulates hours for each config pattern found in the response - each matched pattern adds its configured amount.',
    pseudoFormula: 'Σ pattern_hours for each matched_pattern',
    example: {
      input: 'User response matched: "multi-currency" (+4 hrs), "revenue recognition" (+2 hrs)',
      result: '4 + 2 = 6 hrs',
    },
  },
  signal_sum: {
    description:
      'Sums the values of a set of named sub-signals declared in the task configuration.',
    pseudoFormula: "sig('sub_signal_1') + sig('sub_signal_2') + …",
    example: {
      input: 'sub_signal_1 = 4 hrs, sub_signal_2 = 6 hrs, sub_signal_3 = 2 hrs',
      result: '4 + 6 + 2 = 12 hrs',
    },
  },
  internal_signal_gate: {
    description:
      'Returns a flat hours value when an upstream detection signal flagged an external tool, otherwise zero.',
    pseudoFormula: "upstream_flag = 'external' ? gate_hours : 0",
    example: {
      input: 'Upstream signal detected external billing tool',
      result: "upstream_flag = 'external' → 8 hrs gate applied",
    },
  },
  payment_processor_hours: {
    description:
      'Combines the payment processor type (internal vs. external) with the transaction volume answer to produce implementation hours.',
    pseudoFormula: 'processor_type × volume_band → hours',
    example: {
      input: 'User: external processor (Stripe), high transaction volume',
      result: 'external × high → 8 hrs',
    },
  },
  ai_tool_extraction: {
    description:
      'Extracts third-party tool mentions from free-text using AI inference and alias matching, then sums per-tool hours from the config.',
    pseudoFormula: 'Σ tool_hours for each detected_tool',
    example: {
      input: 'AI detected in response: DocuSign (alias "e-sign"), Stripe',
      result: 'DocuSign (4 hrs) + Stripe (6 hrs) = 10 hrs',
    },
  },
  third_party_dedup: {
    description:
      'Prevents double-counting by keeping only the highest hours value for each tool across multiple extraction signals.',
    pseudoFormula: 'Σ max(hours_in_signal_A, hours_in_signal_B, …) per unique_tool',
    example: {
      input: 'DocuSign found in signal A (4 hrs) and signal B (4 hrs)',
      result: 'max(4, 4) = 4 hrs - one entry kept',
    },
  },
};

/**
 * Full formula segment types for the signal detail pane.
 * sig → clickable chip, cfg → config chip with default, field → questionnaire field ref.
 */
type FormulaSegment =
  | { type: 'sig'; key: string }
  | { type: 'cfg'; key: string; defaultVal: string }
  | { type: 'field'; fn: string; key: string; extra?: string }
  | { type: 'text'; text: string };

/** Tokenises any formula/pseudo-formula expression into typed segments. */
function parseFormulaExpr(expr: string): FormulaSegment[] {
  const segments: FormulaSegment[] = [];
  const re = /\b(sig|cfg|resp|has|num)\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(expr)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: expr.slice(lastIndex, match.index) });
    }
    const fn = match[1]!;
    const argsRaw = match[2]!;
    const args = argsRaw.split(/,\s*/).map((a) => a.trim().replace(/^'|\'$/g, ''));
    const key = args[0] ?? '';
    const arg2 = args[1] ?? null;
    if (fn === 'sig') {
      segments.push({ type: 'sig', key });
    } else if (fn === 'cfg') {
      segments.push({ type: 'cfg', key, defaultVal: arg2 ?? '' });
    } else {
      segments.push({ type: 'field', fn, key, extra: arg2 ?? undefined });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < expr.length) {
    segments.push({ type: 'text', text: expr.slice(lastIndex) });
  }
  return segments;
}

/**
 * Renders a formula expression as inline chips.
 * sig/cfg: clickable when a handler is provided, tooltip-only when not.
 * field refs: always static spans.
 */
function FormulaChips({
  expr,
  signals,
  static: isStatic = false,
}: {
  expr: string;
  signals: Record<string, SignalInfo>;
  static?: boolean;
}) {
  const segments = React.useMemo(() => parseFormulaExpr(expr), [expr]);
  return (
    <span className="ptp-sdt__formula-line">
      {segments.map((seg, i) => {
        if (seg.type === 'sig') {
          const chip = (
            <span key={i} className="ptp-sdt__sig-chip">
              sig('{seg.key}')
            </span>
          );
          if (isStatic) return chip;
          const label = signals[seg.key]?.label ?? seg.key;
          return (
            <Tooltip.Root key={i}>
              <Tooltip.Trigger asChild>
                <span className="ptp-sdt__sig-chip">sig('{seg.key}')</span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                  {label}
                  <Tooltip.Arrow className="ui-tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        }
        if (seg.type === 'cfg') {
          const chip = (
            <span key={i} className="ptp-sdt__cfg-chip">
              {seg.key} = {seg.defaultVal}
            </span>
          );
          if (isStatic) return chip;
          return (
            <Tooltip.Root key={i}>
              <Tooltip.Trigger asChild>
                <span className="ptp-sdt__cfg-chip">
                  {seg.key} = {seg.defaultVal}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                  Config key · default: {seg.defaultVal}
                  <Tooltip.Arrow className="ui-tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        }
        if (seg.type === 'field') {
          const display = seg.extra
            ? `${seg.fn}('${seg.key}', '${seg.extra}')`
            : `${seg.fn}('${seg.key}')`;
          return (
            <span key={i} className="ptp-sdt__field-ref">
              {display}
            </span>
          );
        }
        return (
          <span key={i} className="ptp-sdt__expr-text">
            {(seg as { text: string }).text}
          </span>
        );
      })}
    </span>
  );
}

// ---- Estimate overview (sections / subsections / rules) ----

interface EstimateRuleInfo {
  ruleKey: string;
  label: string;
  description: string | null;
  conditionExpr: string;
  valueExpr: string;
  sourceFieldKeys: string[] | null;
}

interface EstimateSubsectionInfo {
  subsectionKey: string;
  subsectionLabel: string;
  rules: EstimateRuleInfo[];
}

interface EstimateSectionInfo {
  sectionKey: string;
  sectionLabel: string;
  subsections: EstimateSubsectionInfo[];
}

interface EstimateRulesetSummary {
  id: number;
  rulesetKey: string;
  displayName: string;
  engineVersion: string;
  categoryKey: string | null;
  status: string;
  isActive: boolean;
  scope: string;
  rangeConfig: EstimateRangeConfig;
}

interface EstimateOverviewData {
  rulesets: EstimateRulesetSummary[];
  ruleset: { rulesetKey: string; displayName: string; engineVersion: string };
  sections: EstimateSectionInfo[];
  signals: Record<string, SignalInfo>;
  resourcePlanConfig: {
    integration: EstimateIntegrationConfig | null;
    subfolder?: string | null;
    discountPercentage?: number | null;
    offshorePercentage?: number | null;
    profileSubtype?: string | null;
    availableProfileSubtypes?: string[];
  };
  sowConfig: {
    integration: EstimateIntegrationConfig | null;
    subfolder?: string | null;
  };
  exportSettings: ExportGlobalSettings;
}

interface EstimateIntegrationConfig {
  folderId: string | null;
  integrationUuid: string | null;
}

interface ExportGlobalSettings {
  folderPerCustomer: boolean;
  integrationUuid: string | null;
  parentFolderId: string | null;
}

interface EstimateRangeConfig {
  upperPct: number | null;
  lowerPct: number | null;
  disclaimerText: string | null;
  /**
   * Ticket/initiative classification boundary in hours - an item at or below it
   * is a ticket, above it an initiative. Null = default (40).
   */
  ticketMaxHours?: number | null;
  /**
   * Fallback AVERAGE ticket size in hours (used only when the deal has no
   * estimated tickets to average). Null = default (20).
   */
  ticketEffortHours?: number | null;
  /**
   * Fallback AVERAGE initiative size in hours (used only when the deal has no
   * estimated initiatives to average). Null = default (60).
   */
  initiativeEffortHours?: number | null;
  integration?: EstimateIntegrationConfig | null;
  subfolder?: string | null;
}

function EstimateRulesetSection({
  section,
  signals,
  onRuleClick,
  selectedRuleKey,
  initialOpen = false,
}: {
  section: EstimateSectionInfo;
  signals: Record<string, SignalInfo>;
  onRuleClick: (rule: EstimateRuleInfo) => void;
  selectedRuleKey?: string | null;
  initialOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(initialOpen);
  const ruleCount = section.subsections.reduce((sum, s) => sum + s.rules.length, 0);

  return (
    <div className={`ptp-est-section${isOpen ? ' ptp-est-section--open' : ''}`}>
      <button
        type="button"
        className="ptp-est-section__hdr"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
      >
        <span
          className={`ptp-tree-chevron${isOpen ? ' ptp-tree-chevron--open' : ''}`}
          aria-hidden="true"
        />
        <span className="ptp-est-section__title">{section.sectionLabel}</span>
        <div className="ptp-est-section__meta">
          <span className="ptp-est-section__count">{ruleCount}</span>
        </div>
      </button>

      {isOpen && (
        <div className="ptp-est-section__body">
          {section.subsections.map((sub) => (
            <div key={sub.subsectionKey} className="ptp-est-subsection">
              <div className="ptp-est-subsection__hdr">
                <span className="ptp-est-subsection__label">{sub.subsectionLabel}</span>
                <span className="ptp-est-subsection__count">{sub.rules.length}</span>
              </div>
              <ul className="ptp-est-rule-list">
                {sub.rules.map((rule) => {
                  const isSelected = rule.ruleKey === selectedRuleKey;
                  const formulaContent = parseFormulaExpr(rule.valueExpr).map((seg, i) => {
                    if (seg.type === 'sig') {
                      const isSynth = !signals[seg.key];
                      return (
                        <span
                          key={i}
                          className={`ptp-est-sig-chip${isSynth ? ' ptp-est-sig-chip--synth' : ''}`}
                        >
                          {`sig('${seg.key}')`}
                        </span>
                      );
                    }
                    if (seg.type === 'cfg') {
                      return (
                        <span key={i} className="ptp-est-cfg-chip">
                          {`cfg('${seg.key}')`}
                        </span>
                      );
                    }
                    if (seg.type === 'text') {
                      return <span key={i}>{seg.text}</span>;
                    }
                    const display = seg.extra
                      ? `${seg.fn}('${seg.key}', '${seg.extra}')`
                      : `${seg.fn}('${seg.key}')`;
                    return <span key={i}>{display}</span>;
                  });
                  return (
                    <li
                      key={rule.ruleKey}
                      className={`ptp-est-rule${isSelected ? ' ptp-est-rule--selected' : ''}`}
                    >
                      <button
                        type="button"
                        className="ptp-est-rule__row-btn"
                        onClick={() => onRuleClick(rule)}
                      >
                        <div className="ptp-est-rule__main">
                          <span className="ptp-est-rule__label">{rule.label}</span>
                          <code className="ptp-est-rule__expr">{formulaContent}</code>
                        </div>
                        {rule.description && (
                          <span className="ptp-est-rule__sig-hint">{rule.description}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Statement of Work configuration section. Mirrors the Estimate / Resource Plan
 * config panes but scoped to the Google Drive storage integration only - the SOW
 * export route consumes config(domain='sales_sow') to choose the export folder.
 */
interface SowTemplateSummary {
  id: number;
  uuid: string;
  label: string;
  version: string;
  isDefault: boolean;
  status: string;
  productKey: string | null;
  exportType: string;
  provider: string;
  scope: 'system' | 'workspace';
  categoryKey: string | null;
  contract: unknown;
  metadata?: {
    previewSample?: PreviewSample;
    fidelity?: SowFidelityMetadata;
    assetSlots?: Record<string, string>;
  } | null;
}

// Notice shown inside the per-document-type integration sections when the global
// "per-customer folder" setting owns the integration + customer folder. The
// sub-folder within each customer's folder is still chosen per document type below.
function GlobalExportOverrideNotice() {
  return (
    <div className="ptp-est-range-config__block-hint" role="status">
      The Google Drive account and the per-customer folder are managed in{' '}
      <strong>Global Settings</strong>. Choose the sub-folder within each customer's folder for this
      document type below.
    </div>
  );
}

// A single text input for the per-document-type sub-folder name. Typing a name
// that already exists reuses it (the backend find-or-creates), so this one field
// covers both "pick existing" and "create new". `parentHint` describes the folder
// the sub-folder is created inside (e.g. "each customer's folder" or a folder name).
function ExportSubfolderField({
  value,
  onChange,
  parentHint,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  parentHint: string;
  disabled?: boolean;
}) {
  const trimmed = value.trim();
  return (
    <div className="ptp-est-range-config__block-field">
      <label className="ptp-est-range-config__block-label">Sub-folder (optional)</label>
      <input
        className="ptp-est-range-config__create-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Pre-Sales or Pre-Sales/Drafts"
        disabled={disabled}
      />
      <div className="ptp-est-range-config__block-hint">
        {trimmed
          ? `“${trimmed}” is created (or reused) inside ${parentHint}, and exports are saved there. Use “/” to nest multiple levels.`
          : `Leave empty to save directly in ${parentHint}. Use “/” to nest multiple levels (e.g. Pre-Sales/Drafts).`}
      </div>
    </div>
  );
}

// Consolidated export-settings payload sent to PATCH .../export-settings. One
// workspace Drive account + base destination folder + per-customer toggle, plus
// an optional sub-folder per document type (nested under the resolved base).
interface ConsolidatedExportPayload {
  folderPerCustomer: boolean;
  integration: { integrationUuid: string | null; folderId: string | null };
  estimateSubfolder: string | null;
  resourcePlanSubfolder: string | null;
  sowSubfolder: string | null;
}

/**
 * Debounced auto-save for the Global Settings cards. `signature` is a string fingerprint of the
 * current draft values - it changes on every edit, re-arming the debounce timer (true debounce, so
 * a save fires only after the user pauses). The save runs only when `canSave` (dirty + valid) and
 * not already saving, so it never loops once the parent resyncs the source after a save.
 */
function useDebouncedAutoSave(
  signature: string,
  opts: { canSave: boolean; isSaving: boolean; save: () => void; delayMs?: number }
) {
  const { canSave, isSaving, save, delayMs = 800 } = opts;
  const saveRef = React.useRef(save);
  saveRef.current = save;
  React.useEffect(() => {
    if (!canSave || isSaving) return;
    const t = setTimeout(() => saveRef.current(), delayMs);
    return () => clearTimeout(t);
  }, [signature, canSave, isSaving, delayMs]);
}

// Small muted "Saving… / Saved" status that replaces a manual Save button under auto-save.
function AutoSaveStatus({ pending }: { pending: boolean }) {
  return <span className="ptp-gs-autosave">{pending ? 'Saving…' : 'Saved'}</span>;
}

// Compact "Export to Google Drive" card for the admin Global Settings tab. Owns the
// single workspace Drive account, the base destination folder, the per-customer
// toggle, and the optional per-document sub-folders - the whole export model in one
// place (replacing the per-document-type pickers that used to live in the category
// tabs). The account + folder are always shown (not gated behind the toggle).
function ExportDestinationCard({
  exportSettings,
  estimateSubfolder,
  resourcePlanSubfolder,
  sowSubfolder,
  onSave,
  isSaving,
}: {
  exportSettings: ExportGlobalSettings | undefined;
  estimateSubfolder: string | null;
  resourcePlanSubfolder: string | null;
  sowSubfolder: string | null;
  onSave: (payload: ConsolidatedExportPayload) => void;
  isSaving: boolean;
}) {
  const [folderPerCustomer, setFolderPerCustomer] = React.useState(
    () => exportSettings?.folderPerCustomer ?? false
  );
  const [integrationUuidDraft, setIntegrationUuidDraft] = React.useState(
    () => exportSettings?.integrationUuid ?? ''
  );
  const [parentFolderDraft, setParentFolderDraft] = React.useState(
    () => exportSettings?.parentFolderId ?? ''
  );
  const [estimateSubDraft, setEstimateSubDraft] = React.useState(() => estimateSubfolder ?? '');
  const [resourcePlanSubDraft, setResourcePlanSubDraft] = React.useState(
    () => resourcePlanSubfolder ?? ''
  );
  const [sowSubDraft, setSowSubDraft] = React.useState(() => sowSubfolder ?? '');
  const [baseFolderName, setBaseFolderName] = React.useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [availableIntegrations, setAvailableIntegrations] = React.useState<any[]>([]);
  const [isFetchingIntegrations, setIsFetchingIntegrations] = React.useState(false);
  const [hasFetchedIntegrations, setHasFetchedIntegrations] = React.useState(false);

  // The account picker is always shown, so fetch the Drive integrations on mount.
  React.useEffect(() => {
    let active = true;
    if (availableIntegrations.length === 0) {
      setIsFetchingIntegrations(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      apiFetch<{ integrations: any[] }>('/api/integration')
        .then((data) => {
          if (!active) return;
          const driveIntegrations = data.integrations.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (i: any) => i.definition.slug === 'google-drive' && i.lifecycleStatus === 'active'
          );
          setAvailableIntegrations(driveIntegrations);
          // Drop a saved selection that's no longer available; otherwise leave the
          // admin's choice (or empty) untouched - never auto-pick (it would mark the
          // card dirty on load).
          const selectionStillValid = driveIntegrations.some(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (i: any) => i.uuid === integrationUuidDraft
          );
          if (integrationUuidDraft && !selectionStillValid) {
            setIntegrationUuidDraft('');
          }
        })
        .finally(() => {
          if (active) {
            setIsFetchingIntegrations(false);
            setHasFetchedIntegrations(true);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [availableIntegrations.length, integrationUuidDraft]);

  React.useEffect(() => {
    setFolderPerCustomer(exportSettings?.folderPerCustomer ?? false);
    setIntegrationUuidDraft(exportSettings?.integrationUuid ?? '');
    setParentFolderDraft(exportSettings?.parentFolderId ?? '');
  }, [exportSettings]);
  React.useEffect(() => setEstimateSubDraft(estimateSubfolder ?? ''), [estimateSubfolder]);
  React.useEffect(
    () => setResourcePlanSubDraft(resourcePlanSubfolder ?? ''),
    [resourcePlanSubfolder]
  );
  React.useEffect(() => setSowSubDraft(sowSubfolder ?? ''), [sowSubfolder]);

  const dirty = React.useMemo(() => {
    if (folderPerCustomer !== (exportSettings?.folderPerCustomer ?? false)) return true;
    if (integrationUuidDraft !== (exportSettings?.integrationUuid ?? '')) return true;
    if (parentFolderDraft !== (exportSettings?.parentFolderId ?? '')) return true;
    if (estimateSubDraft !== (estimateSubfolder ?? '')) return true;
    if (resourcePlanSubDraft !== (resourcePlanSubfolder ?? '')) return true;
    if (sowSubDraft !== (sowSubfolder ?? '')) return true;
    return false;
  }, [
    exportSettings,
    estimateSubfolder,
    resourcePlanSubfolder,
    sowSubfolder,
    folderPerCustomer,
    integrationUuidDraft,
    parentFolderDraft,
    estimateSubDraft,
    resourcePlanSubDraft,
    sowSubDraft,
  ]);

  const handleSave = React.useCallback(() => {
    if (!dirty) return;
    onSave({
      folderPerCustomer,
      integration: {
        integrationUuid: integrationUuidDraft || null,
        folderId: parentFolderDraft.trim() || null,
      },
      estimateSubfolder: estimateSubDraft.trim() || null,
      resourcePlanSubfolder: resourcePlanSubDraft.trim() || null,
      sowSubfolder: sowSubDraft.trim() || null,
    });
  }, [
    dirty,
    folderPerCustomer,
    integrationUuidDraft,
    parentFolderDraft,
    estimateSubDraft,
    resourcePlanSubDraft,
    sowSubDraft,
    onSave,
  ]);

  const signature = JSON.stringify([
    folderPerCustomer,
    integrationUuidDraft,
    parentFolderDraft,
    estimateSubDraft,
    resourcePlanSubDraft,
    sowSubDraft,
  ]);
  useDebouncedAutoSave(signature, { canSave: dirty, isSaving, save: handleSave });

  const [showSubfolders, setShowSubfolders] = React.useState(
    () => !!(estimateSubfolder || resourcePlanSubfolder || sowSubfolder)
  );

  const subfolderParentHint = folderPerCustomer
    ? "each customer's folder"
    : baseFolderName || 'the destination folder';

  const subfolderFields = [
    { label: 'Estimate', value: estimateSubDraft, onChange: setEstimateSubDraft },
    { label: 'Resource Plan', value: resourcePlanSubDraft, onChange: setResourcePlanSubDraft },
    { label: 'Statement of Work', value: sowSubDraft, onChange: setSowSubDraft },
  ];

  const selectedIntegration = availableIntegrations.find((i) => i.uuid === integrationUuidDraft);

  return (
    <div className="ptp-gs-card">
      <div className="ptp-gs-card__header">
        <p className="ptp-gs-section-label">Export to Google Drive</p>
        <AutoSaveStatus pending={dirty || isSaving} />
      </div>

      <div className="ptp-est-range-config__block-field">
        <label className="ptp-est-range-config__block-label">Storage account</label>
        <Select
          value={integrationUuidDraft}
          onValueChange={setIntegrationUuidDraft}
          size="comfortable"
          disabled={isFetchingIntegrations}
          loading={isFetchingIntegrations}
        >
          {availableIntegrations.map((i) => (
            <SelectItem key={i.uuid} value={i.uuid}>
              {i.displayName}
            </SelectItem>
          ))}
        </Select>
        {selectedIntegration?.description ? (
          <span className="ptp-est-range-config__block-hint">
            Connected as {selectedIntegration.description}.
          </span>
        ) : null}
        {hasFetchedIntegrations && availableIntegrations.length === 0 && !isFetchingIntegrations && (
          <span className="ptp-est-range-config__block-error">
            No active Google Drive integrations found. Connect one in the Integrations page.
          </span>
        )}
      </div>

      {integrationUuidDraft ? (
        <div className="ptp-est-range-config__block-field">
          <DriveFolderField
            compact
            integrationUuid={integrationUuidDraft}
            value={parentFolderDraft}
            onChange={setParentFolderDraft}
            label="Destination folder"
            hint="All exports are saved here."
            onSelectedFolderNameChange={setBaseFolderName}
          />
        </div>
      ) : null}

      <div className="ptp-gs-row ptp-gs-row--compact">
        <div className="ptp-gs-row-info">
          <span className="ptp-gs-row-name">Create a subfolder per customer</span>
          <span className="ptp-gs-row-desc ptp-gs-row-desc--sm">
            Each customer's exports go in their own folder, inside the destination folder.
          </span>
        </div>
        <label
          className="ptp-est-range-config__pill-toggle"
          aria-label="Create a subfolder per customer"
        >
          <input
            type="checkbox"
            checked={folderPerCustomer}
            onChange={(e) => setFolderPerCustomer(e.target.checked)}
          />
          <span className="ptp-est-range-config__pill" aria-hidden="true" />
        </label>
      </div>

      <div className="ptp-gs-disclosure">
        <button
          type="button"
          className="ptp-gs-disclosure__toggle"
          aria-expanded={showSubfolders}
          onClick={() => setShowSubfolders((o) => !o)}
        >
          <span
            className={`ptp-tree-chevron${showSubfolders ? ' ptp-tree-chevron--open' : ''}`}
            aria-hidden="true"
          />
          Per-document subfolders (optional)
        </button>
        {showSubfolders && (
          <div className="ptp-gs-disclosure__body">
            {subfolderFields.map((f) => (
              <div key={f.label} className="ptp-gs-subrow">
                <label className="ptp-gs-subrow__label">{f.label}</label>
                <input
                  className="ptp-est-range-config__create-input"
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                  placeholder="e.g. Pre-Sales or Pre-Sales/Drafts"
                />
              </div>
            ))}
            <p className="ptp-est-range-config__block-hint">
              Created inside {subfolderParentHint}. Use “/” to nest multiple levels; leave empty to
              save directly in {subfolderParentHint}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact "Estimate defaults" card: the estimate range +/-% and disclaimer text,
// moved out of the (hidden) Deal Categories Estimate tab. Saves through the existing
// estimate-range-config route, passing through the row's integration/subfolder
// unchanged so the export config (owned by the card above) is never clobbered.
function EstimateDefaultsCard({
  rangeConfig,
  onSave,
  isSaving,
}: {
  rangeConfig: EstimateRangeConfig;
  onSave: (config: EstimateRangeConfig) => void;
  isSaving: boolean;
}) {
  const [rangeEnabled, setRangeEnabled] = React.useState(() => rangeConfig.upperPct !== null);
  const [upperDraft, setUpperDraft] = React.useState(() => String(rangeConfig.upperPct ?? 15));
  const [lowerDraft, setLowerDraft] = React.useState(() => String(rangeConfig.lowerPct ?? 15));
  const [disclaimerEnabled, setDisclaimerEnabled] = React.useState(
    () => rangeConfig.disclaimerText !== null
  );
  const [disclaimerDraft, setDisclaimerDraft] = React.useState(
    () => rangeConfig.disclaimerText ?? ''
  );
  // Contract-recommendation sizing; empty input = not overridden (server clears
  // the config row and the defaults apply: boundary 40, fallback averages 20/60).
  const [ticketMaxDraft, setTicketMaxDraft] = React.useState(() =>
    rangeConfig.ticketMaxHours != null ? String(rangeConfig.ticketMaxHours) : ''
  );
  const [ticketEffortDraft, setTicketEffortDraft] = React.useState(() =>
    rangeConfig.ticketEffortHours != null ? String(rangeConfig.ticketEffortHours) : ''
  );
  const [initiativeEffortDraft, setInitiativeEffortDraft] = React.useState(() =>
    rangeConfig.initiativeEffortHours != null ? String(rangeConfig.initiativeEffortHours) : ''
  );

  React.useEffect(() => {
    setRangeEnabled(rangeConfig.upperPct !== null);
    setUpperDraft(String(rangeConfig.upperPct ?? 15));
    setLowerDraft(String(rangeConfig.lowerPct ?? 15));
    setDisclaimerEnabled(rangeConfig.disclaimerText !== null);
    setDisclaimerDraft(rangeConfig.disclaimerText ?? '');
    setTicketMaxDraft(rangeConfig.ticketMaxHours != null ? String(rangeConfig.ticketMaxHours) : '');
    setTicketEffortDraft(
      rangeConfig.ticketEffortHours != null ? String(rangeConfig.ticketEffortHours) : ''
    );
    setInitiativeEffortDraft(
      rangeConfig.initiativeEffortHours != null ? String(rangeConfig.initiativeEffortHours) : ''
    );
  }, [rangeConfig]);

  const upper = rangeEnabled ? parseInt(upperDraft, 10) : null;
  const lower = rangeEnabled ? parseInt(lowerDraft, 10) : null;
  const disclaimer = disclaimerEnabled ? disclaimerDraft.trim() || null : null;
  const parseEffort = (draft: string): number | null =>
    draft.trim() === '' ? null : Number.parseFloat(draft);
  const ticketMax = parseEffort(ticketMaxDraft);
  const ticketEffort = parseEffort(ticketEffortDraft);
  const initiativeEffort = parseEffort(initiativeEffortDraft);
  const effortInvalid = (v: number | null) => v !== null && (!Number.isFinite(v) || v <= 0);

  const dirty =
    (rangeConfig.upperPct ?? null) !== upper ||
    (rangeConfig.lowerPct ?? null) !== lower ||
    (rangeConfig.disclaimerText ?? null) !== disclaimer ||
    (rangeConfig.ticketMaxHours ?? null) !== ticketMax ||
    (rangeConfig.ticketEffortHours ?? null) !== ticketEffort ||
    (rangeConfig.initiativeEffortHours ?? null) !== initiativeEffort;

  const invalid =
    (rangeEnabled &&
      (!Number.isInteger(upper) ||
        !Number.isInteger(lower) ||
        (upper as number) < 0 ||
        (upper as number) > 100 ||
        (lower as number) < 0 ||
        (lower as number) > 100)) ||
    effortInvalid(ticketMax) ||
    effortInvalid(ticketEffort) ||
    effortInvalid(initiativeEffort);

  const handleSave = React.useCallback(() => {
    if (!dirty || invalid) return;
    onSave({
      upperPct: upper,
      lowerPct: lower,
      disclaimerText: disclaimer,
      ticketMaxHours: ticketMax,
      ticketEffortHours: ticketEffort,
      initiativeEffortHours: initiativeEffort,
      integration: rangeConfig.integration ?? null,
      subfolder: rangeConfig.subfolder ?? null,
    });
  }, [dirty, invalid, upper, lower, disclaimer, ticketMax, ticketEffort, initiativeEffort, rangeConfig.integration, rangeConfig.subfolder, onSave]);

  const signature = JSON.stringify([rangeEnabled, upperDraft, lowerDraft, disclaimerEnabled, disclaimer, ticketMaxDraft, ticketEffortDraft, initiativeEffortDraft]);
  useDebouncedAutoSave(signature, { canSave: dirty && !invalid, isSaving, save: handleSave });

  return (
    <div className="ptp-gs-card">
      <div className="ptp-gs-card__header">
        <p className="ptp-gs-section-label">Estimate defaults</p>
        <AutoSaveStatus pending={(dirty && !invalid) || isSaving} />
      </div>

      <div className="ptp-gs-row ptp-gs-row--compact">
        <div className="ptp-gs-row-info">
          <span className="ptp-gs-row-name">Estimate range</span>
          <span className="ptp-gs-row-desc ptp-gs-row-desc--sm">
            Show upper and lower variance bands on estimate totals.
          </span>
        </div>
        <label className="ptp-est-range-config__pill-toggle" aria-label="Enable estimate range">
          <input
            type="checkbox"
            checked={rangeEnabled}
            onChange={(e) => setRangeEnabled(e.target.checked)}
          />
          <span className="ptp-est-range-config__pill" aria-hidden="true" />
        </label>
      </div>
      {rangeEnabled && (
        <div className="ptp-est-range-config__fields">
          <div className="ptp-est-range-config__field">
            <span className="ptp-est-range-config__field-label">Upper %</span>
            <NumberInput
              thousandSeparator={false}
              min={0}
              max={100}
              step={1}
              value={upperDraft}
              onChange={(e) => setUpperDraft(e.target.value)}
              className="ptp-est-range-config__input"
            />
          </div>
          <div className="ptp-est-range-config__field">
            <span className="ptp-est-range-config__field-label">Lower %</span>
            <NumberInput
              thousandSeparator={false}
              min={0}
              max={100}
              step={1}
              value={lowerDraft}
              onChange={(e) => setLowerDraft(e.target.value)}
              className="ptp-est-range-config__input"
            />
          </div>
        </div>
      )}

      <div className="ptp-gs-row ptp-gs-row--compact">
        <div className="ptp-gs-row-info">
          <span className="ptp-gs-row-name">Estimate disclaimer</span>
          <span className="ptp-gs-row-desc ptp-gs-row-desc--sm">
            Custom warning text printed on the estimate document.
          </span>
        </div>
        <label
          className="ptp-est-range-config__pill-toggle"
          aria-label="Enable estimate disclaimer"
        >
          <input
            type="checkbox"
            checked={disclaimerEnabled}
            onChange={(e) => {
              const next = e.target.checked;
              setDisclaimerEnabled(next);
              if (next && !disclaimerDraft.trim()) {
                setDisclaimerDraft(
                  'All estimates are in hours. Estimates are indicative and subject to final scoping.'
                );
              }
            }}
          />
          <span className="ptp-est-range-config__pill" aria-hidden="true" />
        </label>
      </div>
      {disclaimerEnabled && (
        <textarea
          className="ptp-est-range-config__disclaimer-input"
          rows={3}
          value={disclaimerDraft}
          onChange={(e) => setDisclaimerDraft(e.target.value)}
        />
      )}

      <div className="ptp-gs-row ptp-gs-row--compact">
        <div className="ptp-gs-row-info">
          <span className="ptp-gs-row-name">Ticket / initiative sizing</span>
          <span className="ptp-gs-row-desc ptp-gs-row-desc--sm">
            A ticket is any estimated scope item up to and including the ticket max (default
            40 hours); an initiative is anything above it. Once estimates are generated, this
            classifies the estimated items and the Contract tab shows the ticket and
            initiative counts. The two average sizes (defaults 20 / 60) are fallbacks only:
            they size the client's expected additional tickets/initiatives when the deal has
            no estimated items of that kind to average - the deal's actual estimated averages
            are always preferred. Blank = defaults.
          </span>
        </div>
      </div>
      <div className="ptp-est-range-config__fields">
        <div className="ptp-est-range-config__field">
          <span className="ptp-est-range-config__field-label">Ticket max (hours)</span>
          <NumberInput
            thousandSeparator={false}
            min={1}
            step={1}
            value={ticketMaxDraft}
            placeholder="40"
            onChange={(e) => setTicketMaxDraft(e.target.value)}
            className="ptp-est-range-config__input"
          />
        </div>
        <div className="ptp-est-range-config__field">
          <span className="ptp-est-range-config__field-label">Avg ticket effort (hours)</span>
          <NumberInput
            thousandSeparator={false}
            min={1}
            step={1}
            value={ticketEffortDraft}
            placeholder="20"
            onChange={(e) => setTicketEffortDraft(e.target.value)}
            className="ptp-est-range-config__input"
          />
        </div>
        <div className="ptp-est-range-config__field">
          <span className="ptp-est-range-config__field-label">Avg initiative effort (hours)</span>
          <NumberInput
            thousandSeparator={false}
            min={1}
            step={1}
            value={initiativeEffortDraft}
            placeholder="60"
            onChange={(e) => setInitiativeEffortDraft(e.target.value)}
            className="ptp-est-range-config__input"
          />
        </div>
      </div>
    </div>
  );
}

function StatementOfWorkOverviewSection({
  productUuid,
  config,
  onConfigSave,
  isSaving,
  globalFolderOverrideActive,
}: {
  productUuid: string;
  config: { integration: EstimateIntegrationConfig | null; subfolder?: string | null } | undefined;
  onConfigSave: (config: {
    integration: EstimateIntegrationConfig | null;
    subfolder: string | null;
  }) => void;
  isSaving: boolean;
  globalFolderOverrideActive: boolean;
}) {
  const [activePane, setActivePane] = React.useState<'configuration' | 'template'>('configuration');
  const [navWidth, setNavWidth] = React.useState(240);

  // ── Template pane: SoW export templates (workspace.template, key='deal:sow') ──
  const [sowTemplates, setSowTemplates] = React.useState<SowTemplateSummary[]>([]);
  const [selectedSowTemplateId, setSelectedSowTemplateId] = React.useState<number | null>(null);
  const [isLoadingSowTemplates, setIsLoadingSowTemplates] = React.useState(false);
  const [isSavingSowTemplate, setIsSavingSowTemplate] = React.useState(false);
  const { workspace } = useWorkspace();
  const { categoryKey: routeCategoryKey } = useParams<{ categoryKey?: string }>();
  // Deep-link target: ?template=<uuid> (e.g. from the /generate-sow-template agent's
  // closing link) lands with that template selected rather than the category default.
  const [searchParams] = useSearchParams();
  const wantTemplateUuid = searchParams.get('template');
  // Brand assets the Cover & Brand panel can swap a template logo to.
  const sowWorkspaceBrand = React.useMemo(
    () => ({
      headerLogoAssetUuid: workspace?.headerLogoAssetUuid ?? null,
      documentLogoAssetUuid: workspace?.documentLogoAssetUuid ?? null,
      coverBackgroundAssetUuid: workspace?.coverBackgroundAssetUuid ?? null,
      footerLogoAssetUuid: workspace?.footerLogoAssetUuid ?? null,
      primaryColor: workspace?.brandPrimaryColor ?? null,
    }),
    [workspace]
  );

  // Returns the freshly-loaded templates so callers can re-select after a mutation.
  const loadSowTemplates = React.useCallback(async (): Promise<SowTemplateSummary[]> => {
    if (!productUuid) return [];
    setIsLoadingSowTemplates(true);
    try {
      const data = await apiFetch<{ templates: SowTemplateSummary[] }>(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-templates`
      );
      setSowTemplates(data.templates);
      return data.templates;
    } catch {
      return [];
    } finally {
      setIsLoadingSowTemplates(false);
    }
  }, [productUuid]);

  React.useEffect(() => {
    let active = true;
    void loadSowTemplates().then((templates) => {
      if (!active) return;
      // Prefer an explicitly deep-linked template (?template=), else the default, else first.
      setSelectedSowTemplateId((prev) => {
        if (prev && templates.some((t) => t.id === prev)) return prev;
        const wanted = wantTemplateUuid
          ? templates.find((t) => t.uuid === wantTemplateUuid)
          : undefined;
        return (wanted ?? templates.find((t) => t.isDefault) ?? templates[0])?.id ?? null;
      });
    });
    return () => {
      active = false;
    };
  }, [loadSowTemplates, wantTemplateUuid]);

  const selectedSowTemplate = React.useMemo(
    () => sowTemplates.find((t) => t.id === selectedSowTemplateId) ?? null,
    [sowTemplates, selectedSowTemplateId]
  );

  const handleSaveSowTemplate = React.useCallback(
    async (
      contract: DocumentContract,
      previewSample: PreviewSample,
      assetSlots?: Record<string, string>
    ) => {
      if (!productUuid || !selectedSowTemplate) return;
      setIsSavingSowTemplate(true);
      try {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-templates/${encodeURIComponent(selectedSowTemplate.uuid)}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              contract,
              previewSample,
              ...(assetSlots ? { assetSlots } : {}),
            }),
          }
        );
        await loadSowTemplates();
      } finally {
        setIsSavingSowTemplate(false);
      }
    },
    [productUuid, selectedSowTemplate, loadSowTemplates]
  );

  const handleDuplicateSowTemplate = React.useCallback(async () => {
    if (!productUuid || !selectedSowTemplate) return;
    setIsSavingSowTemplate(true);
    try {
      const { template } = await apiFetch<{ template: { uuid: string } }>(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-templates`,
        {
          method: 'POST',
          body: JSON.stringify({
            sourceTemplateUuid: selectedSowTemplate.uuid,
            label: `${selectedSowTemplate.label} (copy)`,
          }),
        }
      );
      const templates = await loadSowTemplates();
      const created = templates.find((t) => t.uuid === template.uuid);
      if (created) setSelectedSowTemplateId(created.id);
    } finally {
      setIsSavingSowTemplate(false);
    }
  }, [productUuid, selectedSowTemplate, loadSowTemplates]);

  const handlePublishSowTemplate = React.useCallback(async () => {
    if (!productUuid || !selectedSowTemplate) return;
    setIsSavingSowTemplate(true);
    try {
      await apiFetch(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-templates/${encodeURIComponent(selectedSowTemplate.uuid)}/publish`,
        { method: 'POST' }
      );
      await loadSowTemplates();
    } finally {
      setIsSavingSowTemplate(false);
    }
  }, [productUuid, selectedSowTemplate, loadSowTemplates]);

  const [integrationEnabled, setIntegrationEnabled] = React.useState(() => !!config?.integration);
  const [googleDriveIntegrationUuidDraft, setGoogleDriveIntegrationUuidDraft] = React.useState(
    () => config?.integration?.integrationUuid ?? ''
  );
  const [googleDriveFolderDraft, setGoogleDriveFolderDraft] = React.useState(
    () => config?.integration?.folderId ?? ''
  );
  const [subfolderDraft, setSubfolderDraft] = React.useState(() => config?.subfolder ?? '');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [availableIntegrations, setAvailableIntegrations] = React.useState<any[]>([]);
  const [isFetchingIntegrations, setIsFetchingIntegrations] = React.useState(false);
  const [hasFetchedIntegrations, setHasFetchedIntegrations] = React.useState(false);
  // Resolved name of the selected target folder (surfaced by DriveFolderField) for the
  // subfolder hint below.
  const [googleDriveFolderName, setGoogleDriveFolderName] = React.useState('');

  React.useEffect(() => {
    let active = true;
    if (integrationEnabled && availableIntegrations.length === 0) {
      setIsFetchingIntegrations(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      apiFetch<{ integrations: any[] }>('/api/integration')
        .then((data) => {
          if (!active) return;
          const driveIntegrations = data.integrations.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (i: any) => i.definition.slug === 'google-drive' && i.lifecycleStatus === 'active'
          );
          setAvailableIntegrations(driveIntegrations);
          // Fall back to the first active credential when the currently selected one is
          // no longer among them (e.g. the account was reconnected, minting a new uuid).
          const selectionStillValid = driveIntegrations.some(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (i: any) => i.uuid === googleDriveIntegrationUuidDraft
          );
          if (!selectionStillValid) {
            setGoogleDriveIntegrationUuidDraft(driveIntegrations[0]?.uuid ?? '');
          }
        })
        .finally(() => {
          if (active) {
            setIsFetchingIntegrations(false);
            setHasFetchedIntegrations(true);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [integrationEnabled, availableIntegrations.length, googleDriveIntegrationUuidDraft]);

  React.useEffect(() => {
    setIntegrationEnabled(!!config?.integration);
    setGoogleDriveIntegrationUuidDraft(config?.integration?.integrationUuid ?? '');
    setGoogleDriveFolderDraft(config?.integration?.folderId ?? '');
    setSubfolderDraft(config?.subfolder ?? '');
  }, [config]);

  const configDirty = React.useMemo(() => {
    if (integrationEnabled !== !!config?.integration) return true;
    if (
      integrationEnabled &&
      googleDriveIntegrationUuidDraft !== (config?.integration?.integrationUuid ?? '')
    )
      return true;
    if (integrationEnabled && googleDriveFolderDraft !== (config?.integration?.folderId ?? ''))
      return true;
    // The sub-folder is editable in both modes (global on/off), so compare it unconditionally.
    if (subfolderDraft !== (config?.subfolder ?? '')) return true;
    return false;
  }, [
    config,
    integrationEnabled,
    googleDriveIntegrationUuidDraft,
    googleDriveFolderDraft,
    subfolderDraft,
  ]);

  const handleConfigSave = React.useCallback(() => {
    if (!configDirty) return;
    const folderId = integrationEnabled ? googleDriveFolderDraft.trim() || null : null;
    const integrationUuid = integrationEnabled ? googleDriveIntegrationUuidDraft || null : null;
    onConfigSave({
      integration: integrationEnabled ? { folderId, integrationUuid } : null,
      subfolder: subfolderDraft.trim() || null,
    });
  }, [
    configDirty,
    integrationEnabled,
    googleDriveFolderDraft,
    googleDriveIntegrationUuidDraft,
    subfolderDraft,
    onConfigSave,
  ]);

  return (
    <div className="pcat-artifact-wrap">
      <div className="ptp-est-split">
        {/* ── Left nav ──────────────────────────────────────────────────────── */}
        <div className="ptp-est-nav" style={{ width: `${navWidth}px` }}>
          <div className="ptp-est-nav__list">
            <button
              className={`ptp-est-nav__item${activePane === 'configuration' ? ' ptp-est-nav__item--active' : ''}`}
              onClick={() => setActivePane('configuration')}
            >
              Configuration
            </button>
            <button
              className={`ptp-est-nav__item${activePane === 'template' ? ' ptp-est-nav__item--active' : ''}`}
              onClick={() => setActivePane('template')}
            >
              Template
            </button>
          </div>
        </div>

        <ResizableDivider
          direction="vertical"
          onResize={(delta) => setNavWidth((w) => Math.max(140, Math.min(360, w + delta)))}
          ariaLabel="Resize navigation"
        />

        {/* ── Right detail panel ───────────────────────────────────────────── */}
        <div className="ptp-est-detail">
          {activePane === 'configuration' ? (
            <div className="ptp-est-config-pane">
              <div className="ptp-est-config-pane__header">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!configDirty || isSaving}
                  onClick={() => handleConfigSave()}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
              <div className="ptp-est-range-config">
                <div className="ptp-est-range-config__section">
                  <div className="ptp-est-range-config__section-header">
                    <span className="ptp-est-range-config__section-title">INTEGRATIONS</span>
                    <label
                      className="ptp-est-range-config__pill-toggle"
                      aria-label="Enable integrations"
                    >
                      <input
                        type="checkbox"
                        checked={integrationEnabled}
                        disabled={globalFolderOverrideActive}
                        onChange={(e) => setIntegrationEnabled(e.target.checked)}
                      />
                      <span className="ptp-est-range-config__pill" aria-hidden="true" />
                    </label>
                  </div>
                  <p className="ptp-est-range-config__description">
                    Configure export directories for third-party integrations.
                  </p>
                  {globalFolderOverrideActive && (
                    <div className="ptp-est-range-config__block-fields">
                      <GlobalExportOverrideNotice />
                      <ExportSubfolderField
                        value={subfolderDraft}
                        onChange={setSubfolderDraft}
                        parentHint="each customer's folder"
                      />
                    </div>
                  )}
                  {!globalFolderOverrideActive && integrationEnabled && (
                    <div className="ptp-est-range-config__block-fields">
                      <div className="ptp-est-range-config__block-field">
                        <label className="ptp-est-range-config__block-label">
                          Storage Integration
                        </label>
                        <Select
                          value={googleDriveIntegrationUuidDraft}
                          onValueChange={setGoogleDriveIntegrationUuidDraft}
                          size="comfortable"
                          disabled={isFetchingIntegrations}
                          loading={isFetchingIntegrations}
                        >
                          {availableIntegrations.map((i) => (
                            <SelectItem key={i.uuid} value={i.uuid}>
                              {i.displayName}
                            </SelectItem>
                          ))}
                        </Select>
                        {(() => {
                          const sel = availableIntegrations.find(
                            (i) => i.uuid === googleDriveIntegrationUuidDraft
                          );
                          return sel?.description ? (
                            <span className="ptp-est-range-config__block-hint">
                              Connected as {sel.description}. If you switch the connected Google
                              account, re-pick the target folder below.
                            </span>
                          ) : null;
                        })()}
                        {hasFetchedIntegrations &&
                          availableIntegrations.length === 0 &&
                          !isFetchingIntegrations && (
                            <span className="ptp-est-range-config__block-error">
                              No active Google Drive integrations found. Please connect one in the
                              Integrations page.
                            </span>
                          )}
                      </div>
                      {googleDriveIntegrationUuidDraft && (
                        <div className="ptp-est-range-config__block-field">
                          <DriveFolderField
                            integrationUuid={googleDriveIntegrationUuidDraft}
                            value={googleDriveFolderDraft}
                            onChange={setGoogleDriveFolderDraft}
                            onSelectedFolderNameChange={setGoogleDriveFolderName}
                            label="Target Folder"
                            hint="When a user exports a statement of work, it will be saved in this folder."
                          />
                          <ExportSubfolderField
                            value={subfolderDraft}
                            onChange={setSubfolderDraft}
                            parentHint={googleDriveFolderName || 'the selected folder'}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* ── Template: SoW export template (workspace.template, key='deal:sow') ── */
            <div className="ptp-est-config-pane">
              <div className="ptp-toolbar">
                <div className="ptp-toolbar__left">
                  {sowTemplates.length > 0 && (
                    <Select
                      value={selectedSowTemplateId != null ? String(selectedSowTemplateId) : ''}
                      onValueChange={(v) => setSelectedSowTemplateId(Number(v))}
                      size="sm"
                    >
                      {sowTemplates.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                  {selectedSowTemplate && (
                    <>
                      {/* "Active" only when actually live (published default) - a draft is
                        not yet what the export uses, so it shows its status instead. */}
                      {selectedSowTemplate.status === 'published' &&
                        selectedSowTemplate.isDefault && (
                          <span className="ptp-badge ptp-badge--default">Active</span>
                        )}
                      {selectedSowTemplate.status !== 'published' && (
                        <span
                          className={`ptp-badge ptp-badge--status-${selectedSowTemplate.status}`}
                        >
                          {selectedSowTemplate.status.charAt(0).toUpperCase() +
                            selectedSowTemplate.status.slice(1)}
                        </span>
                      )}
                      {selectedSowTemplate.scope === 'system' ? (
                        <span className="ptp-badge ptp-badge--scope-system">System</span>
                      ) : (
                        <span className="ptp-badge ptp-badge--scope-workspace">Workspace</span>
                      )}
                    </>
                  )}
                </div>
                {workspace?.sowStudioEnabled && selectedSowTemplate && routeCategoryKey ? (
                  <div className="ptp-toolbar__right">
                    <Button asChild variant="primary" size="sm">
                      <Link
                        to={`/administration/products/${productUuid}/categories/${routeCategoryKey}/sow/studio?template=${selectedSowTemplate.uuid}`}
                      >
                        Open in Studio ↗
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
              {isLoadingSowTemplates ? (
                <p className="ptp-empty-text">Loading…</p>
              ) : !selectedSowTemplate ? (
                <p className="ptp-empty-text">No SoW export template found for this product.</p>
              ) : (
                <SowTemplateWorkbench
                  key={selectedSowTemplate.id}
                  template={selectedSowTemplate}
                  saving={isSavingSowTemplate}
                  onSave={handleSaveSowTemplate}
                  onDuplicate={handleDuplicateSowTemplate}
                  onPublish={handlePublishSowTemplate}
                  workspaceBrand={sowWorkspaceBrand}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResourcePlanOverviewSection({
  productUuid: _productUuid,
  config,
  onConfigSave,
  isSaving,
  globalFolderOverrideActive,
}: {
  productUuid: string;
  config:
    | {
        integration: EstimateIntegrationConfig | null;
        subfolder?: string | null;
        discountPercentage?: number | null;
        offshorePercentage?: number | null;
        profileSubtype?: string | null;
        availableProfileSubtypes?: string[];
      }
    | undefined;
  onConfigSave: (config: {
    integration: EstimateIntegrationConfig | null;
    subfolder: string | null;
    discountPercentage?: number | null;
    offshorePercentage?: number | null;
    profileSubtype?: string | null;
  }) => void;
  isSaving: boolean;
  globalFolderOverrideActive: boolean;
}) {
  const [activePane, setActivePane] = React.useState<
    'configuration' | 'rate-card' | 'resource-model'
  >('configuration');
  const [navWidth, setNavWidth] = React.useState(240);

  const [integrationEnabled, setIntegrationEnabled] = React.useState(() => !!config?.integration);
  const [googleDriveIntegrationUuidDraft, setGoogleDriveIntegrationUuidDraft] = React.useState(
    () => config?.integration?.integrationUuid ?? ''
  );
  const [googleDriveFolderDraft, setGoogleDriveFolderDraft] = React.useState(
    () => config?.integration?.folderId ?? ''
  );
  const [subfolderDraft, setSubfolderDraft] = React.useState(() => config?.subfolder ?? '');

  // Discount state derived from resourcePlanConfig
  const [discountPercentage, setDiscountPercentage] = React.useState(
    () => config?.discountPercentage ?? 0
  );
  const [discountEnabled, setDiscountEnabled] = React.useState(
    () => (config?.discountPercentage ?? 0) > 0
  );

  const handleToggleDiscount = (enabled: boolean) => {
    setDiscountEnabled(enabled);
    if (enabled && discountPercentage === 0) {
      setDiscountPercentage(10); // default to 10%
    }
  };

  // Default offshore % state derived from resourcePlanConfig (mirrors discount).
  const [offshorePercentage, setOffshorePercentage] = React.useState(
    () => config?.offshorePercentage ?? 0
  );
  const [offshoreEnabled, setOffshoreEnabled] = React.useState(
    () => config?.offshorePercentage != null
  );

  const handleToggleOffshore = (enabled: boolean) => {
    setOffshoreEnabled(enabled);
    if (enabled && offshorePercentage === 0) {
      setOffshorePercentage(50); // suggested starting value for a hybrid default
    }
  };

  const [availableIntegrations, setAvailableIntegrations] = React.useState<any[]>([]);
  const [isFetchingIntegrations, setIsFetchingIntegrations] = React.useState(false);
  const [hasFetchedIntegrations, setHasFetchedIntegrations] = React.useState(false);
  // Resolved name of the selected target folder (surfaced by DriveFolderField) for the
  // subfolder hint below.
  const [googleDriveFolderName, setGoogleDriveFolderName] = React.useState('');

  // --- Rate Card State ---
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rateCardProfiles, setRateCardProfiles] = React.useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rateCardProfile, setRateCardProfile] = React.useState<any>(null);
  const [isLoadingRateCard, setIsLoadingRateCard] = React.useState(true);
  const [isSavingRateCard, setIsSavingRateCard] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftRateCardConfig, setDraftRateCardConfig] = React.useState<any>(null);
  const [rateCardError, setRateCardError] = React.useState<string | null>(null);
  const [confirmingRateCardDefault, setConfirmingRateCardDefault] = React.useState(false);
  const [isEditingRateCard, setIsEditingRateCard] = React.useState(false);
  const [isCreatingRateCardRole, setIsCreatingRateCardRole] = React.useState(false);
  const [isCreatingRateCardProfile, setIsCreatingRateCardProfile] = React.useState(false);
  const [copyFromProfileId, setCopyFromProfileId] = React.useState<string>('none');

  // --- Resource Model State ---
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [resourceModelProfiles, setResourceModelProfiles] = React.useState<any[]>([]);
  const [selectedRMType, setSelectedRMType] = React.useState<
    'size_tiered' | 'flat_percentage' | 'ai'
  >('size_tiered');
  const [selectedRMProfileId, setSelectedRMProfileId] = React.useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [resourceModelProfile, setResourceModelProfile] = React.useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftResourceModelConfig, setDraftResourceModelConfig] = React.useState<any>(null);
  const [isLoadingResourceModel, setIsLoadingResourceModel] = React.useState(true);
  const [isSavingResourceModel, setIsSavingResourceModel] = React.useState(false);
  const [resourceModelError, setResourceModelError] = React.useState<string | null>(null);
  const [isEditingResourceModel, setIsEditingResourceModel] = React.useState(false);
  const [isCreatingResourceModel, setIsCreatingResourceModel] = React.useState(false);
  const [confirmingResourceModelDefault, setConfirmingResourceModelDefault] = React.useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const seedEmptyContract = (type: 'size_tiered' | 'flat_percentage' | 'ai'): any => {
    if (type === 'ai') {
      return {
        distribution_model: 'ai',
        schemaVersion: '1.0.0',
        name: 'AI-Driven',
        description:
          'AI distributes hours across the category roles per deal; the Project Manager is fixed at the calculator pm_percent. No static phases or weights.',
        display: { show_timeline: false, show_gantt: false },
      };
    }
    return type === 'flat_percentage'
      ? {
          distribution_model: 'flat_percentage',
          schemaVersion: '1.0.0',
          name: 'Flat Percentage',
          description: 'Hours the team delivers per week - drives the project timeline length.',
          phases: [{ key: 'delivery', label: 'Delivery', order: 0 }],
          display: { show_timeline: true, show_gantt: true },
          flat_velocity: 40,
          phase_percentages: { delivery: 1 },
          role_percentages: {},
          phase_weights: {},
        }
      : {
          distribution_model: 'size_tiered',
          schemaVersion: '1.0.0',
          name: 'Size-Tiered',
          description: 'Hours the team delivers per week - drives the project timeline length.',
          phases: [{ key: 'delivery', label: 'Delivery', order: 0 }],
          display: { show_timeline: true, show_gantt: true },
          project_size_thresholds: [{ size: 'standard', max_hours: null }],
          team_velocity: { standard: 40 },
          role_allocations: { standard: {} },
          phase_weights: {},
          phase_section_map: [{ phase_key: 'delivery', section_keys: [], subsections: [] }],
        };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pickRMProfile = (profiles: any[], type: string) => {
    // R2: drop system rows of this subtype when the workspace owns any; R1: then
    // pick the active/default (falling back to the first available).
    const ofType = filterByScope(
      profiles.filter((p) => p.profileSubtype === type),
      () => type
    );
    return pickActive(ofType) ?? null;
  };

  const loadResourceModelProfiles = React.useCallback(() => {
    let active = true;
    setIsLoadingResourceModel(true);
    setResourceModelError(null);
    apiFetch<{ profiles: any[] }>(
      `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/resource-plan-profiles`
    )
      .then((res) => {
        if (!active) return;
        setResourceModelProfiles(res.profiles);
        const prof = pickRMProfile(res.profiles, selectedRMType);
        setResourceModelProfile(prof);
        setSelectedRMProfileId(prof?.id ?? null);
        setDraftResourceModelConfig(
          prof?.config ? JSON.parse(JSON.stringify(prof.config)) : seedEmptyContract(selectedRMType)
        );
        setIsCreatingResourceModel(false);
        setIsEditingResourceModel(false);
      })
      .catch((err) => {
        if (active) setResourceModelError(err.message || 'Failed to load resource models.');
      })
      .finally(() => {
        if (active) setIsLoadingResourceModel(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_productUuid, selectedRMType]);

  React.useEffect(() => {
    return loadResourceModelProfiles();
  }, [loadResourceModelProfiles]);

  const rmRoleList = React.useMemo(() => {
    const rates = Array.isArray(draftRateCardConfig?.rates)
      ? draftRateCardConfig.rates
      : Array.isArray(rateCardProfile?.config?.rates)
        ? rateCardProfile.config.rates
        : [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return rates.map((r: any) => ({ id: r.role_id, label: r.role_name || r.role_id }));
  }, [draftRateCardConfig, rateCardProfile]);

  const rmVersionsOfType = filterByScope(
    resourceModelProfiles.filter((p) => p.profileSubtype === selectedRMType),
    () => selectedRMType
  );

  // Type-dropdown label comes from the contract's `name` (falling back to the
  // default model label) of any profile of that type.
  const rmTypeLabel = (type: 'size_tiered' | 'flat_percentage' | 'ai') => {
    const prof = resourceModelProfiles.find((p) => p.profileSubtype === type);
    return prof?.config?.name ?? DISTRIBUTION_MODEL_LABELS[type];
  };

  const hasRMChanges = React.useMemo(() => {
    if (!draftResourceModelConfig) return false;
    if (isCreatingResourceModel) {
      return (
        Array.isArray(draftResourceModelConfig.phases) && draftResourceModelConfig.phases.length > 0
      );
    }
    if (!resourceModelProfile?.config) return true;
    return JSON.stringify(draftResourceModelConfig) !== JSON.stringify(resourceModelProfile.config);
  }, [draftResourceModelConfig, resourceModelProfile, isCreatingResourceModel]);

  const handleRMChangeType = (type: 'size_tiered' | 'flat_percentage' | 'ai') => {
    if (isEditingResourceModel && hasRMChanges && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    setIsEditingResourceModel(false);
    setIsCreatingResourceModel(false);
    setConfirmingResourceModelDefault(false);
    setSelectedRMType(type);
    const prof = pickRMProfile(resourceModelProfiles, type);
    setResourceModelProfile(prof);
    setSelectedRMProfileId(prof?.id ?? null);
    setDraftResourceModelConfig(
      prof?.config ? JSON.parse(JSON.stringify(prof.config)) : seedEmptyContract(type)
    );
  };

  const handleRMChangeVersion = (id: number) => {
    const prof = resourceModelProfiles.find((p) => p.id === id) ?? null;
    setIsEditingResourceModel(false);
    setIsCreatingResourceModel(false);
    setConfirmingResourceModelDefault(false);
    setResourceModelProfile(prof);
    setSelectedRMProfileId(id);
    setDraftResourceModelConfig(
      prof?.config ? JSON.parse(JSON.stringify(prof.config)) : seedEmptyContract(selectedRMType)
    );
  };

  const handleRMNew = () => {
    const base =
      draftResourceModelConfig ?? resourceModelProfile?.config ?? seedEmptyContract(selectedRMType);
    setIsCreatingResourceModel(true);
    setIsEditingResourceModel(true);
    setConfirmingResourceModelDefault(false);
    setDraftResourceModelConfig(JSON.parse(JSON.stringify(base)));
  };

  const handleRMSave = async (asNew: boolean) => {
    if (!draftResourceModelConfig) return;
    setIsSavingResourceModel(true);
    setResourceModelError(null);
    try {
      if (asNew) {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/resource-plan-profiles`,
          {
            method: 'POST',
            body: JSON.stringify({ config: draftResourceModelConfig, status: 'draft' }),
          }
        );
      } else if (selectedRMProfileId != null) {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/resource-plan-profiles/${selectedRMProfileId}`,
          { method: 'PATCH', body: JSON.stringify({ config: draftResourceModelConfig }) }
        );
      }
      setIsEditingResourceModel(false);
      setIsCreatingResourceModel(false);
      loadResourceModelProfiles();
    } catch (err) {
      setResourceModelError(err instanceof Error ? err.message : 'Failed to save resource model.');
    } finally {
      setIsSavingResourceModel(false);
    }
  };

  const handleRMPublish = async () => {
    if (selectedRMProfileId == null) return;
    setIsSavingResourceModel(true);
    setResourceModelError(null);
    try {
      await apiFetch(
        `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/resource-plan-profiles/${selectedRMProfileId}`,
        { method: 'PATCH', body: JSON.stringify({ status: 'published' }) }
      );
      loadResourceModelProfiles();
    } catch (err) {
      setResourceModelError(err instanceof Error ? err.message : 'Failed to publish.');
    } finally {
      setIsSavingResourceModel(false);
    }
  };

  const handleRMSetDefault = async () => {
    if (selectedRMProfileId == null) return;
    setIsSavingResourceModel(true);
    setResourceModelError(null);
    try {
      await apiFetch(
        `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/resource-plan-profiles/${selectedRMProfileId}`,
        { method: 'PATCH', body: JSON.stringify({ makeDefault: true }) }
      );
      loadResourceModelProfiles();
    } catch (err) {
      setResourceModelError(err instanceof Error ? err.message : 'Failed to set default.');
    } finally {
      setIsSavingResourceModel(false);
      setConfirmingResourceModelDefault(false);
    }
  };

  const loadRateCardProfiles = React.useCallback(() => {
    let active = true;
    setIsLoadingRateCard(true);
    setRateCardError(null);
    apiFetch<{ profiles: any[] }>(
      `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/rate-card-profiles`
    )
      .then((res) => {
        if (!active) return;
        // R2: defensively hide system rows once a workspace rate card exists.
        const visible = filterByScope(res.profiles, () => 'rate_card');
        setRateCardProfiles(visible);
        const activeProfile = pickActive(visible);
        setRateCardProfile(activeProfile);
        // Deep copy the config to break the reference tie
        setDraftRateCardConfig(
          activeProfile?.config ? JSON.parse(JSON.stringify(activeProfile.config)) : null
        );
      })
      .catch((err) => {
        if (active) setRateCardError(err.message || 'Failed to load rate card profiles.');
      })
      .finally(() => {
        if (active) setIsLoadingRateCard(false);
      });
    return () => {
      active = false;
    };
  }, [_productUuid]);

  React.useEffect(() => {
    return loadRateCardProfiles();
  }, [loadRateCardProfiles]);

  const handleRateCardSave = async (asNewVersion = false) => {
    if (!draftRateCardConfig) return;
    setIsSavingRateCard(true);

    try {
      if (asNewVersion) {
        // Create new version
        await apiFetch<any>(
          `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/rate-card-profiles`,
          {
            method: 'POST',
            // Do not copy over the `isDefault` status from the original profile!
            body: JSON.stringify({ config: draftRateCardConfig, makeDefault: false }),
          }
        );
        // Refresh profiles to get the new list
        loadRateCardProfiles();
        setIsCreatingRateCardProfile(false);
        setIsEditingRateCard(false);
        setIsCreatingRateCardRole(false);
      } else {
        // Update existing workspace profile
        if (rateCardProfile.scope === 'system') {
          setRateCardError(
            'Cannot modify system profiles directly. Save as a new version instead.'
          );
          return;
        }

        const res = await apiFetch<any>(
          `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/rate-card-profiles/${rateCardProfile.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ config: draftRateCardConfig }),
          }
        );
        setRateCardProfile((prev: any) => ({
          ...prev,
          version: res.version,
          config: draftRateCardConfig,
        }));
      }
    } catch (err) {
      console.error('[ProductTemplate] save rate card failed', err);
      setRateCardError(err instanceof Error ? err.message : 'Failed to save rate card');
    } finally {
      setIsSavingRateCard(false);
    }
  };

  const handleRateCardSetDefault = async () => {
    if (!rateCardProfile || rateCardProfile.scope === 'system') return;
    setIsSavingRateCard(true);
    try {
      await apiFetch<any>(
        `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/rate-card-profiles/${rateCardProfile.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ makeDefault: true }),
        }
      );
      loadRateCardProfiles();
    } catch (err) {
      console.error('[ProductTemplate] set default rate card failed', err);
      setRateCardError(err instanceof Error ? err.message : 'Failed to set default rate card');
    } finally {
      setIsSavingRateCard(false);
      setConfirmingRateCardDefault(false);
    }
  };

  const handleRateCardPublish = async () => {
    if (!rateCardProfile || rateCardProfile.scope === 'system') return;
    setIsSavingRateCard(true);
    try {
      await apiFetch<any>(
        `/api/workspace/admin/products/${encodeURIComponent(_productUuid)}/rate-card-profiles/${rateCardProfile.id}`,
        { method: 'PATCH', body: JSON.stringify({ status: 'published' }) }
      );
      loadRateCardProfiles();
    } catch (err) {
      setRateCardError(err instanceof Error ? err.message : 'Failed to publish rate card');
    } finally {
      setIsSavingRateCard(false);
    }
  };

  const handleRateCardChangeProfile = (profileIdStr: string) => {
    const profile = rateCardProfiles.find((p) => p.id === parseInt(profileIdStr, 10));
    if (profile) {
      setRateCardProfile(profile);
      // Deep copy to break the reference tie
      setDraftRateCardConfig(profile.config ? JSON.parse(JSON.stringify(profile.config)) : null);
      setConfirmingRateCardDefault(false);
      setIsEditingRateCard(false);
      setIsCreatingRateCardRole(false);
    }
  };

  const handleRateChange = (roleId: string, type: 'onshore' | 'offshore', value: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => {
      const rates = Array.isArray(prev?.rates) ? prev.rates : [];
      return {
        ...prev,
        rates: rates.map((r: any) => (r.role_id === roleId ? { ...r, [type]: value } : r)),
      };
    });
  };

  const handleUpdateDescription = (description: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => ({
      ...prev,
      description,
    }));
  };

  const handleAddRole = (roleId: string, roleName: string, level: string, category: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => {
      const rates = Array.isArray(prev?.rates) ? prev.rates : [];
      return {
        ...prev,
        rates: [
          ...rates,
          { role_id: roleId, role_name: roleName, level, category, onshore: 0, offshore: 0 },
        ],
      };
    });
  };

  const handleUpdateRoleMeta = (
    roleId: string,
    newRoleName: string,
    level: string,
    category: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => {
      const rates = Array.isArray(prev?.rates) ? prev.rates : [];
      return {
        ...prev,
        rates: rates.map((r: any) =>
          r.role_id === roleId ? { ...r, role_name: newRoleName, level, category } : r
        ),
      };
    });
  };

  const handleDeleteRole = (roleId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => {
      const rates = Array.isArray(prev?.rates) ? prev.rates : [];
      return {
        ...prev,
        rates: rates.filter((r: any) => r.role_id !== roleId),
      };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleReorderRoles = (newRates: any[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDraftRateCardConfig((prev: any) => ({
      ...prev,
      rates: newRates,
    }));
  };

  React.useEffect(() => {
    let active = true;
    if (integrationEnabled && availableIntegrations.length === 0) {
      setIsFetchingIntegrations(true);
      apiFetch<{ integrations: any[] }>('/api/integration')
        .then((data) => {
          if (!active) return;
          const driveIntegrations = data.integrations.filter(
            (i: any) => i.definition.slug === 'google-drive' && i.lifecycleStatus === 'active'
          );
          setAvailableIntegrations(driveIntegrations);
          // Fall back to the first active credential when the currently selected one is
          // no longer among them - e.g. the account was reconnected, which soft-deletes
          // the old credential and mints a new uuid. Without this, a template saved
          // against a now-deleted credential keeps firing /folders with a dead uuid and
          // gets CREDENTIAL_NOT_FOUND (404).
          const selectionStillValid = driveIntegrations.some(
            (i: any) => i.uuid === googleDriveIntegrationUuidDraft
          );
          if (!selectionStillValid) {
            setGoogleDriveIntegrationUuidDraft(driveIntegrations[0]?.uuid ?? '');
          }
        })
        .finally(() => {
          if (active) {
            setIsFetchingIntegrations(false);
            setHasFetchedIntegrations(true);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [integrationEnabled, availableIntegrations.length, googleDriveIntegrationUuidDraft]);

  React.useEffect(() => {
    setIntegrationEnabled(!!config?.integration);
    setGoogleDriveIntegrationUuidDraft(config?.integration?.integrationUuid ?? '');
    setGoogleDriveFolderDraft(config?.integration?.folderId ?? '');
    setSubfolderDraft(config?.subfolder ?? '');
    setDiscountPercentage(config?.discountPercentage ?? 0);
    setDiscountEnabled(config?.discountPercentage != null && config.discountPercentage > 0);
    setOffshorePercentage(config?.offshorePercentage ?? 0);
    setOffshoreEnabled(config?.offshorePercentage != null);
  }, [config]);

  const configDirty = React.useMemo(() => {
    if (integrationEnabled !== !!config?.integration) return true;
    if (
      integrationEnabled &&
      googleDriveIntegrationUuidDraft !== (config?.integration?.integrationUuid ?? '')
    )
      return true;
    if (integrationEnabled && googleDriveFolderDraft !== (config?.integration?.folderId ?? ''))
      return true;
    // The sub-folder is editable in both modes (global on/off), so compare it unconditionally.
    if (subfolderDraft !== (config?.subfolder ?? '')) return true;

    const currentDiscountPercentage = discountEnabled ? discountPercentage : null;
    if (currentDiscountPercentage !== (config?.discountPercentage ?? null)) return true;

    const currentOffshorePercentage = offshoreEnabled ? offshorePercentage : null;
    if (currentOffshorePercentage !== (config?.offshorePercentage ?? null)) return true;

    return false;
  }, [
    config,
    integrationEnabled,
    googleDriveIntegrationUuidDraft,
    googleDriveFolderDraft,
    subfolderDraft,
    discountEnabled,
    discountPercentage,
    offshoreEnabled,
    offshorePercentage,
  ]);

  const hasRateCardChanges = React.useMemo(() => {
    if (!draftRateCardConfig) return false;

    // If we're creating a completely new profile, saving is enabled once we have data
    if (isCreatingRateCardProfile) {
      return draftRateCardConfig.rates && draftRateCardConfig.rates.length > 0;
    }

    if (!rateCardProfile?.config) return false;
    // Deep clone before stringify to safely strip undefined values or proxy wrappers if any,
    // though native JSON.stringify should handle plain objects.
    // Also, ensure we don't accidentally mutate the original profile config anywhere!
    return JSON.stringify(draftRateCardConfig) !== JSON.stringify(rateCardProfile.config);
  }, [draftRateCardConfig, rateCardProfile, isCreatingRateCardProfile]);

  const handleConfigSave = React.useCallback(async () => {
    if (configDirty) {
      const folderId = integrationEnabled ? googleDriveFolderDraft.trim() || null : null;
      const integrationUuid = integrationEnabled ? googleDriveIntegrationUuidDraft || null : null;

      const integration = integrationEnabled ? { folderId, integrationUuid } : null;

      onConfigSave({
        integration,
        subfolder: subfolderDraft.trim() || null,
        discountPercentage: discountEnabled ? discountPercentage : null,
        offshorePercentage: offshoreEnabled ? offshorePercentage : null,
      });
    }

    if (hasRateCardChanges) {
      await handleRateCardSave();
    }
  }, [
    configDirty,
    hasRateCardChanges,
    integrationEnabled,
    googleDriveFolderDraft,
    googleDriveIntegrationUuidDraft,
    subfolderDraft,
    onConfigSave,
    handleRateCardSave,
    discountEnabled,
    discountPercentage,
    offshoreEnabled,
    offshorePercentage,
  ]);

  return (
    <div className="pcat-artifact-wrap">
      <div className="ptp-est-split">
        {/* ── Left nav ──────────────────────────────────────────────────────── */}
        <div className="ptp-est-nav" style={{ width: `${navWidth}px` }}>
          <div className="ptp-est-nav__list">
            <button
              className={`ptp-est-nav__item${activePane === 'configuration' ? ' ptp-est-nav__item--active' : ''}`}
              onClick={() => setActivePane('configuration')}
            >
              Configuration
            </button>
            <button
              className={`ptp-est-nav__item${activePane === 'rate-card' ? ' ptp-est-nav__item--active' : ''}`}
              onClick={() => setActivePane('rate-card')}
            >
              Rate Card
            </button>
            <button
              className={`ptp-est-nav__item${activePane === 'resource-model' ? ' ptp-est-nav__item--active' : ''}`}
              onClick={() => setActivePane('resource-model')}
            >
              Resource Model
            </button>
          </div>
        </div>

        <ResizableDivider
          direction="vertical"
          onResize={(delta) => setNavWidth((w) => Math.max(140, Math.min(360, w + delta)))}
          ariaLabel="Resize navigation"
        />

        {/* ── Right detail panel ───────────────────────────────────────────── */}
        <div className="ptp-est-detail">
          {activePane === 'configuration' ? (
            <div className="ptp-est-config-pane">
              <div className="ptp-est-config-pane__header">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={(!configDirty && !hasRateCardChanges) || isSaving || isSavingRateCard}
                  onClick={() => void handleConfigSave()}
                >
                  {isSaving || isSavingRateCard ? 'Saving…' : 'Save'}
                </Button>
              </div>
              <div className="ptp-est-range-config">
                {config && (
                  <div className="ptp-est-range-config__section">
                    <div className="ptp-est-range-config__section-header">
                      <span className="ptp-est-range-config__section-title">
                        DEFAULT DISCOUNT RATE
                      </span>
                      <label
                        className="ptp-est-range-config__pill-toggle"
                        aria-label="Enable discount"
                      >
                        <input
                          type="checkbox"
                          checked={discountEnabled}
                          onChange={(e) => handleToggleDiscount(e.target.checked)}
                        />
                        <span className="ptp-est-range-config__pill" aria-hidden="true" />
                      </label>
                    </div>
                    <p className="ptp-est-range-config__description">
                      Configure the default discount rate for the resource plan.
                    </p>
                    {discountEnabled && (
                      <div
                        className="ptp-est-range-config__block-fields"
                        style={{ marginTop: 'var(--space-4)' }}
                      >
                        <div className="ptp-est-range-config__block-field">
                          <label className="ptp-est-range-config__block-label">
                            Discount Percentage (%)
                          </label>
                          <p className="ptp-est-range-config__description" style={{ margin: 0 }}>
                            (e.g., 10 for 10% discount)
                          </p>
                          <div>
                            <NumberInput
                              min={0}
                              max={100}
                              step={1}
                              style={{ width: '120px' }}
                              value={discountPercentage}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setDiscountPercentage(
                                  isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {config && (
                  <div className="ptp-est-range-config__section">
                    <div className="ptp-est-range-config__section-header">
                      <span className="ptp-est-range-config__section-title">
                        DEFAULT OFFSHORE RATE
                      </span>
                      <label
                        className="ptp-est-range-config__pill-toggle"
                        aria-label="Enable default offshore rate"
                      >
                        <input
                          type="checkbox"
                          checked={offshoreEnabled}
                          onChange={(e) => handleToggleOffshore(e.target.checked)}
                        />
                        <span className="ptp-est-range-config__pill" aria-hidden="true" />
                      </label>
                    </div>
                    <p className="ptp-est-range-config__description">
                      Default offshore percentage applied to a role when it is switched to the
                      Hybrid pricing basis.
                    </p>
                    {offshoreEnabled && (
                      <div
                        className="ptp-est-range-config__block-fields"
                        style={{ marginTop: 'var(--space-4)' }}
                      >
                        <div className="ptp-est-range-config__block-field">
                          <label className="ptp-est-range-config__block-label">
                            Offshore Percentage (%)
                          </label>
                          <p className="ptp-est-range-config__description" style={{ margin: 0 }}>
                            (e.g., 30 for 30% delivered offshore)
                          </p>
                          <div>
                            <NumberInput
                              min={0}
                              max={100}
                              step={1}
                              style={{ width: '120px' }}
                              value={offshorePercentage}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setOffshorePercentage(
                                  isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="ptp-est-range-config__section">
                  <div className="ptp-est-range-config__section-header">
                    <span className="ptp-est-range-config__section-title">INTEGRATIONS</span>
                    <label
                      className="ptp-est-range-config__pill-toggle"
                      aria-label="Enable integrations"
                    >
                      <input
                        type="checkbox"
                        checked={integrationEnabled}
                        disabled={globalFolderOverrideActive}
                        onChange={(e) => setIntegrationEnabled(e.target.checked)}
                      />
                      <span className="ptp-est-range-config__pill" aria-hidden="true" />
                    </label>
                  </div>
                  <p className="ptp-est-range-config__description">
                    Configure export directories for third-party integrations.
                  </p>
                  {globalFolderOverrideActive && (
                    <div className="ptp-est-range-config__block-fields">
                      <GlobalExportOverrideNotice />
                      <ExportSubfolderField
                        value={subfolderDraft}
                        onChange={setSubfolderDraft}
                        parentHint="each customer's folder"
                      />
                    </div>
                  )}
                  {!globalFolderOverrideActive && integrationEnabled && (
                    <div className="ptp-est-range-config__block-fields">
                      <div className="ptp-est-range-config__block-field">
                        <label className="ptp-est-range-config__block-label">
                          Storage Integration
                        </label>
                        <Select
                          value={googleDriveIntegrationUuidDraft}
                          onValueChange={setGoogleDriveIntegrationUuidDraft}
                          size="comfortable"
                          disabled={isFetchingIntegrations}
                          loading={isFetchingIntegrations}
                        >
                          {availableIntegrations.map((i) => (
                            <SelectItem key={i.uuid} value={i.uuid}>
                              {i.displayName}
                            </SelectItem>
                          ))}
                        </Select>
                        {(() => {
                          const sel = availableIntegrations.find(
                            (i) => i.uuid === googleDriveIntegrationUuidDraft
                          );
                          return sel?.description ? (
                            <span className="ptp-est-range-config__block-hint">
                              Connected as {sel.description}. If you switch the connected Google
                              account, re-pick the target folder below.
                            </span>
                          ) : null;
                        })()}
                        {hasFetchedIntegrations &&
                          availableIntegrations.length === 0 &&
                          !isFetchingIntegrations && (
                            <span className="ptp-est-range-config__block-error">
                              No active Google Drive integrations found. Please connect one in the
                              Integrations page.
                            </span>
                          )}
                      </div>
                      {googleDriveIntegrationUuidDraft && (
                        <div className="ptp-est-range-config__block-field">
                          <DriveFolderField
                            integrationUuid={googleDriveIntegrationUuidDraft}
                            value={googleDriveFolderDraft}
                            onChange={setGoogleDriveFolderDraft}
                            onSelectedFolderNameChange={setGoogleDriveFolderName}
                            label="Target Folder"
                            hint="When a user exports a resource plan, it will be saved in this folder."
                          />
                          <ExportSubfolderField
                            value={subfolderDraft}
                            onChange={setSubfolderDraft}
                            parentHint={googleDriveFolderName || 'the selected folder'}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : activePane === 'resource-model' ? (
            <div className="ptp-est-config-pane">
              <div className="ptp-toolbar">
                <div className="ptp-toolbar__left">
                  <Select
                    value={selectedRMType}
                    onValueChange={(v) =>
                      handleRMChangeType(v as 'size_tiered' | 'flat_percentage' | 'ai')
                    }
                    size="sm"
                  >
                    {(['flat_percentage', 'size_tiered', 'ai'] as const).map((t) => (
                      <SelectItem key={t} value={t}>
                        {rmTypeLabel(t)}
                      </SelectItem>
                    ))}
                  </Select>

                  {!isCreatingResourceModel && rmVersionsOfType.length > 0 && (
                    <Select
                      value={selectedRMProfileId != null ? String(selectedRMProfileId) : ''}
                      onValueChange={(v) => handleRMChangeVersion(Number(v))}
                      size="sm"
                    >
                      {rmVersionsOfType.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {formatVersionLabel(p)}
                        </SelectItem>
                      ))}
                    </Select>
                  )}

                  {isCreatingResourceModel ? (
                    <span className="ptp-badge ptp-badge--status-draft">New draft</span>
                  ) : resourceModelProfile ? (
                    <>
                      {resourceModelProfile.isDefault && (
                        <span className="ptp-badge ptp-badge--default">Default</span>
                      )}
                      {resourceModelProfile.scope === 'system' && (
                        <span className="ptp-badge ptp-badge--scope-system">System</span>
                      )}
                    </>
                  ) : null}
                </div>

                <div className="ptp-toolbar__right">
                  {!isEditingResourceModel &&
                    resourceModelProfile &&
                    resourceModelProfile.scope === 'workspace' &&
                    resourceModelProfile.status === 'published' &&
                    !resourceModelProfile.isDefault &&
                    (confirmingResourceModelDefault ? (
                      <div className="da-detail__header-actions">
                        <span className="ptp-toolbar__confirm-label">Set as default?</span>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => void handleRMSetDefault()}
                          disabled={isSavingResourceModel}
                        >
                          Yes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingResourceModelDefault(false)}
                          disabled={isSavingResourceModel}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <ComingSoon>
                        <Button variant="primary" size="sm" disabled>
                          Set as default
                        </Button>
                      </ComingSoon>
                    ))}

                  <div className="da-detail__header-actions">
                    {!isEditingResourceModel ? (
                      <>
                        <ComingSoon>
                          <button
                            type="button"
                            className="da-detail__edit-btn"
                            disabled
                            onClick={handleRMNew}
                          >
                            Create
                          </button>
                        </ComingSoon>
                        {resourceModelProfile &&
                          resourceModelProfile.scope === 'workspace' &&
                          resourceModelProfile.status === 'draft' && (
                            <ComingSoon>
                              <button
                                type="button"
                                className="da-detail__edit-btn"
                                disabled
                                onClick={() => void handleRMPublish()}
                              >
                                Publish
                              </button>
                            </ComingSoon>
                          )}
                        <ComingSoon>
                          <button
                            type="button"
                            className="da-detail__edit-btn"
                            disabled
                            onClick={() => setIsEditingResourceModel(true)}
                          >
                            Edit
                          </button>
                        </ComingSoon>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="da-detail__edit-btn da-detail__edit-btn--cancel"
                          disabled={isSavingResourceModel}
                          onClick={() => {
                            setIsEditingResourceModel(false);
                            setIsCreatingResourceModel(false);
                            setDraftResourceModelConfig(
                              resourceModelProfile?.config
                                ? JSON.parse(JSON.stringify(resourceModelProfile.config))
                                : seedEmptyContract(selectedRMType)
                            );
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="da-detail__edit-btn da-detail__edit-btn--done"
                          disabled={!hasRMChanges || isSavingResourceModel}
                          onClick={() =>
                            void handleRMSave(
                              isCreatingResourceModel ||
                                resourceModelProfile?.scope === 'system' ||
                                selectedRMProfileId == null
                            )
                          }
                        >
                          {isSavingResourceModel ? 'Saving…' : 'Save'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {isLoadingResourceModel ? (
                <div className="rmp" aria-busy="true">
                  <div className="rmp-skeleton-tabs">
                    {[0, 1, 2, 3].map((i) => (
                      <SkeletonBlock key={i} width="5rem" height="1.25rem" />
                    ))}
                  </div>
                  <div className="rmp-settings" style={{ padding: 'var(--space-4)' }}>
                    {[0, 1, 2].map((i) => (
                      <SkeletonBlock key={i} height="6rem" radius="var(--radius-md)" />
                    ))}
                  </div>
                </div>
              ) : resourceModelError ? (
                <div className="ptp-est-range-config">
                  <PageErrorState
                    message={resourceModelError}
                    onRetry={loadResourceModelProfiles}
                  />
                </div>
              ) : (
                <ResourceModelPane
                  draftConfig={draftResourceModelConfig ?? seedEmptyContract(selectedRMType)}
                  isEditing={isEditingResourceModel}
                  roleList={rmRoleList}
                  onChange={setDraftResourceModelConfig}
                />
              )}
            </div>
          ) : activePane === 'rate-card' ? (
            <div className="ptp-est-config-pane">
              <div className="ptp-toolbar">
                <div className="ptp-toolbar__left">
                  {isCreatingRateCardProfile ? (
                    <div className="da-detail__header-actions">
                      <div style={{ width: '220px' }}>
                        <Select
                          value={copyFromProfileId}
                          onValueChange={setCopyFromProfileId}
                          size="sm"
                        >
                          <SelectItem value="none">Empty (Start from scratch)</SelectItem>
                          {rateCardProfiles.map((p) => (
                            <SelectItem key={p.id} value={p.id.toString()}>
                              {formatVersionLabel(p)}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                      <button
                        type="button"
                        className="da-detail__edit-btn da-detail__edit-btn--cancel"
                        disabled={copyFromProfileId === 'none'}
                        onClick={() => {
                          const p = rateCardProfiles.find(
                            (x) => x.id.toString() === copyFromProfileId
                          );
                          if (p) {
                            const clonedConfig = p.config
                              ? JSON.parse(JSON.stringify(p.config))
                              : { rates: [] };
                            setDraftRateCardConfig(clonedConfig);
                          }
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  ) : (
                    rateCardProfile && (
                      <>
                        <Select
                          value={rateCardProfile.id.toString()}
                          onValueChange={handleRateCardChangeProfile}
                          size="sm"
                        >
                          {rateCardProfiles.map((p) => (
                            <SelectItem key={p.id} value={p.id.toString()}>
                              {formatVersionLabel(p)}
                            </SelectItem>
                          ))}
                        </Select>

                        {rateCardProfile.isDefault && (
                          <span className="ptp-badge ptp-badge--default">Default</span>
                        )}
                        {rateCardProfile.scope === 'system' && (
                          <span className="ptp-badge ptp-badge--scope-system">System</span>
                        )}
                      </>
                    )
                  )}
                </div>

                <div className="ptp-toolbar__right">
                  {rateCardProfile &&
                    !rateCardProfile.isDefault &&
                    rateCardProfile.scope === 'workspace' &&
                    rateCardProfile.status === 'published' &&
                    !isEditingRateCard &&
                    (confirmingRateCardDefault ? (
                      <div className="da-detail__header-actions">
                        <span className="ptp-toolbar__confirm-label">Set as default?</span>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => void handleRateCardSetDefault()}
                          disabled={isSavingRateCard}
                        >
                          Yes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingRateCardDefault(false)}
                          disabled={isSavingRateCard}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <ComingSoon>
                        <Button variant="primary" size="sm" disabled>
                          Set as default
                        </Button>
                      </ComingSoon>
                    ))}

                  {rateCardProfile && (
                    <div className="da-detail__header-actions">
                      {!isEditingRateCard ? (
                        <>
                          <ComingSoon>
                            <button type="button" className="da-detail__edit-btn" disabled>
                              Create
                            </button>
                          </ComingSoon>
                          {rateCardProfile.scope === 'workspace' &&
                            rateCardProfile.status === 'draft' && (
                              <ComingSoon>
                                <button
                                  type="button"
                                  className="da-detail__edit-btn"
                                  disabled
                                  onClick={() => void handleRateCardPublish()}
                                >
                                  Publish
                                </button>
                              </ComingSoon>
                            )}
                          <ComingSoon>
                            <button
                              type="button"
                              className="da-detail__edit-btn"
                              disabled
                              onClick={() => setIsEditingRateCard(true)}
                            >
                              Edit
                            </button>
                          </ComingSoon>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="da-detail__edit-btn da-detail__edit-btn--cancel"
                            onClick={() => {
                              setIsCreatingRateCardProfile(false);
                              setIsEditingRateCard(false);
                              setIsCreatingRateCardRole(false);
                              setDraftRateCardConfig(
                                rateCardProfile.config
                                  ? JSON.parse(JSON.stringify(rateCardProfile.config))
                                  : null
                              );
                            }}
                            disabled={isSavingRateCard}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="da-detail__edit-btn da-detail__edit-btn--done"
                            disabled={!hasRateCardChanges || isSavingRateCard}
                            onClick={() =>
                              void handleRateCardSave(
                                isCreatingRateCardProfile || rateCardProfile.scope === 'system'
                              )
                            }
                          >
                            {isSavingRateCard ? 'Saving…' : 'Save'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isLoadingRateCard ? (
                <div className="ptp-est-config-pane" aria-busy="true">
                  <div className="da-detail">
                    <div className="da-option-list mx-6 mb-6 mt-6">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div className="da-option-row" key={i}>
                          <div className="da-option-row__label-group">
                            <SkeletonBlock width="9rem" height="1rem" />
                          </div>
                          <div className="da-option-row__rates">
                            <SkeletonBlock width="4.5rem" height="1rem" />
                            <SkeletonBlock width="4.5rem" height="1rem" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : rateCardError || !draftRateCardConfig ? (
                <div className="ptp-est-range-config">
                  <PageErrorState
                    message={rateCardError || 'Failed to load rate card.'}
                    onRetry={loadRateCardProfiles}
                  />
                </div>
              ) : (
                <RateCardPane
                  draftConfig={draftRateCardConfig}
                  handleRateChange={handleRateChange}
                  handleUpdateDescription={handleUpdateDescription}
                  handleAddRole={handleAddRole}
                  handleUpdateRoleMeta={handleUpdateRoleMeta}
                  handleDeleteRole={handleDeleteRole}
                  handleReorderRoles={handleReorderRoles}
                  isEditing={isEditingRateCard}
                  isCreatingRole={isCreatingRateCardRole}
                  onCancelCreateRole={() => setIsCreatingRateCardRole(false)}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EstimateOverviewSection({
  productUuid: _productUuid,
  onSigClick: _onSigClick,
  onCfgClick: _onCfgClick,
  onRuleClick,
  selectedRuleKey,
  selectedRulesetId,
  onRulesetChange,
  onCategoryChange,
  onSetDefault,
  onPublish,
  onCreateFromSource,
  categoryOptions,
  data,
  error,
  collapsed,
  onToggleCollapsed,
  rangeConfig,
  onRangeConfigSave,
  isSavingRangeConfig,
  onActivePaneChange,
  globalFolderOverrideActive,
  scopedCategoryKey,
}: {
  productUuid: string;
  onSigClick: (key: string, signals: Record<string, SignalInfo>) => void;
  onCfgClick: (
    key: string,
    signals: Record<string, SignalInfo>,
    contextSourceKeys: string[]
  ) => void;
  onRuleClick: (rule: EstimateRuleInfo, signals: Record<string, SignalInfo>) => void;
  selectedRuleKey?: string | null;
  selectedRulesetId: number | null;
  onRulesetChange: (id: number) => void;
  onCategoryChange: (id: number, key: string | null) => void;
  onSetDefault: (id: number) => void;
  onPublish: (id: number) => void;
  onCreateFromSource: (sourceId: number) => void;
  categoryOptions: CategoryOption[];
  data: EstimateOverviewData | null;
  error: string | null;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  rangeConfig: EstimateRangeConfig;
  onRangeConfigSave: (config: EstimateRangeConfig) => void;
  isSavingRangeConfig: boolean;
  onActivePaneChange?: (pane: 'configuration' | 'template') => void;
  globalFolderOverrideActive: boolean;
  /**
   * When set, only rulesets for this deal category (with fallback to the shared
   * category-agnostic ones) are offered, and the category re-assignment dropdown
   * is replaced by a read-only Scoped/Shared badge. The parent keeps the selected
   * ruleset aligned to this category.
   */
  scopedCategoryKey?: string | null;
}) {
  const [activePane, setActivePane] = React.useState<'configuration' | 'template'>('configuration');

  const handleActivePaneChange = React.useCallback(
    (pane: 'configuration' | 'template') => {
      setActivePane(pane);
      onActivePaneChange?.(pane);
    },
    [onActivePaneChange]
  );
  const [navWidth, setNavWidth] = React.useState(240);
  const [confirmingDefault, setConfirmingDefault] = React.useState(false);

  // Range config draft state
  const [rangeEnabled, setRangeEnabled] = React.useState(() => rangeConfig.upperPct !== null);
  const [upperDraft, setUpperDraft] = React.useState(() => String(rangeConfig.upperPct ?? 15));
  const [lowerDraft, setLowerDraft] = React.useState(() => String(rangeConfig.lowerPct ?? 15));
  const [disclaimerEnabled, setDisclaimerEnabled] = React.useState(
    () => rangeConfig.disclaimerText !== null
  );
  const [disclaimerDraft, setDisclaimerDraft] = React.useState(
    () => rangeConfig.disclaimerText ?? ''
  );

  const [integrationEnabled, setIntegrationEnabled] = React.useState(
    () => !!rangeConfig.integration
  );
  const [googleDriveIntegrationUuidDraft, setGoogleDriveIntegrationUuidDraft] = React.useState(
    () => rangeConfig.integration?.integrationUuid ?? ''
  );
  const [googleDriveFolderDraft, setGoogleDriveFolderDraft] = React.useState(
    () => rangeConfig.integration?.folderId ?? ''
  );
  const [subfolderDraft, setSubfolderDraft] = React.useState(() => rangeConfig.subfolder ?? '');
  const [availableIntegrations, setAvailableIntegrations] = React.useState<any[]>([]);
  const [isFetchingIntegrations, setIsFetchingIntegrations] = React.useState(false);
  const [hasFetchedIntegrations, setHasFetchedIntegrations] = React.useState(false);
  // Resolved name of the selected target folder (surfaced by DriveFolderField) for the
  // subfolder hint below.
  const [googleDriveFolderName, setGoogleDriveFolderName] = React.useState('');

  // Sync drafts when rangeConfig changes (e.g. after save or ruleset switch)
  React.useEffect(() => {
    setRangeEnabled(rangeConfig.upperPct !== null);
    setUpperDraft(String(rangeConfig.upperPct ?? 15));
    setLowerDraft(String(rangeConfig.lowerPct ?? 15));
    setDisclaimerEnabled(rangeConfig.disclaimerText !== null);
    setDisclaimerDraft(rangeConfig.disclaimerText ?? '');
    setIntegrationEnabled(!!rangeConfig.integration);
    setGoogleDriveIntegrationUuidDraft(rangeConfig.integration?.integrationUuid ?? '');
    setGoogleDriveFolderDraft(rangeConfig.integration?.folderId ?? '');
    setSubfolderDraft(rangeConfig.subfolder ?? '');
  }, [rangeConfig]);

  React.useEffect(() => {
    let active = true;
    if (integrationEnabled && availableIntegrations.length === 0) {
      setIsFetchingIntegrations(true);
      apiFetch<{ integrations: any[] }>('/api/integration')
        .then((data) => {
          if (!active) return;
          const driveIntegrations = data.integrations.filter(
            (i: any) => i.definition.slug === 'google-drive' && i.lifecycleStatus === 'active'
          );
          setAvailableIntegrations(driveIntegrations);
          // Fall back to the first active credential when the currently selected one is
          // no longer among them - e.g. the account was reconnected, which soft-deletes
          // the old credential and mints a new uuid. Without this, a template saved
          // against a now-deleted credential keeps firing /folders with a dead uuid and
          // gets CREDENTIAL_NOT_FOUND (404).
          const selectionStillValid = driveIntegrations.some(
            (i: any) => i.uuid === googleDriveIntegrationUuidDraft
          );
          if (!selectionStillValid) {
            setGoogleDriveIntegrationUuidDraft(driveIntegrations[0]?.uuid ?? '');
          }
        })
        .finally(() => {
          if (active) {
            setIsFetchingIntegrations(false);
            setHasFetchedIntegrations(true);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [integrationEnabled, availableIntegrations.length, googleDriveIntegrationUuidDraft]);

  // Group rulesets by rulesetKey (family); R2: defensively hide system rulesets
  // of a category once the workspace owns one.
  const rulesetGroups = React.useMemo(() => {
    const all = data?.rulesets ?? [];
    // When scoped to a category, offer only that category's rulesets, falling
    // back to the shared (category-agnostic, null) ones if it has none of its own.
    let pool = all;
    if (scopedCategoryKey != null) {
      const exact = all.filter((r) => r.categoryKey === scopedCategoryKey);
      pool = exact.length > 0 ? exact : all.filter((r) => r.categoryKey == null);
    }
    const map = new Map<string, EstimateRulesetSummary[]>();
    for (const r of filterByScope(pool, (x) => x.categoryKey ?? '')) {
      if (!map.has(r.rulesetKey)) map.set(r.rulesetKey, []);
      map.get(r.rulesetKey)!.push(r);
    }
    return map;
  }, [data?.rulesets, scopedCategoryKey]);

  const selected = React.useMemo(
    () => data?.rulesets.find((r) => r.id === selectedRulesetId) ?? data?.rulesets[0] ?? null,
    [data, selectedRulesetId]
  );

  // Display name for a family: use the first entry's displayName stripped of any version suffix
  const familyLabel = React.useCallback(
    (key: string): string => {
      const group = rulesetGroups.get(key) ?? [];
      const rep = group[0];
      return rep ? stripVersionSuffix(rep.displayName) : key;
    },
    [rulesetGroups]
  );

  const handleFamilyChange = React.useCallback(
    (key: string) => {
      const group = rulesetGroups.get(key) ?? [];
      const preferred = [...group].sort((a, b) => b.id - a.id)[0];
      if (preferred) onRulesetChange(preferred.id);
    },
    [rulesetGroups, onRulesetChange]
  );

  const handleVersionChange = React.useCallback(
    (idStr: string) => {
      onRulesetChange(parseInt(idStr, 10));
    },
    [onRulesetChange]
  );

  const familyRulesets = selected ? (rulesetGroups.get(selected.rulesetKey) ?? [selected]) : [];

  const rangeDirty = React.useMemo(() => {
    if (rangeEnabled !== (rangeConfig.upperPct !== null)) return true;
    if (disclaimerEnabled !== (rangeConfig.disclaimerText !== null)) return true;
    if (
      rangeEnabled &&
      (String(rangeConfig.upperPct ?? 15) !== upperDraft ||
        String(rangeConfig.lowerPct ?? 15) !== lowerDraft)
    )
      return true;
    if (disclaimerEnabled && disclaimerDraft !== (rangeConfig.disclaimerText ?? '')) return true;

    if (integrationEnabled !== !!rangeConfig.integration) return true;
    if (
      integrationEnabled &&
      googleDriveIntegrationUuidDraft !== (rangeConfig.integration?.integrationUuid ?? '')
    )
      return true;
    if (integrationEnabled && googleDriveFolderDraft !== (rangeConfig.integration?.folderId ?? ''))
      return true;
    // The sub-folder is editable in both modes (global on/off), so compare it unconditionally.
    if (subfolderDraft !== (rangeConfig.subfolder ?? '')) return true;

    return false;
  }, [
    rangeEnabled,
    upperDraft,
    lowerDraft,
    disclaimerEnabled,
    disclaimerDraft,
    integrationEnabled,
    googleDriveIntegrationUuidDraft,
    googleDriveFolderDraft,
    subfolderDraft,
    rangeConfig,
  ]);

  const handleRangeSave = React.useCallback(() => {
    const disclaimer = disclaimerEnabled ? disclaimerDraft.trim() || null : null;
    const folderId = integrationEnabled ? googleDriveFolderDraft.trim() || null : null;
    const integrationUuid = integrationEnabled ? googleDriveIntegrationUuidDraft || null : null;
    const integration = integrationEnabled ? { folderId, integrationUuid } : null;
    const subfolder = subfolderDraft.trim() || null;

    if (!rangeEnabled) {
      onRangeConfigSave({
        upperPct: null,
        lowerPct: null,
        disclaimerText: disclaimer,
        integration,
        subfolder,
      });
      return;
    }
    const upper = parseInt(upperDraft, 10);
    const lower = parseInt(lowerDraft, 10);
    if (
      !isNaN(upper) &&
      !isNaN(lower) &&
      upper >= 0 &&
      upper <= 100 &&
      lower >= 0 &&
      lower <= 100
    ) {
      onRangeConfigSave({
        upperPct: upper,
        lowerPct: lower,
        disclaimerText: disclaimer,
        integration,
        subfolder,
      });
    }
  }, [
    rangeEnabled,
    upperDraft,
    lowerDraft,
    disclaimerEnabled,
    disclaimerDraft,
    integrationEnabled,
    googleDriveIntegrationUuidDraft,
    googleDriveFolderDraft,
    subfolderDraft,
    onRangeConfigSave,
  ]);

  return (
    <div className="ptp-est-split">
      {/* ── Left nav ──────────────────────────────────────────────────────── */}
      <div className="ptp-est-nav" style={{ width: `${navWidth}px` }}>
        <div className="ptp-est-nav__list">
          <button
            className={`ptp-est-nav__item${activePane === 'configuration' ? ' ptp-est-nav__item--active' : ''}`}
            onClick={() => handleActivePaneChange('configuration')}
          >
            Configuration
          </button>
          <button
            className={`ptp-est-nav__item${activePane === 'template' ? ' ptp-est-nav__item--active' : ''}`}
            onClick={() => handleActivePaneChange('template')}
          >
            Template
          </button>
        </div>
      </div>

      <ResizableDivider
        direction="vertical"
        onResize={(delta) => setNavWidth((w) => Math.max(140, Math.min(360, w + delta)))}
        ariaLabel="Resize navigation"
      />

      {/* ── Right detail panel ───────────────────────────────────────────── */}
      <div className="ptp-est-detail">
        {activePane === 'configuration' ? (
          /* ── Configuration pane ── */
          <div className="ptp-est-config-pane">
            <div className="ptp-est-config-pane__header">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!rangeDirty || isSavingRangeConfig}
                onClick={handleRangeSave}
              >
                {isSavingRangeConfig ? 'Saving…' : 'Save'}
              </Button>
            </div>
            <div className="ptp-est-range-config">
              <div className="ptp-est-range-config__section">
                <div className="ptp-est-range-config__section-header">
                  <span className="ptp-est-range-config__section-title">Estimate Range</span>
                  <label
                    className="ptp-est-range-config__pill-toggle"
                    aria-label="Enable estimate range"
                  >
                    <input
                      type="checkbox"
                      checked={rangeEnabled}
                      onChange={(e) => setRangeEnabled(e.target.checked)}
                    />
                    <span className="ptp-est-range-config__pill" aria-hidden="true" />
                  </label>
                </div>
                <p className="ptp-est-range-config__description">
                  Show upper and lower percentage variance bands on estimate totals.
                </p>
                {rangeEnabled && (
                  <div className="ptp-est-range-config__fields">
                    <div className="ptp-est-range-config__field">
                      <span className="ptp-est-range-config__field-label">Upper %</span>
                      <NumberInput
                        thousandSeparator={false}
                        min={0}
                        max={100}
                        step={1}
                        value={upperDraft}
                        onChange={(e) => setUpperDraft(e.target.value)}
                        className="ptp-est-range-config__input"
                      />
                    </div>
                    <div className="ptp-est-range-config__field">
                      <span className="ptp-est-range-config__field-label">Lower %</span>
                      <NumberInput
                        thousandSeparator={false}
                        min={0}
                        max={100}
                        step={1}
                        value={lowerDraft}
                        onChange={(e) => setLowerDraft(e.target.value)}
                        className="ptp-est-range-config__input"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="ptp-est-range-config__section">
                <div className="ptp-est-range-config__section-header">
                  <span className="ptp-est-range-config__section-title">Estimate Disclaimer</span>
                  <label
                    className="ptp-est-range-config__pill-toggle"
                    aria-label="Enable estimate disclaimer"
                  >
                    <input
                      type="checkbox"
                      checked={disclaimerEnabled}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setDisclaimerEnabled(next);
                        if (next && !disclaimerDraft.trim()) {
                          setDisclaimerDraft(
                            'All estimates are in hours. Estimates are indicative and subject to final scoping.'
                          );
                        }
                      }}
                    />
                    <span className="ptp-est-range-config__pill" aria-hidden="true" />
                  </label>
                </div>
                <p className="ptp-est-range-config__description">
                  Custom warning text printed on the estimate document.
                </p>
                {disclaimerEnabled && (
                  <textarea
                    className="ptp-est-range-config__disclaimer-input"
                    rows={3}
                    value={disclaimerDraft}
                    onChange={(e) => setDisclaimerDraft(e.target.value)}
                  />
                )}
              </div>

              <div className="ptp-est-range-config__section">
                <div className="ptp-est-range-config__section-header">
                  <span className="ptp-est-range-config__section-title">INTEGRATIONS</span>
                  <label
                    className="ptp-est-range-config__pill-toggle"
                    aria-label="Enable integrations"
                  >
                    <input
                      type="checkbox"
                      checked={integrationEnabled}
                      disabled={globalFolderOverrideActive}
                      onChange={(e) => setIntegrationEnabled(e.target.checked)}
                    />
                    <span className="ptp-est-range-config__pill" aria-hidden="true" />
                  </label>
                </div>
                <p className="ptp-est-range-config__description">
                  Configure export directories for third-party integrations.
                </p>
                {globalFolderOverrideActive && (
                  <div className="ptp-est-range-config__block-fields">
                    <GlobalExportOverrideNotice />
                    <ExportSubfolderField
                      value={subfolderDraft}
                      onChange={setSubfolderDraft}
                      parentHint="each customer's folder"
                    />
                  </div>
                )}
                {!globalFolderOverrideActive && integrationEnabled && (
                  <div className="ptp-est-range-config__block-fields">
                    <div className="ptp-est-range-config__block-field">
                      <label className="ptp-est-range-config__block-label">
                        Storage Integration
                      </label>
                      <Select
                        value={googleDriveIntegrationUuidDraft}
                        onValueChange={setGoogleDriveIntegrationUuidDraft}
                        size="comfortable"
                        disabled={isFetchingIntegrations}
                        loading={isFetchingIntegrations}
                      >
                        {availableIntegrations.map((i) => (
                          <SelectItem key={i.uuid} value={i.uuid}>
                            {i.displayName}
                          </SelectItem>
                        ))}
                      </Select>
                      {(() => {
                        const sel = availableIntegrations.find(
                          (i) => i.uuid === googleDriveIntegrationUuidDraft
                        );
                        return sel?.description ? (
                          <span className="ptp-est-range-config__block-hint">
                            Connected as {sel.description}. If you switch the connected Google
                            account, re-pick the target folder below.
                          </span>
                        ) : null;
                      })()}
                      {hasFetchedIntegrations &&
                        availableIntegrations.length === 0 &&
                        !isFetchingIntegrations && (
                          <span className="ptp-est-range-config__block-error">
                            No active Google Drive integrations found. Please connect one in the
                            Integrations page.
                          </span>
                        )}
                    </div>
                    {googleDriveIntegrationUuidDraft && (
                      <div className="ptp-est-range-config__block-field">
                        <DriveFolderField
                          integrationUuid={googleDriveIntegrationUuidDraft}
                          value={googleDriveFolderDraft}
                          onChange={setGoogleDriveFolderDraft}
                          onSelectedFolderNameChange={setGoogleDriveFolderName}
                          label="Target Folder"
                          hint="When a user exports a cost estimate, it will be saved in this folder."
                        />
                        <ExportSubfolderField
                          value={subfolderDraft}
                          onChange={setSubfolderDraft}
                          parentHint={googleDriveFolderName || 'the selected folder'}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : error || !data ? (
          /* ── Template: error/empty ── */
          <p className="ptp-empty-text">{error ?? 'No estimate ruleset found for this product.'}</p>
        ) : (
          /* ── Template: toolbar + sections ── */
          <>
            <div className="ptp-toolbar">
              <div className="ptp-toolbar__left">
                <Select
                  value={selected?.rulesetKey ?? ''}
                  onValueChange={handleFamilyChange}
                  size="sm"
                >
                  {[...rulesetGroups.keys()].map((key) => (
                    <SelectItem key={key} value={key}>
                      {familyLabel(key)}
                    </SelectItem>
                  ))}
                </Select>
                {familyRulesets.length > 1 && (
                  <Select
                    value={selected ? String(selected.id) : ''}
                    onValueChange={handleVersionChange}
                    size="sm"
                  >
                    {familyRulesets.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {formatVersionLabel({
                          version: r.engineVersion,
                          status: r.status,
                          scope: r.scope,
                        })}
                      </SelectItem>
                    ))}
                  </Select>
                )}
                {scopedCategoryKey != null
                  ? selected && <ScopeBadge scoped={selected.categoryKey === scopedCategoryKey} />
                  : selected &&
                    categoryOptions.length > 0 && (
                      <Select
                        value={selected.categoryKey ?? ''}
                        onValueChange={(v) => onCategoryChange(selected.id, v || null)}
                        size="sm"
                      >
                        {categoryOptions.map((o) => (
                          <SelectItem key={o.valueKey} value={o.valueKey}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                {selected?.isActive && <span className="ptp-badge ptp-badge--default">Active</span>}
                {selected?.scope === 'system' && (
                  <span className="ptp-badge ptp-badge--scope-system">System</span>
                )}
              </div>
              <div className="ptp-toolbar__right">
                {selected && (
                  <ComingSoon>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled
                      onClick={() => void onCreateFromSource(selected.id)}
                    >
                      New
                    </Button>
                  </ComingSoon>
                )}
                {selected && selected.scope === 'workspace' && selected.status === 'draft' && (
                  <ComingSoon>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled
                      onClick={() => void onPublish(selected.id)}
                    >
                      Publish
                    </Button>
                  </ComingSoon>
                )}
                {selected && !selected.isActive && selected.status === 'published' && (
                  <>
                    {confirmingDefault ? (
                      <>
                        <span className="ptp-toolbar__confirm-label">Set as active?</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmingDefault(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setConfirmingDefault(false);
                            void onSetDefault(selected.id);
                          }}
                        >
                          Confirm
                        </Button>
                      </>
                    ) : (
                      <ComingSoon>
                        <Button type="button" variant="primary" size="sm" disabled>
                          Set as active
                        </Button>
                      </ComingSoon>
                    )}
                  </>
                )}
                <Button type="button" variant="outline" size="sm" onClick={onToggleCollapsed}>
                  {collapsed ? 'Expand all' : 'Collapse all'}
                </Button>
              </div>
            </div>
            <div className="ptp-est-sections">
              {data.sections.map((section) => (
                <EstimateRulesetSection
                  key={`${section.sectionKey}-${String(collapsed)}`}
                  section={section}
                  signals={data.signals}
                  onRuleClick={(rule) => onRuleClick(rule, data.signals)}
                  selectedRuleKey={selectedRuleKey}
                  initialOpen={!collapsed}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Shared Estimate Detail Pane ─────────────────────────────────────────────

interface DetailSection {
  label: string;
  content: React.ReactNode;
}

/**
 * Shared layout wrapper for Signal, Config, and Rule detail panes.
 * Renders the standard header (title + key), optional type-badge row,
 * description paragraph, and a list of labelled sections.
 */
function EstimateDetailPane({
  title,
  itemKey,
  typeRow,
  description,
  sections,
}: {
  title: string;
  itemKey: string;
  typeRow?: React.ReactNode;
  description?: string | null;
  sections: DetailSection[];
}) {
  return (
    <div className="ptp-sdt">
      <div className="ptp-sdt__header">
        <span className="ptp-sdt__label">{title}</span>
        <code className="ptp-sdt__key">{itemKey}</code>
      </div>
      {typeRow && <div className="ptp-sdt__type-row">{typeRow}</div>}
      {description != null && <p className="ptp-sdt__description">{description}</p>}
      {sections.map((sec) => (
        <div key={sec.label} className="ptp-sdt__section">
          <span className="ptp-sdt__section-label">{sec.label}</span>
          {sec.content}
        </div>
      ))}
    </div>
  );
}

// ─── Rule Detail Pane ────────────────────────────────────────────────────────

interface RuleExampleMeta {
  input: string;
  result: string;
}

/** Example inputs and outputs for rules that have non-trivial computed formulas. */
const RULE_EXAMPLE_META: Record<string, RuleExampleMeta> = {
  // ── NetSuite - R2R ─────────────────────────────────────────────────────────
  r2r_subsidiary_per_unit: {
    input: '4 operating subsidiaries, subsidiary_per_unit = 2 hrs',
    result: '4 × 2 = 8 hrs',
  },
  r2r_multibook_per_additional_book: {
    input: '5 total accounting books, per_additional_book = 4 hrs',
    result: 'max(5 − 2, 0) × 4 = 3 × 4 = 12 hrs',
  },
  r2r_fixed_assets_count_adder: {
    input: '450 assets, threshold = 100, per_100_adder = 4 hrs',
    result: 'floor((450 − 1) / 100) × 4 = 4 × 4 = 16 hrs',
  },
  // ── NetSuite - Rollups ─────────────────────────────────────────────────────
  rollup_testing: {
    input: 'pre_go_live_before_testing signal = 250 hrs, testing_percent = 20%',
    result: 'ceil(250 × 0.20) = 50 hrs',
  },
  rollup_go_live: {
    input: 'pre_go_live signal = 600 hrs (medium tier: 401–1000)',
    result: '40 hrs',
  },
  rollup_post_go_live: {
    input: 'option = custom, base = 40 hrs, 4 extra weeks × 7 hrs/week',
    result: '40 + 4 × 7 = 68 hrs',
  },
  rollup_project_management: {
    input: 'pre_go_live = 300 hrs, go_live = 40 hrs, pm_percent = 20%',
    result: 'ceil((300 + 40) × 0.20) = ceil(68) = 68 hrs',
  },
  // ── Rillet - Discovery adders ──────────────────────────────────────────────
  rillet_discovery_techstack: {
    input: 'Current ERP = QuickBooks',
    result: 'lookup(quickbooks) → 20 hrs',
  },
  rillet_discovery_stage: {
    input: 'Company stage = Series A',
    result: 'lookup(stage_series_a) → 4 hrs',
  },
  rillet_discovery_employees: {
    input: '550 employees (medium tier: 101–1000)',
    result: 'lookup(employees_medium) → 8 hrs',
  },
  // ── Rillet - Core GL ───────────────────────────────────────────────────────
  rillet_core_gl_per_entity_adder: {
    input: '4 subsidiaries, per_entity_adder = 4 hrs',
    result: 'max(4 − 1, 0) × 4 = 3 × 4 = 12 hrs',
  },
  // ── Rillet - Revenue Recognition ───────────────────────────────────────────
  rillet_revenue_recognition_model_adder: {
    input: '3 revenue models, per_model_adder = 8 hrs',
    result: 'max(3 − 1, 0) × 8 = 2 × 8 = 16 hrs',
  },
  // ── Rillet - COA ───────────────────────────────────────────────────────────
  rillet_coa_gl_accounts_adder: {
    input: '750 GL accounts, threshold = 500, block_size = 100, per_block = 2 hrs',
    result: 'floor((750 − 500) / 100) × 2 = 2 × 2 = 4 hrs',
  },
  // ── Rillet - Data Migration ────────────────────────────────────────────────
  rillet_migration_all_historical_per_year_adder: {
    input: '4 years of history, per_additional_year = 6 hrs',
    result: 'max(4 − 1, 0) × 6 = 3 × 6 = 18 hrs',
  },
  // ── Rillet - Rollups ───────────────────────────────────────────────────────
  rillet_rollup_testing: {
    input: 'pre_go_live_before_testing signal = 160 hrs, testing_percent = 15%',
    result: 'ceil(160 × 0.15) = ceil(24) = 24 hrs',
  },
  rillet_rollup_project_management: {
    input: 'pre_go_live = 180 hrs, go_live = 24 hrs, pm_percent = 20%',
    result: 'ceil((180 + 24) × 0.20) = ceil(40.8) = 41 hrs',
  },
};

function RuleDetailPane({
  rule,
  signals,
  fieldLabels,
  productUuid,
}: {
  rule: EstimateRuleInfo;
  signals: Record<string, SignalInfo>;
  fieldLabels?: Record<string, string>;
  productUuid: string;
}) {
  const valueSegs = React.useMemo(() => parseFormulaExpr(rule.valueExpr), [rule.valueExpr]);
  const condSegs = React.useMemo(() => parseFormulaExpr(rule.conditionExpr), [rule.conditionExpr]);

  const cfgRefs = React.useMemo(() => {
    const seen = new Map<string, { key: string; defaultVal: string }>();
    for (const seg of [...valueSegs, ...condSegs]) {
      if (seg.type === 'cfg' && !seen.has(seg.key)) {
        seen.set(seg.key, { key: seg.key, defaultVal: seg.defaultVal });
      }
    }
    return [...seen.values()];
  }, [valueSegs, condSegs]);

  const fieldRefs = React.useMemo(() => {
    const seen = new Map<string, { fn: string; key: string }>();
    for (const seg of [...valueSegs, ...condSegs]) {
      if (seg.type === 'field' && !seen.has(seg.key)) {
        seen.set(seg.key, { fn: seg.fn, key: seg.key });
      }
    }
    return [...seen.values()];
  }, [valueSegs, condSegs]);

  const sigRefs = React.useMemo(() => {
    const seen = new Set<string>();
    for (const seg of [...valueSegs, ...condSegs]) {
      if (seg.type === 'sig') seen.add(seg.key);
    }
    return [...seen];
  }, [valueSegs, condSegs]);

  const [cfgValues, setCfgValues] = React.useState<Record<string, unknown>>({});
  const [isLoadingCfg, setIsLoadingCfg] = React.useState(false);
  React.useEffect(() => {
    if (cfgRefs.length === 0 || !productUuid) return;
    setIsLoadingCfg(true);
    apiFetch<{ profile: EstimateProfileData }>(
      `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/calculator-profile`
    )
      .then((r) => {
        const vals: Record<string, unknown> = {};
        for (const ref of cfgRefs) {
          const config = r.profile.config as Record<string, unknown>;
          // Try the full key as a flat JSON key first (e.g. 'rillet.testing_percent');
          // fall back to nested traversal for namespaced keys like 'rillet_coa_hours.gl_account_threshold'.
          if (ref.key in config) {
            vals[ref.key] = config[ref.key];
            continue;
          }
          const parts = ref.key.split('.');
          let cur: unknown = config;
          for (const part of parts) {
            if (cur !== null && typeof cur === 'object') {
              cur = (cur as Record<string, unknown>)[part];
            } else {
              cur = undefined;
              break;
            }
          }
          vals[ref.key] = cur;
        }
        setCfgValues(vals);
      })
      .catch(() => {})
      .finally(() => setIsLoadingCfg(false));
  }, [productUuid, rule.ruleKey]);

  const showCondition = rule.conditionExpr !== 'true' && rule.conditionExpr !== '1';

  const sections: DetailSection[] = [
    {
      label: 'Formula',
      content: (
        <div className="ptp-sdt__formula-block">
          <FormulaChips expr={rule.valueExpr} signals={signals} static />
        </div>
      ),
    },
    ...(showCondition
      ? [
          {
            label: 'Condition',
            content: (
              <div className="ptp-sdt__formula-block">
                <FormulaChips expr={rule.conditionExpr} signals={signals} static />
              </div>
            ),
          },
        ]
      : []),
    ...(cfgRefs.length > 0
      ? [
          {
            label: 'Config values',
            content: (
              <div className="ptp-rdt__ref-list">
                {cfgRefs.map((ref) => {
                  const meta = CONFIG_KEY_META[ref.key] ?? null;
                  const dotIdx = ref.key.lastIndexOf('.');
                  const leafKey = dotIdx >= 0 ? ref.key.slice(dotIdx + 1) : ref.key;
                  const label = meta?.label ?? humanizeCfgKey(leafKey);
                  const resolvedVal = cfgValues[ref.key];
                  return (
                    <div key={ref.key} className="ptp-rdt__ref-card">
                      <div className="ptp-rdt__ref-card-row">
                        <span className="ptp-rdt__ref-label">{label}</span>
                        <span className="ptp-sdt__type-badge ptp-sdt__type-badge--field">
                          Config
                        </span>
                      </div>
                      <code className="ptp-rdt__ref-key">{ref.key}</code>
                      {meta?.description && <p className="ptp-rdt__ref-desc">{meta.description}</p>}
                      <div className="ptp-rdt__ref-value-row">
                        <span className="ptp-rdt__ref-value-label">Value</span>
                        <code className="ptp-rdt__ref-value-val">
                          {isLoadingCfg
                            ? '…'
                            : resolvedVal !== undefined
                              ? formatProfileValue(resolvedVal)
                              : '-'}
                        </code>
                      </div>
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]
      : []),
    ...(fieldRefs.length > 0
      ? [
          {
            label: 'Field inputs',
            content: (
              <div className="ptp-rdt__ref-list">
                {fieldRefs.map((ref) => {
                  const label = fieldLabels?.[ref.key];
                  return (
                    <div key={ref.key} className="ptp-rdt__ref-card">
                      <div className="ptp-rdt__ref-card-row">
                        {label && <span className="ptp-rdt__ref-label">{label}</span>}
                        <span className="ptp-sdt__type-badge ptp-sdt__type-badge--field">
                          {ref.fn}
                        </span>
                      </div>
                      <code className="ptp-rdt__ref-key">{ref.key}</code>
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]
      : []),
    ...(sigRefs.length > 0
      ? [
          {
            label: 'Signal inputs',
            content: (
              <div className="ptp-rdt__ref-list">
                {sigRefs.map((key) => {
                  const sig = signals[key];
                  const aiMeta = sig?.aiTaskKey ? AI_TASK_META[sig.aiTaskKey] : null;
                  const formulaMeta =
                    sig?.computationType === 'formula' ? FORMULA_SIGNAL_META[key] : null;
                  const example = aiMeta?.example ?? formulaMeta?.example ?? null;
                  const sourceKeys = sig?.sourceFieldKeys ?? [];
                  return (
                    <div key={key} className="ptp-rdt__ref-card">
                      <div className="ptp-rdt__ref-card-row">
                        {sig && <span className="ptp-rdt__ref-label">{sig.label}</span>}
                        <span className="ptp-sdt__type-badge ptp-sdt__type-badge--field">
                          Signal
                        </span>
                      </div>
                      <code className="ptp-rdt__ref-key">{key}</code>
                      {sourceKeys.length > 0 && (
                        <div className="ptp-sdt__key-list">
                          {sourceKeys.map((k) => (
                            <Tooltip.Simple key={k} label={k}>
                              <code className="ptp-sdt__key-chip">
                                {fieldLabels?.[k] ? `${fieldLabels[k]} (${k})` : k}
                              </code>
                            </Tooltip.Simple>
                          ))}
                        </div>
                      )}
                      {example && (
                        <div className="ptp-sdt__example-block">
                          <span className="ptp-sdt__example-input">{example.input}</span>
                          <span className="ptp-sdt__example-result">{example.result}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]
      : []),
    ...(RULE_EXAMPLE_META[rule.ruleKey]
      ? [
          {
            label: 'Example',
            content: (
              <div className="ptp-sdt__example-block">
                <span className="ptp-sdt__example-input">
                  {RULE_EXAMPLE_META[rule.ruleKey]!.input}
                </span>
                <span className="ptp-sdt__example-result">
                  {RULE_EXAMPLE_META[rule.ruleKey]!.result}
                </span>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <EstimateDetailPane
      title={rule.label}
      itemKey={rule.ruleKey}
      description={rule.description}
      sections={sections}
    />
  );
}

// ─── Signal Detail Pane ───────────────────────────────────────────────────────

interface FormulaSignalMeta {
  description: string;
  example: { input: string; result: string };
}

/** Metadata for formula-type signals keyed by signalKey. */
const FORMULA_SIGNAL_META: Record<string, FormulaSignalMeta> = {
  'netsuite.design_hours': {
    description:
      'Scales discovery hours by a fixed design-to-discovery ratio. Design scoping is always proportional to discovery effort.',
    example: {
      input: 'discovery_hours = 20 hrs, design_to_discovery_ratio = 1.0 (default)',
      result: '20 × 1.0 = 20 hrs',
    },
  },
  'netsuite.o2c_cash_hours': {
    description:
      'Adds hours for each electronic payment method the client uses, then flat fees for missing processor setup and credit memo workflows.',
    example: {
      input: 'User: ACH + credit card, no payment processor configured, uses credit memos',
      result: '4 (electronic) + 4 (no processor) + 4 (credit memos) = 12 hrs',
    },
  },
  'rillet.discovery_hours': {
    description:
      'Starts from a base and adds per-point hours for each complexity point the deal scores above 1 on the subjective effort scale.',
    example: {
      input: 'User subjective effort score = 4 (self-rated or AI-assisted)',
      result: '8 + max(4 − 1, 0) × 4 = 8 + 12 = 20 hrs',
    },
  },
  'rillet.design_hours': {
    description:
      'Scales discovery hours by the design-to-discovery ratio. Design work is always proportional to discovery effort.',
    example: {
      input: 'discovery_hours = 20 hrs, design_to_discovery_ratio = 1.0 (default)',
      result: '20 × 1.0 = 20 hrs',
    },
  },
};

function SignalDetailPane({
  signalKey,
  signal,
  signals,
  fieldLabels,
}: {
  signalKey: string;
  signal: SignalInfo;
  signals: Record<string, SignalInfo>;
  fieldLabels?: Record<string, string>;
}) {
  const aiMeta = signal.aiTaskKey ? AI_TASK_META[signal.aiTaskKey] : null;
  const formulaMeta = signal.computationType === 'formula' ? FORMULA_SIGNAL_META[signalKey] : null;
  const description = aiMeta?.description ?? formulaMeta?.description ?? null;
  const formulaToShow = signal.formulaExpr ?? aiMeta?.pseudoFormula ?? null;
  const example = aiMeta?.example ?? formulaMeta?.example ?? null;

  const sections: DetailSection[] = [
    ...(formulaToShow
      ? [
          {
            label: signal.formulaExpr ? 'Expression' : 'Logic',
            content: (
              <div className="ptp-sdt__formula-block">
                <FormulaChips expr={formulaToShow} signals={signals} static />
              </div>
            ),
          },
        ]
      : []),
    ...(signal.sourceFieldKeys && signal.sourceFieldKeys.length > 0
      ? [
          {
            label: 'Source fields',
            content: (
              <div className="ptp-sdt__key-list">
                {signal.sourceFieldKeys.map((k) => (
                  <Tooltip.Simple key={k} label={k}>
                    <code className="ptp-sdt__key-chip">
                      {fieldLabels?.[k] ? `${fieldLabels[k]} (${k})` : k}
                    </code>
                  </Tooltip.Simple>
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(example
      ? [
          {
            label: 'Example',
            content: (
              <div className="ptp-sdt__example-block">
                <span className="ptp-sdt__example-input">{example.input}</span>
                <span className="ptp-sdt__example-result">{example.result}</span>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <EstimateDetailPane
      title={signal.label}
      itemKey={signalKey}
      typeRow={
        <>
          <span className={`ptp-sdt__type-badge ptp-sdt__type-badge--${signal.computationType}`}>
            {signal.computationType === 'formula' ? 'Formula' : 'AI Task'}
          </span>
          {signal.aiTaskKey && <code className="ptp-sdt__task-key">{signal.aiTaskKey}</code>}
        </>
      }
      description={description}
      sections={sections}
    />
  );
}

// ─── Config Detail Pane ─────────────────────────────────────────────────────

interface ConfigKeyMeta {
  label: string;
  description: string;
}

/** Human-readable labels and descriptions for known calculator profile keys. */
const CONFIG_KEY_META: Record<string, ConfigKeyMeta> = {
  // ── Top-level engine constants ─────────────────────────────────────────────
  discovery_base_hours: {
    label: 'Discovery Base Hours',
    description:
      'Minimum hours allocated to any discovery engagement, before complexity adders are applied. All discovery formulas add on top of this floor.',
  },
  complexity_adder_per_score_point: {
    label: 'Complexity Adder Per Score Point',
    description:
      'Extra discovery hours added for each combined complexity point above the threshold (5). Increase this to make high-complexity deals proportionally heavier.',
  },
  ai_complexity_weight: {
    label: 'AI Complexity Weight',
    description:
      "Fraction (0–1) of the blended complexity score that comes from the AI-assessed objective score. The remainder comes from the user's slider. 0.4 means 40% AI, 60% user.",
  },
  design_to_discovery_ratio: {
    label: 'Design to Discovery Ratio',
    description:
      'Design hours expressed as a multiple of discovery hours. A ratio of 1.0 means design equals discovery; 0.5 means half as many design hours.',
  },
  testing_percent: {
    label: 'Testing Percent',
    description:
      'Fraction of total billable hours allocated to testing. Applied as a percentage on top of all other phase hours at the end of the estimate.',
  },
  integration_flow_hours_per_flow: {
    label: 'Hours Per Integration Flow',
    description: 'Flat hour cost charged for each custom integration flow in scope.',
  },
  suitebilling_high_volume_threshold: {
    label: 'SuiteBilling High-Volume Threshold',
    description:
      'Monthly invoice count above which the SuiteBilling high-subscription-volume trigger flag is raised during AI assessment.',
  },
  post_go_live_base_hours: {
    label: 'Post Go-Live Base Hours',
    description:
      'Base support hours included in every engagement regardless of the requested support period.',
  },
  post_go_live_weekly_adder_hours: {
    label: 'Post Go-Live Weekly Adder',
    description:
      'Additional hours added per week of post-go-live support beyond the default period.',
  },
  post_go_live_support_days_default: {
    label: 'Post Go-Live Default Duration (Days)',
    description: 'Default post-go-live support window in days when no duration is specified.',
  },
  // ── R2R hours ─────────────────────────────────────────────────────────────
  'r2r_hours.bundle_base': {
    label: 'R2R Bundle Base',
    description: 'Base hours applied for the core record-to-report bundle.',
  },
  'r2r_hours.subsidiary_base': {
    label: 'Subsidiary Base',
    description: 'Base hours for operating subsidiary setup within R2R.',
  },
  'r2r_hours.subsidiary_per_unit': {
    label: 'Subsidiary Per-Unit Hours',
    description: 'Additional hours for each subsidiary beyond the first.',
  },
  'r2r_hours.coa_base': {
    label: 'Chart of Accounts Base',
    description: 'Base hours for chart of accounts configuration.',
  },
  'r2r_hours.coa_ready_adder': {
    label: 'CoA Ready Adder',
    description: 'Hours added when the client provides a ready-to-use chart of accounts.',
  },
  'r2r_hours.coa_iteration_1_adder': {
    label: 'CoA Iteration 1 Adder',
    description: 'Hours added for a single round of CoA iteration and cleanup.',
  },
  'r2r_hours.coa_iteration_2_adder': {
    label: 'CoA Iteration 2 Adder',
    description: 'Hours added for two rounds of CoA iteration - reflects higher cleanup effort.',
  },
  'r2r_hours.coa_light_cleanup': {
    label: 'CoA Light Cleanup',
    description: 'Hours for minor CoA cleanup (few renaming or reorg changes).',
  },
  'r2r_hours.coa_moderate_cleanup': {
    label: 'CoA Moderate Cleanup',
    description: 'Hours for moderate CoA cleanup (restructuring segments or adding sub-accounts).',
  },
  'r2r_hours.coa_heavy_cleanup': {
    label: 'CoA Heavy Cleanup',
    description: 'Hours for heavy CoA cleanup (full restructure, mergers, or legacy debt).',
  },
  'r2r_hours.coa_build_new': {
    label: 'CoA Build New',
    description: 'Hours to build a net-new chart of accounts from scratch.',
  },
  'r2r_hours.multibook_base': {
    label: 'Multibook Base Hours',
    description: 'Base hours for setting up multi-book accounting (e.g. GAAP + IFRS).',
  },
  'r2r_hours.multibook_per_additional_book': {
    label: 'Multibook Per Additional Book',
    description: 'Hours added for each additional accounting book beyond the first.',
  },
  'r2r_hours.sales_tax_tool': {
    label: 'Sales Tax Tool Hours',
    description: 'Hours for integrating and configuring an external sales tax tool (e.g. Avalara).',
  },
  'r2r_hours.intercompany_journal': {
    label: 'Intercompany Journal Hours',
    description: 'Base hours for setting up intercompany journal entry rules.',
  },
  'r2r_hours.intercompany_sales_purchases_adder': {
    label: 'Intercompany Sales & Purchases Adder',
    description:
      'Additional hours when intercompany sales/purchase transactions are in scope (beyond journals).',
  },
  // ── O2C hours ─────────────────────────────────────────────────────────────
  'o2c_hours.suitebilling': {
    label: 'SuiteBilling Hours',
    description: 'Hours allocated when SuiteBilling is recommended for the engagement.',
  },
  'o2c_hours.bill_schedule': {
    label: 'Bill Schedule Hours',
    description: 'Hours for configuring billing schedules without full SuiteBilling.',
  },
  'o2c_hours.external_billing_flat': {
    label: 'External Billing Flat Hours',
    description: 'Flat hours applied when billing is handled by an external system.',
  },
  'o2c_hours.suitebilling_score_threshold': {
    label: 'SuiteBilling Score Threshold',
    description:
      'Minimum SuiteBilling trigger score required to recommend SuiteBilling over simpler billing options.',
  },
  'o2c_hours.invoice_complexity_high': {
    label: 'Invoice Complexity - High',
    description: 'Hours added when invoice complexity is AI-assessed as high.',
  },
  'o2c_hours.invoice_complexity_low': {
    label: 'Invoice Complexity - Low',
    description: 'Hours added when invoice complexity is AI-assessed as low.',
  },
  'o2c_hours.invoice_template_base': {
    label: 'Invoice Template Base',
    description: 'Base hours for the first invoice template.',
  },
  'o2c_hours.invoice_template_per_additional': {
    label: 'Invoice Template Per Additional',
    description: 'Hours for each template beyond the first.',
  },
  'o2c_hours.invoice_volume_low_threshold': {
    label: 'Invoice Volume Low Threshold',
    description: 'Monthly invoice count at or below which low-volume hours are used.',
  },
  'o2c_hours.invoice_volume_mid_threshold': {
    label: 'Invoice Volume Mid Threshold',
    description: 'Monthly invoice count at or below which mid-volume hours are used.',
  },
  'o2c_hours.invoice_volume_low': {
    label: 'Invoice Volume - Low',
    description: 'Hours added for low monthly invoice volume.',
  },
  'o2c_hours.invoice_volume_mid': {
    label: 'Invoice Volume - Mid',
    description: 'Hours added for mid-range monthly invoice volume.',
  },
  'o2c_hours.invoice_volume_high': {
    label: 'Invoice Volume - High',
    description: 'Hours added for high monthly invoice volume.',
  },
  'o2c_hours.payment_processor_single': {
    label: 'Payment Processor - Single',
    description: 'Hours for integrating a single payment processor.',
  },
  'o2c_hours.payment_processor_multi': {
    label: 'Payment Processor - Multi',
    description: 'Hours for integrating multiple payment processors.',
  },
  'o2c_hours.cash_collection_electronic': {
    label: 'Cash Collection - Electronic',
    description: 'Hours added per electronic payment method (ACH, wire, portal, credit card).',
  },
  'o2c_hours.cash_collection_check': {
    label: 'Cash Collection - Check',
    description: 'Hours added for check-based payment collection.',
  },
  'o2c_hours.cash_collection_no_processor': {
    label: 'Cash Collection - No Processor',
    description: 'Hours added when no payment processor is configured.',
  },
  'o2c_hours.cash_collection_credit_memo': {
    label: 'Cash Collection - Credit Memo',
    description: 'Hours added when credit memo workflows are in scope.',
  },
  // ── Revenue hours ─────────────────────────────────────────────────────────
  'revenue_hours.base': {
    label: 'Revenue Base Hours',
    description: 'Base hours for any revenue management configuration.',
  },
  'revenue_hours.asc606_adder': {
    label: 'ASC 606 Adder',
    description:
      'Additional hours when ASC 606 fair-value revenue allocation is in scope - reflects the added SSP analysis and setup complexity.',
  },
  // ── P2P hours ─────────────────────────────────────────────────────────────
  'p2p_hours.base': {
    label: 'P2P Base Hours',
    description: 'Base hours for procure-to-pay configuration.',
  },
  'p2p_hours.purchase_orders_adder': {
    label: 'Purchase Orders Adder',
    description: 'Hours added when purchase orders are in scope.',
  },
  'p2p_hours.vendor_bills_adder': {
    label: 'Vendor Bills Adder',
    description: 'Hours added for vendor bill processing configuration.',
  },
  'p2p_hours.automate_payments_adder': {
    label: 'Automate Payments Adder',
    description: 'Hours added when automated payment runs are in scope.',
  },
  'p2p_hours.high_volume_threshold': {
    label: 'High-Volume Bill Threshold',
    description: 'Monthly bill count above which the high-volume adder applies.',
  },
  'p2p_hours.high_volume_adder': {
    label: 'High-Volume Bill Adder',
    description: 'Hours added when monthly bill count exceeds the high-volume threshold.',
  },
  'p2p_hours.ocr_adder': {
    label: 'OCR Adder',
    description: 'Hours added when optical character recognition (bill capture) is in scope.',
  },
  // ── Third-party hours ──────────────────────────────────────────────────────
  'third_party_hours.support_per_tool': {
    label: 'Hours Per Third-Party Tool',
    description: 'Hours allocated for each third-party integration tool in scope.',
  },
  // ── Optional modules ──────────────────────────────────────────────────────
  'optional_module_hours.optional_low_complexity': {
    label: 'Optional Module - Low Complexity',
    description: 'Hours for an optional add-on module scoped at low complexity.',
  },
  'optional_module_hours.optional_mid_complexity': {
    label: 'Optional Module - Mid Complexity',
    description: 'Hours for an optional add-on module scoped at mid complexity.',
  },
  'optional_module_hours.optional_high_complexity': {
    label: 'Optional Module - High Complexity',
    description: 'Hours for an optional add-on module scoped at high complexity.',
  },
  // ── Data migration ─────────────────────────────────────────────────────────
  'data_migration_hours.trial_balances': {
    label: 'Migration - Trial Balances Only',
    description: 'Hours when only trial balance data is migrated into the new system.',
  },
  'data_migration_hours.one_year_historical': {
    label: 'Migration - 1 Year Historical',
    description: 'Hours when one year of historical transaction data is migrated.',
  },
  'data_migration_hours.all_historical': {
    label: 'Migration - Full Historical',
    description: 'Hours when the full historical transaction dataset is migrated.',
  },
  // ── Rillet module hours ────────────────────────────────────────────────────
  'rillet_module_hours.core_gl': {
    label: 'Core GL',
    description: 'Base GL setup - chart of accounts, journal entries, single-entity close.',
  },
  'rillet_module_hours.per_entity_adder': {
    label: 'Per Entity Adder',
    description: 'Incremental hours for each additional legal entity beyond the first.',
  },
  'rillet_module_hours.ar_collections': {
    label: 'AR Collections',
    description: 'AR collections module - dunning workflows, aging reports, cash application.',
  },
  'rillet_module_hours.ap_payments': {
    label: 'AP Payments',
    description: 'AP payments module - bill entry, approval workflows, payment runs.',
  },
  'rillet_module_hours.revenue_recognition_base': {
    label: 'Revenue Recognition Base',
    description: 'Base hours for revenue recognition configuration.',
  },
  'rillet_module_hours.revenue_recognition_per_model_adder': {
    label: 'Revenue Recognition Per-Model Adder',
    description: 'Additional hours per revenue recognition model type beyond the first.',
  },
  'rillet_module_hours.multi_entity_consolidations': {
    label: 'Multi-Entity Consolidations',
    description: 'Elimination entries, minority interest, FX translation.',
  },
  'rillet_module_hours.intercompany_eliminations': {
    label: 'Intercompany Eliminations',
    description: 'Intercompany eliminations configuration.',
  },
  'rillet_module_hours.close_management': {
    label: 'Close Management',
    description: 'Month-end close management - task templates, owner assignment, sign-off.',
  },
  'rillet_module_hours.budgeting_forecasting': {
    label: 'Budgeting & Forecasting',
    description:
      'Budgeting & forecasting module - plan setup, variance reporting, scenario modelling.',
  },
  'rillet_module_hours.financial_reporting': {
    label: 'Financial Reporting',
    description: 'Custom report builder, dashboards, scheduled distribution.',
  },
  'rillet_module_hours.custom_dimensions': {
    label: 'Custom Dimensions',
    description: 'Custom dimensions / tracking categories setup and mapping.',
  },
  // ── Rillet CoA hours ───────────────────────────────────────────────────────
  'rillet_coa_hours.migrate_existing': {
    label: 'Migrate Existing CoA',
    description:
      'Migrate an existing chart of accounts from the legacy ERP into Rillet with cleanup.',
  },
  'rillet_coa_hours.build_new': {
    label: 'Build New CoA',
    description: 'Build a brand-new chart of accounts from scratch.',
  },
  'rillet_coa_hours.hybrid': {
    label: 'Hybrid CoA',
    description: 'Hybrid approach - partial migration plus new account creation.',
  },
  'rillet_coa_hours.gl_account_threshold': {
    label: 'GL Account Threshold',
    description: 'GL account count above which per-block volume adder hours are charged.',
  },
  'rillet_coa_hours.gl_account_block_size': {
    label: 'GL Account Block Size',
    description: 'Block size for the per-block volume adder calculation.',
  },
  'rillet_coa_hours.gl_account_per_block_adder': {
    label: 'GL Account Per-Block Adder',
    description: 'Additional hours per block of accounts above the threshold.',
  },
  // ── Rillet data migration hours ────────────────────────────────────────────
  'rillet_data_migration_hours.trial_balances': {
    label: 'Trial Balances Only',
    description: 'Opening balances as of go-live date, no historical transactions.',
  },
  'rillet_data_migration_hours.one_year_historical': {
    label: '1 Year Historical',
    description: 'One year of historical transactions migrated in addition to opening balances.',
  },
  'rillet_data_migration_hours.all_historical_base': {
    label: 'Full Historical Base',
    description: 'Base hours for a full historical migration (all years).',
  },
  'rillet_data_migration_hours.per_additional_year': {
    label: 'Per Additional Year',
    description: 'Incremental hours for each year of history beyond the first in a full migration.',
  },
  // ── Rillet ERP techstack hours ─────────────────────────────────────────────
  'rillet_techstack_hours.quickbooks': {
    label: 'QuickBooks',
    description: 'Discovery hours for migration from QuickBooks Online / Desktop.',
  },
  'rillet_techstack_hours.sage': {
    label: 'Sage',
    description: 'Discovery hours for migration from Sage Intacct / Sage 50 / Sage 200.',
  },
  'rillet_techstack_hours.microsoft_dynamics': {
    label: 'Microsoft Dynamics',
    description: 'Discovery hours for migration from Dynamics 365 / Business Central / GP.',
  },
  'rillet_techstack_hours.odoo': {
    label: 'Odoo',
    description: 'Discovery hours for migration from Odoo ERP.',
  },
  'rillet_techstack_hours.sap': {
    label: 'SAP',
    description: 'Discovery hours for migration from SAP ECC / S/4HANA.',
  },
  'rillet_techstack_hours.netsuite': {
    label: 'NetSuite',
    description: 'Discovery hours for migration from NetSuite.',
  },
  'rillet_techstack_hours.oracle_erp_cloud': {
    label: 'Oracle ERP Cloud',
    description: 'Discovery hours for migration from Oracle Fusion Cloud.',
  },
  'rillet_techstack_hours.workday_financials': {
    label: 'Workday Financials',
    description: 'Discovery hours for migration from Workday Financials.',
  },
  'rillet_techstack_hours.xero': {
    label: 'Xero',
    description: 'Discovery hours for migration from Xero.',
  },
  'rillet_techstack_hours.freshbooks': {
    label: 'FreshBooks',
    description: 'Discovery hours for migration from FreshBooks.',
  },
  'rillet_techstack_hours.zoho_books': {
    label: 'Zoho Books',
    description: 'Discovery hours for migration from Zoho Books.',
  },
  'rillet_techstack_hours.campfire': {
    label: 'Campfire',
    description: 'Discovery hours for migration from Campfire.',
  },
  'rillet_techstack_hours.spreadsheets': {
    label: 'Spreadsheets',
    description: 'Discovery hours for migration from Excel / Google Sheets.',
  },
  'rillet_techstack_hours.infor': {
    label: 'Infor',
    description: 'Discovery hours for migration from Infor CloudSuite / M3.',
  },
  'rillet_techstack_hours.accumatica': {
    label: 'Acumatica',
    description: 'Discovery hours for migration from Acumatica.',
  },
  // ── Rillet complexity adder hours ──────────────────────────────────────────
  'rillet_complexity_hours.stage_preseed': {
    label: 'Stage - Pre-Seed',
    description: 'Discovery adder for pre-seed stage companies.',
  },
  'rillet_complexity_hours.stage_seed': {
    label: 'Stage - Seed',
    description: 'Discovery adder for seed stage companies.',
  },
  'rillet_complexity_hours.stage_series_a': {
    label: 'Stage - Series A',
    description: 'Discovery adder for Series A companies.',
  },
  'rillet_complexity_hours.stage_series_b': {
    label: 'Stage - Series B',
    description: 'Discovery adder for Series B companies.',
  },
  'rillet_complexity_hours.stage_series_c': {
    label: 'Stage - Series C',
    description: 'Discovery adder for Series C companies.',
  },
  'rillet_complexity_hours.stage_series_d_plus': {
    label: 'Stage - Series D+',
    description: 'Discovery adder for Series D+ companies.',
  },
  'rillet_complexity_hours.stage_ipo': {
    label: 'Stage - IPO',
    description: 'Discovery adder for IPO-stage / public companies.',
  },
  'rillet_complexity_hours.employees_low': {
    label: 'Employees - Low (0–100)',
    description: 'Discovery adder for low employee count (0–100).',
  },
  'rillet_complexity_hours.employees_medium': {
    label: 'Employees - Medium (101–1000)',
    description: 'Discovery adder for medium employee count (101–1000).',
  },
  'rillet_complexity_hours.employees_high': {
    label: 'Employees - High (1000+)',
    description: 'Discovery adder for high employee count (1000+).',
  },
  'rillet_complexity_hours.subsidiaries_low': {
    label: 'Subsidiaries - Low (1)',
    description: 'Discovery adder for single subsidiary.',
  },
  'rillet_complexity_hours.subsidiaries_medium': {
    label: 'Subsidiaries - Medium (2–3)',
    description: 'Discovery adder for 2–3 subsidiaries.',
  },
  'rillet_complexity_hours.subsidiaries_high': {
    label: 'Subsidiaries - High (4+)',
    description: 'Discovery adder for 4+ subsidiaries.',
  },
  'rillet_complexity_hours.currencies_low': {
    label: 'Currencies - Low (1)',
    description: 'Discovery adder for single currency.',
  },
  'rillet_complexity_hours.currencies_medium': {
    label: 'Currencies - Medium (2–3)',
    description: 'Discovery adder for 2–3 currencies.',
  },
  'rillet_complexity_hours.currencies_high': {
    label: 'Currencies - High (4+)',
    description: 'Discovery adder for 4+ currencies.',
  },
  'rillet_complexity_hours.revenue_low': {
    label: 'Revenue - Low (<$50M ARR)',
    description: 'Discovery adder for ARR under $50M.',
  },
  'rillet_complexity_hours.revenue_medium': {
    label: 'Revenue - Medium ($50M–$150M ARR)',
    description: 'Discovery adder for ARR $50M–$150M.',
  },
  'rillet_complexity_hours.revenue_high': {
    label: 'Revenue - High (>$150M ARR)',
    description: 'Discovery adder for ARR over $150M.',
  },
  'rillet_complexity_hours.revenue_models_low': {
    label: 'Revenue Models - Low (0–2)',
    description: 'Discovery adder for 0–2 revenue model types.',
  },
  'rillet_complexity_hours.revenue_models_medium': {
    label: 'Revenue Models - Medium (3–4)',
    description: 'Discovery adder for 3–4 revenue model types.',
  },
  'rillet_complexity_hours.revenue_models_high': {
    label: 'Revenue Models - High (5+)',
    description: 'Discovery adder for 5+ revenue model types.',
  },
};

function humanizeCfgKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ConfigDetailPane({
  cfgKey,
  productUuid,
  signals,
  fieldLabels,
  contextSourceKeys,
}: {
  cfgKey: string;
  productUuid: string;
  signals?: Record<string, SignalInfo>;
  fieldLabels?: Record<string, string>;
  contextSourceKeys?: string[];
}) {
  const dotIdx = cfgKey.lastIndexOf('.');
  const parentKey = dotIdx >= 0 ? cfgKey.slice(0, dotIdx) : null;
  const leafKey = dotIdx >= 0 ? cfgKey.slice(dotIdx + 1) : cfgKey;

  const [resolvedValue, setResolvedValue] = React.useState<unknown>(undefined);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (!productUuid) return;
    setIsLoading(true);
    apiFetch<{ profile: EstimateProfileData }>(
      `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/calculator-profile`
    )
      .then((r) => {
        const config = r.profile.config as Record<string, unknown>;
        // Try the full key as a flat JSON key first (e.g. 'rillet.testing_percent');
        // fall back to nested traversal for keys like 'rillet_coa_hours.gl_account_threshold'.
        if (cfgKey in config) {
          setResolvedValue(config[cfgKey]);
          return;
        }
        const parts = cfgKey.split('.');
        let cur: unknown = config;
        for (const part of parts) {
          if (cur !== null && typeof cur === 'object') {
            cur = (cur as Record<string, unknown>)[part];
          } else {
            cur = undefined;
            break;
          }
        }
        setResolvedValue(cur);
      })
      .catch(() => setResolvedValue(undefined))
      .finally(() => setIsLoading(false));
  }, [productUuid, cfgKey]);

  const referencedSignals = React.useMemo(() => {
    if (!signals) return [] as Array<[string, SignalInfo]>;
    return Object.entries(signals).filter(
      ([, sig]) => sig.formulaExpr?.includes(`'${cfgKey}'`) ?? false
    );
  }, [signals, cfgKey]);

  const referencedSourceKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const [, sig] of referencedSignals) {
      for (const fieldKey of sig.sourceFieldKeys ?? []) keys.add(fieldKey);
    }
    return [...keys];
  }, [referencedSignals]);

  const meta = CONFIG_KEY_META[cfgKey] ?? null;
  const sourceKeys =
    contextSourceKeys && contextSourceKeys.length > 0 ? contextSourceKeys : referencedSourceKeys;

  const displayLabel = meta?.label ?? humanizeCfgKey(leafKey);
  const parentLabel = parentKey
    ? (PROFILE_KEY_LABELS[parentKey] ?? humanizeCfgKey(parentKey))
    : null;
  const description =
    meta?.description ??
    'Set in the Calculator Profile. Workspace profiles can override this value - the engine resolves the workspace value first, falling back to the default shown here.';

  const sections: DetailSection[] = [
    {
      label: 'Default value',
      content: (
        <div className="ptp-sdt__formula-block">
          {isLoading ? (
            <span className="ptp-sdt__expr-text">…</span>
          ) : (
            <span className="ptp-sdt__formula-line ptp-sdt__expr-text">
              {resolvedValue !== undefined ? formatProfileValue(resolvedValue) : '-'}
            </span>
          )}
        </div>
      ),
    },
    ...(referencedSignals.length > 0
      ? [
          {
            label: 'Referenced by',
            content: (
              <div className="ptp-sdt__key-list">
                {referencedSignals.map(([key, sig]) => (
                  <Tooltip.Simple key={key} label={key}>
                    <span className="ptp-sdt__sig-ref">{sig.label}</span>
                  </Tooltip.Simple>
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(sourceKeys.length > 0
      ? [
          {
            label: 'Source fields',
            content: (
              <div className="ptp-sdt__key-list">
                {sourceKeys.map((k) => (
                  <Tooltip.Simple key={k} label={k}>
                    <code className="ptp-sdt__key-chip">
                      {fieldLabels?.[k] ? `${fieldLabels[k]} (${k})` : k}
                    </code>
                  </Tooltip.Simple>
                ))}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <EstimateDetailPane
      title={displayLabel}
      itemKey={cfgKey}
      typeRow={
        <>
          <span className="ptp-sdt__type-badge ptp-sdt__type-badge--config">Config</span>
          {parentKey && <code className="ptp-sdt__task-key">{parentKey}</code>}
          {parentLabel && <span className="ptp-sdt__parent-label">{parentLabel}</span>}
        </>
      }
      description={description}
      sections={sections}
    />
  );
}

// ─── Deal category master-detail ────────────────────────────────────────────

/**
 * Primary product-level tabs (category-agnostic axis). `stage` (absent = ga) gates a tab via the
 * workspace feature-min-stage: alpha tabs show only in local/dev and are hidden in uat/prod
 * (featureMinStage='beta'). Same lightweight gate as the deal-page Resource Plan tab. See isStageAvailable.
 */
const PRODUCT_PRIMARY_TABS: ReadonlyArray<{
  value: string;
  label: string;
  stage?: 'alpha' | 'beta';
}> = [
  { value: 'global-settings', label: 'Global Settings' },
  { value: 'categories', label: 'Deal Categories', stage: 'alpha' },
  { value: 'custom-attributes', label: 'Custom Attributes' },
];

/** Secondary artifact tabs shown inside a selected deal category. */
const CATEGORY_ARTIFACT_TABS = [
  { value: 'questionnaire', label: 'Questionnaire' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'resource-plan', label: 'Resource Plan' },
  { value: 'statement-of-work', label: 'Statement of Work' },
];

/**
 * Read-only metadata header for the selected deal category: title + scope pill,
 * description, priority-role chips, and AI hints. Editing the metadata is deferred
 * to the category-authoring flow.
 */
function CategoryMetadataHeader({ category }: { category: CategoryOption | null }) {
  if (!category) return null;
  const meta = category.metadata ?? null;
  const roles = meta?.priorityRoles ?? [];
  const hasContent = Boolean(
    category.aiMode || meta?.description || roles.length > 0 || meta?.aiHints
  );
  if (!hasContent) return null;
  return (
    <header className="pcat-category-header">
      {category.aiMode && (
        <div className="pcat-category-header__title-row">
          <Tooltip.Simple label="Estimate is AI-inferred per scope item (no rule calculator)">
            <span className="da-scope-pill da-scope-pill--ai">AI mode</span>
          </Tooltip.Simple>
        </div>
      )}
      {meta?.description && <p className="pcat-category-header__desc">{meta.description}</p>}
      {roles.length > 0 && (
        <div className="pcat-category-header__band">
          <span className="pcat-category-header__roles">
            <span className="pcat-category-header__roles-label">Priority roles:</span>
            {roles.map((r) => (
              <span key={r} className="pcat-role-chip">
                {r}
              </span>
            ))}
          </span>
        </div>
      )}
      {meta?.aiHints && (
        <p className="pcat-category-header__hints">
          <span className="pcat-category-header__hints-label">AI hints:</span> {meta.aiHints}
        </p>
      )}
    </header>
  );
}

/**
 * Master list of deal categories (the left rail of the category hub). Reuses the
 * deal-attributes .da-nav idiom; each row shows the category label, a
 * System/Workspace pill, and a one-line metadata preview.
 */
function CategoryMasterList({
  categories,
  selectedKey,
  onSelect,
  width,
  onResize,
}: {
  categories: CategoryOption[];
  selectedKey: string | null;
  onSelect: (valueKey: string) => void;
  width: number;
  onResize: (delta: number) => void;
}) {
  return (
    <>
      <nav className="da-nav pcat-nav" aria-label="Deal categories" style={{ width: `${width}px` }}>
        {categories.length === 0 ? (
          <p className="da-nav__error">No deal categories for this product.</p>
        ) : (
          categories.map((cat) => {
            const preview = cat.metadata?.description ?? null;
            const active = selectedKey === cat.valueKey;
            return (
              <button
                key={cat.valueKey}
                className={['da-nav__item', 'pcat-nav__item', active ? 'da-nav__item--active' : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onSelect(cat.valueKey)}
                aria-current={active ? 'true' : undefined}
              >
                <span className="pcat-nav__item-head">
                  <span className="da-nav__label">{cat.label}</span>
                  <span
                    className={`da-scope-pill da-scope-pill--${cat.isSystem ? 'system' : 'workspace'}`}
                  >
                    {cat.isSystem ? 'System' : 'Workspace'}
                  </span>
                </span>
                {preview && <span className="pcat-nav__item-preview">{preview}</span>}
              </button>
            );
          })
        )}
      </nav>
      <ResizableDivider direction="vertical" onResize={onResize} ariaLabel="Resize category list" />
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProductAdminPage() {
  const { productUuid, productAdminSection, categoryKey, artifact } = useParams<{
    productUuid: string;
    productAdminSection?: string;
    categoryKey?: string;
    artifact?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { productName } = (location.state as { productName?: string } | null) ?? {};
  const { workspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { setRightPaneVisible, setRightPaneMode, contextPaneWidth, setContextPaneWidth } =
    useLayout();

  // Declarative base layout: context pane (product admin menu) shown, reset on unmount.
  // Dynamic right-pane mode (signal-detail) is driven by the handlers below.
  useLayoutConfig({ showContextPane: true });
  const { items: menuItems, isLoading: isMenuLoading } = useWorkspaceMenu();
  const [adminMenuCollapsed, setAdminMenuCollapsed] = React.useState(false);
  const savedPaneWidth = React.useRef(contextPaneWidth);

  // Guard: the admin menu starts expanded (adminMenuCollapsed is local state), but
  // contextPaneWidth is shared/persisted. If a previous page or session left it at
  // a collapsed width (~48px), the expanded menu would render clipped in a too-narrow
  // pane. Restore a sane width before paint so the menu is never cut off.
  React.useLayoutEffect(() => {
    if (contextPaneWidth < 100) {
      setContextPaneWidth(280);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleAdminMenu = React.useCallback(() => {
    if (adminMenuCollapsed) {
      setContextPaneWidth(savedPaneWidth.current);
      setAdminMenuCollapsed(false);
    } else {
      savedPaneWidth.current = contextPaneWidth;
      const navCollapsedWidth = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--pane-leftnav-width-collapsed'
        ),
        10
      );
      setContextPaneWidth(navCollapsedWidth);
      setAdminMenuCollapsed(true);
    }
  }, [adminMenuCollapsed, contextPaneWidth, setContextPaneWidth]);

  // Primary product-level scope (category-agnostic axis). The bare product URL
  // redirects to `categories`, and category routes carry no productAdminSection,
  // so anything that isn't an explicit product-level section resolves to the
  // Deal Categories hub.
  const scope: 'categories' | 'custom-attributes' | 'global-settings' =
    productAdminSection === 'global-settings'
      ? 'global-settings'
      : productAdminSection === 'custom-attributes'
        ? 'custom-attributes'
        : 'categories';
  // Secondary artifact within a selected category.
  const selectedArtifact = CATEGORY_ARTIFACT_TABS.some((t) => t.value === artifact)
    ? (artifact as string)
    : 'questionnaire';
  // The estimate right rail (signal/config/rule detail) is only relevant inside
  // the Estimate artifact of the category hub.
  const isEstimateArtifact = scope === 'categories' && selectedArtifact === 'estimate';

  // Detail pane state - tracks which item is open in the signal-detail right rail
  const [detailType, setDetailType] = React.useState<'signal' | 'config' | 'rule'>('rule');
  const [selectedSignalKey, setSelectedSignalKey] = React.useState<string | null>(null);
  const [signalMap, setSignalMap] = React.useState<Record<string, SignalInfo> | null>(null);
  const [selectedCfgKey, setSelectedCfgKey] = React.useState<string | null>(null);
  const [cfgContextSourceKeys, setCfgContextSourceKeys] = React.useState<string[]>([]);
  const [selectedRule, setSelectedRule] = React.useState<EstimateRuleInfo | null>(null);

  // Clear detail selections when leaving the estimate artifact
  React.useEffect(() => {
    if (!isEstimateArtifact) {
      setSelectedSignalKey(null);
      setSignalMap(null);
      setSelectedCfgKey(null);
      setCfgContextSourceKeys([]);
      setSelectedRule(null);
    }
  }, [isEstimateArtifact, setRightPaneVisible, setRightPaneMode]);

  const handleSigClick = React.useCallback(
    (key: string, signals: Record<string, SignalInfo>) => {
      setDetailType('signal');
      setSelectedSignalKey(key);
      setSignalMap(signals);
      setRightPaneVisible(true);
      setRightPaneMode('signal-detail');
    },
    [setRightPaneVisible, setRightPaneMode]
  );

  const handleCfgClick = React.useCallback(
    (key: string, signals: Record<string, SignalInfo>, contextSourceKeys: string[]) => {
      setDetailType('config');
      setSelectedCfgKey(key);
      setCfgContextSourceKeys(contextSourceKeys);
      setSignalMap(signals);
      setRightPaneVisible(true);
      setRightPaneMode('signal-detail');
    },
    [setRightPaneVisible, setRightPaneMode, setCfgContextSourceKeys]
  );

  const handleRuleClick = React.useCallback(
    (rule: EstimateRuleInfo, signals: Record<string, SignalInfo>) => {
      setDetailType('rule');
      setSelectedRule(rule);
      setSignalMap(signals);
      setRightPaneVisible(true);
      setRightPaneMode('signal-detail');
    },
    [setRightPaneVisible, setRightPaneMode]
  );

  const [templates, setTemplates] = React.useState<DealTemplateSummary[]>([]);
  const [categoryOptions, setCategoryOptions] = React.useState<CategoryOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [estimateData, setEstimateData] = React.useState<EstimateOverviewData | null>(null);
  const [estimateError, setEstimateError] = React.useState<string | null>(null);
  const [selectedEstimateRulesetId, setSelectedEstimateRulesetId] = React.useState<number | null>(
    null
  );
  const [isSavingRangeConfig, setIsSavingRangeConfig] = React.useState(false);
  const [estimateActivePane, setEstimateActivePane] = React.useState<'configuration' | 'template'>(
    'configuration'
  );
  const [forkSource, setForkSource] = React.useState<DealTemplateSummary | null>(null);
  const [selectedTemplateUuid, setSelectedTemplateUuid] = React.useState<string | null>(null);
  const [treeData, setTreeData] = React.useState<TemplateTreeSection[] | null>(null);
  const [profileFieldMappings, setProfileFieldMappings] = React.useState<Record<string, unknown>>(
    {}
  );
  const [isLoadingTree, setIsLoadingTree] = React.useState(false);
  const [isPatching, setIsPatching] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [treeCollapsed, setTreeCollapsed] = React.useState(true);
  const [estimateCollapsed, setEstimateCollapsed] = React.useState(true);
  const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [categoryNavWidth, setCategoryNavWidth] = React.useState(260);

  // Selected deal category (the category hub's master selection). Falls back to
  // the first available option until the URL carries an explicit category key.
  const selectedCategoryKey = categoryKey ?? categoryOptions[0]?.valueKey ?? null;
  const selectedCategoryOption =
    categoryOptions.find((o) => o.valueKey === selectedCategoryKey) ?? null;

  // Templates for the selected category: prefer category-specific, else fall back
  // to the shared (category-agnostic) templates. Drives the questionnaire panel.
  const scopedTemplates = React.useMemo(() => {
    if (!selectedCategoryKey) return templates;
    const exact = templates.filter((t) => t.categoryKey === selectedCategoryKey);
    return exact.length > 0 ? exact : templates.filter((t) => t.categoryKey == null);
  }, [templates, selectedCategoryKey]);

  // Custom Attributes is product-scoped (category-agnostic); any product template
  // uuid identifies the product for the deal-attributes endpoint. Prefer a stable
  // template so switching categories doesn't refetch the (always-mounted) panel.
  const attributeTemplateUuid = templates[0]?.uuid ?? selectedTemplateUuid ?? null;

  // Reset edit mode and tree state when selection changes
  React.useEffect(() => {
    setIsEditing(false);
    setTreeCollapsed(true);
  }, [selectedTemplateUuid]);

  const loadEstimateData = React.useCallback(
    async (rulesetId?: number) => {
      if (!productUuid) return;
      setEstimateError(null);
      const url = rulesetId
        ? `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/estimate-overview?rulesetId=${rulesetId}`
        : `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/estimate-overview`;
      try {
        const result = await apiFetch<EstimateOverviewData>(url);
        setEstimateData(result);
        // On initial load (no rulesetId passed), set the selected id to the default
        if (!rulesetId && result.rulesets.length > 0) {
          setSelectedEstimateRulesetId(result.rulesets[0]!.id);
        }
      } catch {
        setEstimateError('Failed to load estimate overview.');
      }
    },
    [productUuid]
  );

  const loadTemplates = React.useCallback(async () => {
    if (!productUuid) return;
    setIsLoading(true);
    setLoadError(null);
    setEstimateError(null);
    try {
      const [templateResult, estimateResult, categoryResult] = await Promise.allSettled([
        apiFetch<{ templates: DealTemplateSummary[] }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates`
        ),
        apiFetch<EstimateOverviewData>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/estimate-overview`
        ),
        apiFetch<{ options: CategoryOption[] }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/category-options`
        ),
      ]);
      if (templateResult.status === 'fulfilled') {
        setTemplates(templateResult.value.templates);
      } else {
        setLoadError(
          templateResult.reason instanceof Error
            ? templateResult.reason.message
            : 'Failed to load templates'
        );
      }
      if (estimateResult.status === 'fulfilled') {
        setEstimateData(estimateResult.value);
        if (estimateResult.value.rulesets.length > 0) {
          setSelectedEstimateRulesetId(estimateResult.value.rulesets[0]!.id);
        }
      } else {
        setEstimateError('Failed to load estimate overview.');
      }
      if (categoryResult.status === 'fulfilled') {
        setCategoryOptions(categoryResult.value.options);
      }
    } finally {
      setIsLoading(false);
    }
  }, [productUuid]);

  React.useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleRulesetChange = React.useCallback(
    (id: number) => {
      setSelectedEstimateRulesetId(id);
      void loadEstimateData(id);
    },
    [loadEstimateData]
  );

  // Keep the selected estimate ruleset aligned to the selected deal category.
  // When the category changes (or its rulesets load), re-point the selection to
  // that category's ruleset (falling back to the shared category-agnostic ones),
  // unless the current selection already belongs to that scope.
  React.useEffect(() => {
    const all = estimateData?.rulesets ?? [];
    if (all.length === 0 || selectedCategoryKey == null) return;
    const exact = all.filter((r) => r.categoryKey === selectedCategoryKey);
    const pool = exact.length > 0 ? exact : all.filter((r) => r.categoryKey == null);
    if (pool.length === 0) return;
    if (pool.some((r) => r.id === selectedEstimateRulesetId)) return;
    const preferred = pool.find((r) => r.isActive) ?? pool[0];
    if (preferred && preferred.id !== selectedEstimateRulesetId) {
      handleRulesetChange(preferred.id);
    }
  }, [selectedCategoryKey, estimateData?.rulesets, selectedEstimateRulesetId, handleRulesetChange]);

  const handleRangeConfigSave = React.useCallback(
    async (config: EstimateRangeConfig) => {
      if (!productUuid || !selectedEstimateRulesetId) return;
      setIsSavingRangeConfig(true);
      try {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/estimate-range-config`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              rulesetId: selectedEstimateRulesetId,
              upperPct: config.upperPct,
              lowerPct: config.lowerPct,
              disclaimerText: config.disclaimerText,
              ticketMaxHours: config.ticketMaxHours ?? null,
              ticketEffortHours: config.ticketEffortHours ?? null,
              initiativeEffortHours: config.initiativeEffortHours ?? null,
              integration: config.integration,
              subfolder: config.subfolder ?? null,
            }),
          }
        );
        setEstimateData((prev) =>
          prev
            ? {
                ...prev,
                rulesets: prev.rulesets.map((r) =>
                  r.id === selectedEstimateRulesetId ? { ...r, rangeConfig: config } : r
                ),
              }
            : null
        );
      } finally {
        setIsSavingRangeConfig(false);
      }
    },
    [productUuid, selectedEstimateRulesetId]
  );

  const [isSavingResourcePlanConfig, setIsSavingResourcePlanConfig] = React.useState(false);

  const handleResourcePlanConfigSave = React.useCallback(
    async (config: {
      integration: EstimateIntegrationConfig | null;
      subfolder?: string | null;
      discountPercentage?: number | null;
      offshorePercentage?: number | null;
    }) => {
      if (!productUuid) return;
      setIsSavingResourcePlanConfig(true);
      try {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/resource-plan-config`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              integration: config.integration,
              subfolder: config.subfolder ?? null,
              discountPercentage: config.discountPercentage,
              offshorePercentage: config.offshorePercentage,
            }),
          }
        );
        setEstimateData((prev) =>
          prev
            ? {
                ...prev,
                resourcePlanConfig: config,
              }
            : null
        );
      } finally {
        setIsSavingResourcePlanConfig(false);
      }
    },
    [productUuid]
  );

  const [isSavingSowConfig, setIsSavingSowConfig] = React.useState(false);

  const handleSowConfigSave = React.useCallback(
    async (config: { integration: EstimateIntegrationConfig | null; subfolder: string | null }) => {
      if (!productUuid) return;
      setIsSavingSowConfig(true);
      try {
        await apiFetch(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-config`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              integration: config.integration,
              subfolder: config.subfolder ?? null,
            }),
          }
        );
        setEstimateData((prev) => (prev ? { ...prev, sowConfig: config } : null));
      } finally {
        setIsSavingSowConfig(false);
      }
    },
    [productUuid]
  );

  const [isSavingExportSettings, setIsSavingExportSettings] = React.useState(false);
  const [isReindexingCorpus, setIsReindexingCorpus] = React.useState(false);
  const [reindexResult, setReindexResult] = React.useState<{
    total: number;
    reindexed: number;
  } | null>(null);

  const handleReindexCorpus = React.useCallback(async () => {
    setIsReindexingCorpus(true);
    setReindexResult(null);
    try {
      const result = await apiFetch<{ data: { total: number; reindexed: number } }>(
        '/api/sales/reference-corpus/reindex',
        { method: 'POST' }
      );
      setReindexResult(result.data);
    } finally {
      setIsReindexingCorpus(false);
    }
  }, []);

  const handleConsolidatedExportSave = React.useCallback(
    async (payload: ConsolidatedExportPayload) => {
      if (!productUuid) return;
      setIsSavingExportSettings(true);
      try {
        const { data } = await apiFetch<{ data: ExportGlobalSettings }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/export-settings`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              folderPerCustomer: payload.folderPerCustomer,
              integration: payload.integration,
              estimateSubfolder: payload.estimateSubfolder,
              resourcePlanSubfolder: payload.resourcePlanSubfolder,
              sowSubfolder: payload.sowSubfolder,
            }),
          }
        );
        // The export-settings response carries the sales_exports values; the
        // per-document sub-folders live on the per-type configs, so patch them back
        // from the payload (the default ruleset owns the estimate sub-folder).
        setEstimateData((prev) =>
          prev
            ? {
                ...prev,
                exportSettings: data,
                rulesets: prev.rulesets.map((r, idx) =>
                  idx === 0
                    ? { ...r, rangeConfig: { ...r.rangeConfig, subfolder: payload.estimateSubfolder } }
                    : r
                ),
                resourcePlanConfig: {
                  ...prev.resourcePlanConfig,
                  subfolder: payload.resourcePlanSubfolder,
                },
                sowConfig: { ...prev.sowConfig, subfolder: payload.sowSubfolder },
              }
            : null
        );
      } finally {
        setIsSavingExportSettings(false);
      }
    },
    [productUuid]
  );

  const handleUpdated = React.useCallback((updated: DealTemplateSummary) => {
    setTemplates((prev) =>
      prev.map((t) => {
        // If we just set one as default, clear others
        if (updated.isDefault && t.uuid !== updated.uuid && t.scope === 'workspace') {
          return { ...t, isDefault: false };
        }
        return t.uuid === updated.uuid ? updated : t;
      })
    );
  }, []);

  const handleForked = React.useCallback((created: DealTemplateSummary) => {
    setTemplates((prev) => [...prev, created]);
    setForkSource(null);
    setSelectedTemplateUuid(created.uuid);
  }, []);

  // Auto-select within the category-scoped template set: prefer default workspace
  // template, then any workspace template, then first. Re-selects when the selected
  // category changes the available set.
  React.useEffect(() => {
    if (scopedTemplates.length === 0) {
      setSelectedTemplateUuid(null);
      return;
    }
    if (selectedTemplateUuid && scopedTemplates.some((t) => t.uuid === selectedTemplateUuid))
      return;
    const preferred =
      scopedTemplates.find((t) => t.scope === 'workspace' && t.isDefault) ??
      scopedTemplates.find((t) => t.scope === 'workspace') ??
      scopedTemplates[0];
    setSelectedTemplateUuid(preferred?.uuid ?? null);
  }, [scopedTemplates, selectedTemplateUuid]);

  const loadTree = React.useCallback(
    async (templateUuid: string) => {
      if (!productUuid) return;
      setIsLoadingTree(true);
      setTreeData(null);
      try {
        const result = await apiFetch<{ template: { sections: TemplateTreeSection[] } }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates/${encodeURIComponent(templateUuid)}`
        );
        setTreeData(result.template.sections);
      } catch {
        setTreeData([]);
      } finally {
        setIsLoadingTree(false);
      }
    },
    [productUuid]
  );

  React.useEffect(() => {
    if (selectedTemplateUuid) void loadTree(selectedTemplateUuid);
  }, [selectedTemplateUuid, loadTree]);

  React.useEffect(() => {
    if (!productUuid) {
      setProfileFieldMappings({});
      return;
    }
    apiFetch<{ profile: EstimateProfileData }>(
      `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/calculator-profile`
    )
      .then((r) => {
        const fieldMappings =
          r.profile.config && typeof r.profile.config === 'object'
            ? (r.profile.config as Record<string, unknown>).field_mappings
            : undefined;
        setProfileFieldMappings(
          fieldMappings && typeof fieldMappings === 'object'
            ? (fieldMappings as Record<string, unknown>)
            : {}
        );
      })
      .catch(() => setProfileFieldMappings({}));
  }, [productUuid]);

  const fieldLabelMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const section of treeData ?? []) {
      for (const group of section.groups) {
        for (const field of group.fields) {
          const qualifiedKey = `${group.groupKey}.${field.fieldKey}`;
          map[qualifiedKey] = field.label;
          // Fallback for any legacy bare-key lookups.
          if (!map[field.fieldKey]) {
            map[field.fieldKey] = field.label;
          }
        }
      }
    }

    // Resolve logical aliases (e.g. o2c.*) to questionnaire field labels via profile mappings.
    for (const [aliasKey, mapped] of Object.entries(profileFieldMappings)) {
      if (typeof mapped === 'string') {
        const label = map[mapped];
        if (label) map[aliasKey] = label;
      } else if (Array.isArray(mapped)) {
        const labels = mapped
          .filter((v): v is string => typeof v === 'string')
          .map((fieldKey) => map[fieldKey])
          .filter((v): v is string => Boolean(v));
        if (labels.length > 0) {
          map[aliasKey] = labels.join(' + ');
        }
      }
    }

    return map;
  }, [treeData, profileFieldMappings]);

  const handleStatusChange = React.useCallback(
    async (status: 'draft' | 'saved' | 'published') => {
      if (!productUuid || !selectedTemplateUuid || isPatching) return;
      setIsPatching(true);
      try {
        const result = await apiFetch<{ template: DealTemplateSummary }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates/${encodeURIComponent(selectedTemplateUuid)}`,
          { method: 'PATCH', body: JSON.stringify({ status }) }
        );
        handleUpdated(result.template);
        setIsEditing(false);
      } finally {
        setIsPatching(false);
      }
    },
    [productUuid, selectedTemplateUuid, isPatching, handleUpdated]
  );

  const handleSetDefault = React.useCallback(async () => {
    if (!productUuid || !selectedTemplateUuid || isPatching) return;
    setIsPatching(true);
    try {
      const result = await apiFetch<{ template: DealTemplateSummary }>(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates/${encodeURIComponent(selectedTemplateUuid)}`,
        { method: 'PATCH', body: JSON.stringify({ isDefault: true }) }
      );
      handleUpdated(result.template);
    } finally {
      setIsPatching(false);
    }
  }, [productUuid, selectedTemplateUuid, isPatching, handleUpdated]);

  const handleTemplateCategoryChange = React.useCallback(
    async (key: string | null) => {
      if (!productUuid || !selectedTemplateUuid || isPatching) return;
      setIsPatching(true);
      try {
        const result = await apiFetch<{ template: DealTemplateSummary }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/templates/${encodeURIComponent(selectedTemplateUuid)}`,
          { method: 'PATCH', body: JSON.stringify({ categoryKey: key }) }
        );
        handleUpdated(result.template);
      } finally {
        setIsPatching(false);
      }
    },
    [productUuid, selectedTemplateUuid, isPatching, handleUpdated]
  );

  const patchRuleset = React.useCallback(
    async (rulesetId: number, patch: Record<string, unknown>) => {
      if (!productUuid) return;
      const result = await apiFetch<{ ruleset: EstimateRulesetSummary }>(
        `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/rulesets/${rulesetId}`,
        { method: 'PATCH', body: JSON.stringify(patch) }
      );
      setEstimateData((prev) =>
        prev
          ? {
              ...prev,
              rulesets: prev.rulesets.map((r) =>
                r.id === rulesetId ? { ...r, ...result.ruleset } : r
              ),
            }
          : prev
      );
    },
    [productUuid]
  );

  const handleRulesetCategoryChange = React.useCallback(
    async (rulesetId: number, key: string | null) => {
      try {
        await patchRuleset(rulesetId, { categoryKey: key });
      } catch {
        // ignore
      }
    },
    [patchRuleset]
  );

  const handleRulesetSetDefault = React.useCallback(
    async (rulesetId: number) => {
      try {
        await patchRuleset(rulesetId, { isActive: true });
      } catch {
        // ignore
      }
    },
    [patchRuleset]
  );

  const handleRulesetPublish = React.useCallback(
    async (rulesetId: number) => {
      try {
        await patchRuleset(rulesetId, { status: 'published' });
      } catch {
        // ignore
      }
    },
    [patchRuleset]
  );

  const handleRulesetCreateFromSource = React.useCallback(
    async (sourceId: number) => {
      if (!productUuid) return;
      try {
        const res = await apiFetch<{ id: number }>(
          `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/rulesets`,
          { method: 'POST', body: JSON.stringify({ copyFromRulesetId: sourceId }) }
        );
        setSelectedEstimateRulesetId(res.id);
        await loadEstimateData(res.id);
      } catch {
        setEstimateError('Failed to create a new ruleset version.');
      }
    },
    [productUuid, loadEstimateData]
  );

  const handleRemove = React.useCallback(async () => {
    if (!productUuid || isRemoving) return;
    setIsRemoving(true);
    try {
      await apiFetch(`/api/workspace/admin/products/${encodeURIComponent(productUuid)}`, {
        method: 'DELETE',
      });
      navigate('/administration/products');
    } finally {
      setIsRemoving(false);
    }
  }, [productUuid, isRemoving, navigate]);

  const sectionTitle =
    scope === 'global-settings'
      ? 'Global Settings'
      : scope === 'custom-attributes'
        ? 'Custom Attributes'
        : 'Deal Categories';

  const leftNav = (
    <AppNav
      selected="administration"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => {}}
    />
  );

  const contextPane = (
    <AdminContextMenuPane
      selectedSection="products"
      collapsed={adminMenuCollapsed}
      onToggle={handleToggleAdminMenu}
      menuItems={menuItems}
      isMenuLoading={isMenuLoading}
    />
  );

  const breadcrumbTitleNode = (
    <>
      <Link to="/administration/products" className="admin-breadcrumb-link">
        Products
      </Link>
      <span className="admin-breadcrumb-sep" aria-hidden="true">
        ›
      </span>
      <span>{productName ?? 'Product'}</span>
    </>
  );

  const productBase = `/administration/products/${productUuid ?? ''}`;
  // Navigate to a product-level section (Custom Attributes / Global Settings).
  const goToSection = (section: string) =>
    navigate(`${productBase}/${section}`, { state: { productName } });
  // Navigate within the category hub, preserving the selected category + artifact.
  const goToCategory = (catKey: string, art: string = selectedArtifact) =>
    navigate(`${productBase}/categories/${encodeURIComponent(catKey)}/${art}`, {
      state: { productName },
    });

  // Stage-gate the primary tabs against the workspace feature-min-stage: alpha tabs (Deal
  // Categories) drop out in uat/prod. Filtered list drives the subnav; the badge marks staged
  // tabs in local/dev. Mirrors the deal-page Resource Plan tab.
  const visiblePrimaryTabs = React.useMemo<TabsItem[]>(
    () =>
      PRODUCT_PRIMARY_TABS.filter((t) => isStageAvailable(t.stage, workspace?.featureMinStage)).map(
        (t) => ({
          value: t.value,
          label: t.stage ? (
            <span className="page-tabs__stage-label">
              {t.label}
              <StageBadge stage={t.stage} />
            </span>
          ) : (
            t.label
          ),
        })
      ),
    [workspace?.featureMinStage]
  );
  // Deal Categories is the default scope; when it's stage-hidden, redirect off it so the page
  // never lands on a missing tab (also catches direct /categories[/...] URLs). Guarded on
  // `workspace` so we don't redirect during load (featureMinStage undefined fail-opens to visible).
  const categoriesVisible = isStageAvailable(
    PRODUCT_PRIMARY_TABS.find((t) => t.value === 'categories')?.stage,
    workspace?.featureMinStage
  );
  React.useEffect(() => {
    if (workspace && !categoriesVisible && scope === 'categories') {
      navigate(`${productBase}/global-settings`, { replace: true });
    }
  }, [workspace, categoriesVisible, scope, productBase, navigate]);

  const main = (
    <MainContentShell
      title={sectionTitle}
      titleNode={breadcrumbTitleNode}
      loading={isLoading}
      actions={
        <>
          <Button size="sm" variant="destructive" onClick={() => setRemoveConfirmOpen(true)}>
            Remove
          </Button>
          <ConfirmDeleteDialog
            open={removeConfirmOpen}
            onOpenChange={setRemoveConfirmOpen}
            title="Remove product?"
            confirmKeyword={productName ?? 'REMOVE'}
            description={`${productName ?? 'This product'} will be removed from this workspace.`}
            confirmLabel="Remove"
            pendingLabel="Removing…"
            isPending={isRemoving}
            onConfirm={handleRemove}
          />
        </>
      }
    >
      {isLoading ? null : loadError ? (
        <PageErrorState message={loadError} onRetry={() => void loadTemplates()} />
      ) : null}
      <Tabs
        items={visiblePrimaryTabs}
        value={scope}
        ariaLabel="Product sections"
        variant="subnav"
        size="md"
        className="page-tabs"
        style={isLoading || loadError ? { display: 'none' } : undefined}
        onTabClick={(item) =>
          item.value === 'categories'
            ? selectedCategoryKey
              ? goToCategory(selectedCategoryKey)
              : goToSection('categories')
            : goToSection(item.value)
        }
      >
        <TabsContent value="categories" className="page-tab-content">
          <div className="pcat-master-detail">
            <CategoryMasterList
              categories={categoryOptions}
              selectedKey={selectedCategoryKey}
              onSelect={(k) => goToCategory(k)}
              width={categoryNavWidth}
              onResize={(delta) =>
                setCategoryNavWidth((w) => Math.max(200, Math.min(420, w + delta)))
              }
            />
            <div className="pcat-detail">
              {!selectedCategoryOption ? (
                <p className="ptp-empty-text">
                  {categoryOptions.length === 0
                    ? 'No deal categories for this product.'
                    : 'Select a deal category to view its configuration.'}
                </p>
              ) : (
                <>
                  <CategoryMetadataHeader category={selectedCategoryOption} />
                  <Tabs
                    items={CATEGORY_ARTIFACT_TABS}
                    value={selectedArtifact}
                    ariaLabel="Category configuration"
                    variant="subnav"
                    size="sm"
                    className="pcat-artifact-tabs"
                    onTabClick={(item) =>
                      selectedCategoryKey && goToCategory(selectedCategoryKey, item.value)
                    }
                  >
                    <TabsContent value="questionnaire" className="page-tab-content">
                      <div className="page-content ptp-tab-layout">
                        {forkSource && (
                          <ForkDialog
                            sourceTemplate={forkSource}
                            productUuid={productUuid ?? ''}
                            onCreated={handleForked}
                            onCancel={() => setForkSource(null)}
                          />
                        )}
                        {selectedTemplateUuid && scopedTemplates.length > 0 && (
                          <TemplateVersionBar
                            templates={scopedTemplates}
                            selectedUuid={selectedTemplateUuid}
                            isEditing={isEditing}
                            treeCollapsed={treeCollapsed}
                            onSelect={setSelectedTemplateUuid}
                            onEditCancel={() => setIsEditing(false)}
                            onForkClick={() => {
                              const t = scopedTemplates.find(
                                (x) => x.uuid === selectedTemplateUuid
                              );
                              if (t) setForkSource(t);
                            }}
                            onStatusChange={(s) => void handleStatusChange(s)}
                            onSetDefault={() => void handleSetDefault()}
                            onCategoryChange={(k) => void handleTemplateCategoryChange(k)}
                            onToggleTree={() => setTreeCollapsed((c) => !c)}
                            isPatching={isPatching}
                            categoryOptions={categoryOptions}
                            scopedCategoryKey={selectedCategoryKey}
                          />
                        )}
                        {scopedTemplates.length === 0 ? (
                          <p className="ptp-empty-text">
                            No questionnaire template is available for this category.
                          </p>
                        ) : (
                          <TemplateTreeView
                            key={selectedTemplateUuid ?? 'none'}
                            sections={treeData ?? []}
                            isLoading={
                              isLoadingTree || selectedTemplateUuid === null || treeData === null
                            }
                            collapsed={treeCollapsed}
                          />
                        )}
                      </div>
                    </TabsContent>
                    <TabsContent value="estimate" className="page-tab-content">
                      <EstimateOverviewSection
                        productUuid={productUuid ?? ''}
                        data={estimateData}
                        error={estimateError}
                        onSigClick={handleSigClick}
                        onCfgClick={handleCfgClick}
                        onRuleClick={handleRuleClick}
                        selectedRuleKey={selectedRule?.ruleKey ?? null}
                        selectedRulesetId={selectedEstimateRulesetId}
                        onRulesetChange={handleRulesetChange}
                        onCategoryChange={(id, k) => void handleRulesetCategoryChange(id, k)}
                        onSetDefault={(id) => void handleRulesetSetDefault(id)}
                        onPublish={(id) => void handleRulesetPublish(id)}
                        onCreateFromSource={(id) => void handleRulesetCreateFromSource(id)}
                        categoryOptions={categoryOptions}
                        collapsed={estimateCollapsed}
                        onToggleCollapsed={() => setEstimateCollapsed((c) => !c)}
                        rangeConfig={
                          (
                            estimateData?.rulesets.find(
                              (r) => r.id === selectedEstimateRulesetId
                            ) ??
                            estimateData?.rulesets[0] ??
                            null
                          )?.rangeConfig ?? { upperPct: null, lowerPct: null, disclaimerText: null }
                        }
                        onRangeConfigSave={(config) => void handleRangeConfigSave(config)}
                        isSavingRangeConfig={isSavingRangeConfig}
                        onActivePaneChange={(pane) => {
                          setEstimateActivePane(pane);
                          if (pane === 'configuration') {
                            setRightPaneVisible(false);
                            setRightPaneMode(null);
                          }
                        }}
                        globalFolderOverrideActive={
                          !!estimateData?.exportSettings?.folderPerCustomer
                        }
                        scopedCategoryKey={selectedCategoryKey}
                      />
                    </TabsContent>
                    <TabsContent value="resource-plan" className="page-tab-content">
                      <ResourcePlanOverviewSection
                        productUuid={productUuid ?? ''}
                        config={estimateData?.resourcePlanConfig}
                        onConfigSave={(config) => void handleResourcePlanConfigSave(config)}
                        isSaving={isSavingResourcePlanConfig}
                        globalFolderOverrideActive={
                          !!estimateData?.exportSettings?.folderPerCustomer
                        }
                      />
                    </TabsContent>
                    <TabsContent value="statement-of-work" className="page-tab-content">
                      <StatementOfWorkOverviewSection
                        productUuid={productUuid ?? ''}
                        config={estimateData?.sowConfig}
                        onConfigSave={(config) => void handleSowConfigSave(config)}
                        isSaving={isSavingSowConfig}
                        globalFolderOverrideActive={
                          !!estimateData?.exportSettings?.folderPerCustomer
                        }
                      />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="custom-attributes" className="page-tab-content" forceMount>
          {/* Custom (non-category) attributes are product-scoped; the backend resolves
              the set from the product (any template uuid in the path identifies it). */}
          {isLoading || !attributeTemplateUuid ? (
            <DealAttributesSkeleton />
          ) : (
            <DealAttributesSection
              productUuid={productUuid ?? ''}
              templateUuid={attributeTemplateUuid}
              excludeAttributeKeys={['deal_category']}
            />
          )}
        </TabsContent>
        <TabsContent value="global-settings" className="page-tab-content">
          <div className="page-content ptp-gs-form">
            <ExportDestinationCard
              exportSettings={estimateData?.exportSettings}
              estimateSubfolder={estimateData?.rulesets[0]?.rangeConfig.subfolder ?? null}
              resourcePlanSubfolder={estimateData?.resourcePlanConfig.subfolder ?? null}
              sowSubfolder={estimateData?.sowConfig.subfolder ?? null}
              onSave={(payload) => void handleConsolidatedExportSave(payload)}
              isSaving={isSavingExportSettings}
            />
            <EstimateDefaultsCard
              rangeConfig={
                estimateData?.rulesets[0]?.rangeConfig ?? {
                  upperPct: null,
                  lowerPct: null,
                  disclaimerText: null,
                }
              }
              onSave={(config) => void handleRangeConfigSave(config)}
              isSaving={isSavingRangeConfig}
            />
            {isWorkspaceAdmin && isStageAvailable('alpha', workspace?.featureMinStage) && (
              <div className="ptp-gs-corpus-row">
                <div className="ptp-gs-corpus-row__info">
                  <span className="ptp-gs-row-name page-tabs__stage-label">
                    Reference corpus
                    <StageBadge stage="alpha" />
                  </span>
                  <span className="ptp-gs-row-desc ptp-gs-row-desc--sm">
                    Re-process uploaded estimate files to refresh scope and topology data.
                    {reindexResult
                      ? ` ${reindexResult.reindexed} of ${reindexResult.total} file${
                          reindexResult.total !== 1 ? 's' : ''
                        } re-indexed.`
                      : ''}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleReindexCorpus()}
                  disabled={isReindexingCorpus}
                >
                  {isReindexingCorpus ? 'Re-processing…' : 'Re-process'}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </MainContentShell>
  );

  const rightRailItems: RightRailItem[] = [
    {
      id: 'signal-detail',
      label:
        detailType === 'rule'
          ? 'Rule Detail'
          : detailType === 'config'
            ? 'Config Detail'
            : 'Signal Detail',
      icon: <InfoCircledIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
      enabled: isEstimateArtifact && estimateActivePane === 'template',
      content:
        detailType === 'rule' && selectedRule && signalMap ? (
          <RuleDetailPane
            rule={selectedRule}
            signals={signalMap}
            fieldLabels={fieldLabelMap}
            productUuid={productUuid ?? ''}
          />
        ) : detailType === 'config' && selectedCfgKey ? (
          <ConfigDetailPane
            cfgKey={selectedCfgKey}
            productUuid={productUuid ?? ''}
            signals={signalMap ?? undefined}
            fieldLabels={fieldLabelMap}
            contextSourceKeys={cfgContextSourceKeys}
          />
        ) : selectedSignalKey && signalMap && signalMap[selectedSignalKey] ? (
          <SignalDetailPane
            signalKey={selectedSignalKey}
            signal={signalMap[selectedSignalKey]!}
            signals={signalMap}
            fieldLabels={fieldLabelMap}
          />
        ) : (
          <div className="ptp-empty-text">Select a rule, signal, or config to view details.</div>
        ),
    },
  ];

  return (
    <AppShell
      leftNav={leftNav}
      contextPane={contextPane}
      contextPaneDividerClassName="admin-contextpane-divider"
      contextPaneCollapsed={adminMenuCollapsed}
      main={main}
      rightRailItems={rightRailItems}
    />
  );
}

// backward-compat alias for any existing import
export { ProductAdminPage as ProductTemplatePage };
